import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../lib/supabase';
import ClassSchedule from './ClassSchedule';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import { Search, Save, ChevronLeft, ChevronRight, Users, Eye, Edit2, Trash2, Printer, FileText } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import DateInput from '../components/ui/DateInput';
import ExportButton from '../components/ui/ExportButton';
import { fmtDate } from '../lib/exportUtils';

// 'YYYY-MM-DD' (or ISO string) -> '13-Jul-2026'; anything else is returned unchanged
const DMY_MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const dmy = v => { if (v instanceof Date) return isNaN(v) ? '' : `${String(v.getDate()).padStart(2,'0')}-${DMY_MON[v.getMonth()]}-${v.getFullYear()}`; const m = String(v == null ? '' : v).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}-${DMY_MON[Number(m[2]) - 1]}-${m[1]}` : (v == null ? '' : String(v)); };

function today() { return new Date().toISOString().split('T')[0]; }
function formatDate(d) { return dmy(d); }

// ── PRINT CSS shared ─────────────────────────────────────────────────────────
const PRINT_CSS = `
  @page { size: A4 landscape; margin: 8mm 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 8px; color: #000; }
  .header { text-align: center; margin-bottom: 8px; border-bottom: 2px solid #1a3a6b; padding-bottom: 6px; }
  .header h1 { font-size: 14px; font-weight: 900; color: #1a3a6b; letter-spacing: 1px; text-transform: uppercase; }
  .header h2 { font-size: 11px; color: #444; margin-top: 2px; }
  .header .meta { font-size: 9px; color: #666; margin-top: 3px; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th { background: #1a3a6b; color: #fff; padding: 3px 3px; font-size: 8px; text-align: center; border: 1px solid #000; }
  td { border: 1px solid #999; padding: 2px 3px; font-size: 8px; vertical-align: middle; }
  .sno { text-align: center; width: 24px; }
  .name { font-weight: 600; }
  .regn { font-family: monospace; text-align: center; }
  .num  { text-align: center; }
  .pct-high { background: #d1fae5; color: #065f46; font-weight: 700; text-align: center; }
  .pct-mid  { background: #fef9c3; color: #854d0e; font-weight: 700; text-align: center; }
  .pct-low  { background: #fee2e2; color: #991b1b; font-weight: 700; text-align: center; }
  .footer { margin-top: 10px; display: flex; justify-content: space-between; font-size: 9px; color: #555; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

function pctClass(p) {
  const n = parseFloat(p);
  if (n >= 75) return 'pct-high';
  if (n >= 50) return 'pct-mid';
  return 'pct-low';
}

// ── REPORT 1: Single Topic Attendance Report ──────────────────────────────────
function printTopicReport(batchName, topicName, totalSessions, students) {
  // students: [{full_name, register_number, presentSessions, pct}]
  // Split into two columns like the PDF sample
  const ROWS_PER_PAGE = 43;
  const pages = [];
  for (let p = 0; p < students.length; p += ROWS_PER_PAGE * 2) {
    const pageStudents = students.slice(p, p + ROWS_PER_PAGE * 2);
    const col1 = pageStudents.slice(0, ROWS_PER_PAGE);
    const col2 = pageStudents.slice(ROWS_PER_PAGE);
    pages.push({ col1, col2, offset: p });
  }
  const tBody = pages.map(page => {
    const rowCount = Math.max(page.col1.length, page.col2.length);
    const pageRows = Array.from({ length: rowCount }, (_, i) => {
      const ls = page.col1[i], rs = page.col2[i];
      const li = page.offset + i;
      const ri = page.offset + ROWS_PER_PAGE + i;
      const lPct = ls ? parseFloat(ls.pct || 0).toFixed(1) : '';
      const rPct = rs ? parseFloat(rs.pct || 0).toFixed(1) : '';
      return `<tr>
        <td class="sno">${ls ? li + 1 : ''}</td>
        <td class="name">${ls ? ls.full_name : ''}</td>
        <td class="regn">${ls ? ls.register_number : ''}</td>
        <td class="num">${ls ? ls.presentSessions : ''}</td>
        <td class="${ls ? pctClass(lPct) : ''}">${ls ? lPct + '%' : ''}</td>
        <td style="width:6px;background:#e5e7eb;border:none;"></td>
        <td class="sno">${rs ? ri + 1 : ''}</td>
        <td class="name">${rs ? rs.full_name : ''}</td>
        <td class="regn">${rs ? rs.register_number : ''}</td>
        <td class="num">${rs ? rs.presentSessions : ''}</td>
        <td class="${rs ? pctClass(rPct) : ''}">${rs ? rPct + '%' : ''}</td>
      </tr>`;
    }).join('');
    return pageRows + (page.offset + ROWS_PER_PAGE * 2 < students.length ? '<tr style="page-break-after:always"><td colspan="11" style="border:none;padding:0;"></td></tr>' : '');
  }).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
  <title>Attendance Report - ${topicName}</title>
  <style>${PRINT_CSS}</style></head><body>
  <div class="header">
    <h1>Attendance Report - ${batchName}</h1>
    <h2>Topic: ${topicName}</h2>
    <div class="meta">Total Sessions: ${totalSessions} &nbsp;|&nbsp; Total Students: ${students.length} &nbsp;|&nbsp; Generated: ${dmy(new Date())}</div>
  </div>
  <table>
    <thead><tr>
      <th class="sno">#</th><th style="min-width:120px">Name</th><th>Register No.</th><th>Attendance</th><th>Att. %</th>
      <th style="width:6px;background:#1a3a6b;border:none;"></th>
      <th class="sno">#</th><th style="min-width:120px">Name</th><th>Register No.</th><th>Attendance</th><th>Att. %</th>
    </tr></thead>
    <tbody>${tBody}</tbody>
  </table>
  <div class="footer">
    <span>🟢 ≥75% &nbsp;&nbsp; 🟡 50–74% &nbsp;&nbsp; 🔴 &lt;50%</span>
    <span>Armstech Engineers Pvt Ltd - Attendance Management System</span>
    <span>Page 1</span>
  </div>
  <script>window.onload = () => window.print();<\/script>
  </body></html>`;

  const w = window.open('', '_blank', 'width=1100,height=800');
  w.document.write(html);
  w.document.close();
}

// ── REPORT 2: Full Batch Attendance Report (all topics) ───────────────────────
function printBatchReport(batchName, allTopics, totalSessions, students, topicMap={}) {
  // students: [{full_name, register_number, presentSessions, pct, topicStats:[{topic, present, total, pct}]}]
  const tHead = allTopics.map(t => { const sc = topicMap && topicMap[t] ? topicMap[t] : t.substring(0,10); return `<th style="writing-mode:vertical-rl;transform:rotate(180deg);height:60px;min-width:28px;" title="${t}">${sc}</th>`; }).join('');

  const tBody = students.map((s, i) => {
    const topicCells = allTopics.map(topic => {
      const ts = s.topicStats?.find(x => x.topic === topic || x.topic_name === topic);
      const p = ts ? parseFloat(ts.topicPct || 0).toFixed(0) : '0';
      const val = ts ? ts.topicPresent : '0';
      return `<td class="${pctClass(p)}" title="${topic}: ${val}/${ts?.topicTotalSess||0} (${p}%)">${val}</td>`;
    }).join('');
    const overallPct = parseFloat(s.pct || 0).toFixed(1);
    return `<tr>
      <td class="sno">${i + 1}</td>
      <td class="name" style="min-width:120px;">${s.full_name}</td>
      <td class="regn">${s.register_number}</td>
      ${topicCells}
      <td class="num" style="font-weight:700;">${s.presentSessions}</td>
      <td class="${pctClass(overallPct)}" style="font-weight:700;">${overallPct}%</td>
    </tr>`;
  }).join('');

  // Total sessions row
  const totalRow = allTopics.map(topic => {
    const ts = students[0]?.topicStats?.find(x => x.topic === topic);
    return `<td style="text-align:center;font-weight:700;background:#e8edf5;">${ts?.topicTotalSess || ''}</td>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
  <title>Batch Attendance Report - ${batchName}</title>
  <style>
    ${PRINT_CSS}
    @page { size: A4 landscape; margin: 6mm 8mm; }
    td, th { font-size: 8.5px; padding: 3px 3px; }
  </style></head><body>
  <div class="header">
    <h1>Batch Attendance Report - ${batchName}</h1>
    <div class="meta">Total Sessions: ${totalSessions} &nbsp;|&nbsp; Total Students: ${students.length} &nbsp;|&nbsp; Topics: ${allTopics.length} &nbsp;|&nbsp; Generated: ${dmy(new Date())}</div>
  </div>
  <table>
    <thead>
      <tr>
        <th class="sno" rowspan="2">#</th>
        <th rowspan="2" style="min-width:110px;">Name</th>
        <th rowspan="2">Register No.</th>
        <th colspan="${allTopics.length}" style="background:#2d5a9e;">Topic-wise Attendance</th>
        <th rowspan="2">Total<br/>Sessions</th>
        <th rowspan="2">Overall<br/>%</th>
      </tr>
      <tr>${tHead}</tr>
      <tr>
        <td colspan="3" style="text-align:right;font-weight:700;background:#e8edf5;">Total Sessions →</td>
        ${totalRow}
        <td style="text-align:center;font-weight:700;background:#e8edf5;">${totalSessions}</td>
        <td style="background:#e8edf5;"></td>
      </tr>
    </thead>
    <tbody>${tBody}</tbody>
  </table>
  <div class="footer">
    <span>Values = sessions attended per topic &nbsp;|&nbsp; 🟢 ≥75% &nbsp; 🟡 50–74% &nbsp; 🔴 &lt;50%</span>
    <span>Armstech Engineers Pvt Ltd - Attendance Management System</span>
  </div>
  <script>window.onload = () => window.print();<\/script>
  </body></html>`;

  const w = window.open('', '_blank', 'width=1200,height=900');
  w.document.write(html);
  w.document.close();
}
const ALL_SESSIONS = [
  { key: 'session1', label: 'Session 1' },
  { key: 'session2', label: 'Session 2' },
  { key: 'session3', label: 'Session 3' },
  { key: 'session4', label: 'Session 4' },
  { key: 'session5', label: 'Session 5' },
  { key: 'session6', label: 'Session 6' },
  { key: 'session7', label: 'Session 7' },
  { key: 'session8', label: 'Session 8' },
];

const emptyAtt = (n = 4) => {
  const obj = {};
  ALL_SESSIONS.slice(0, n).forEach(s => { obj[s.key] = 'absent'; });
  return obj;
};


// ── LAB REPORT TAB COMPONENT ─────────────────────────────────────────────────
const OWNER_MAIL = 'ershadarmstech@gmail.com';
const OFFICE_MAILS = ['armstechac@gmail.com', 'armstechekm@gmail.com'];
const NOT_OWN = 'You can change only your own batches';

// Staff assigned to specific batches may change only those batches; every other batch is view-only.
function useBatchLimit() {
  const { canEditBatch, assignedBatches, isCoordinator, isAdmin, user } = useAuth();
  const meMail = String(user?.email || '').trim().toLowerCase();
  const batchRestricted = meMail !== OWNER_MAIL && !OFFICE_MAILS.includes(meMail) && !isAdmin && ((assignedBatches || []).length > 0 || !!isCoordinator);
  const ownBatchId = id => !batchRestricted || (typeof canEditBatch === 'function' && !!id && canEditBatch(id));
  return { batchRestricted, ownBatchId };
}

const skDate = k => String(k || '').split('~')[0];
const skNo = k => Number(String(k || '').split('~')[1] || 1);
const mkKey = (d, n) => (Number(n) > 1 ? `${d}~${n}` : String(d));
const dmyKey = k => dmy(skDate(k)) + (skNo(k) > 1 ? ` · S${skNo(k)}` : '');

function LabReportTab({ supabase }) {
  const { ownBatchId } = useBatchLimit();
  const dmy = dmyKey;   // session keys may carry a session number: show it with the date
  const [batches, setBatches] = React.useState([]);
  const [selBatch, setSelBatch] = React.useState('');
  const [batchInfo, setBatchInfo] = React.useState(null);
  const labOwn = ownBatchId(batchInfo?.id);
  const [labTopics, setLabTopics] = React.useState([]);
  const [allStudents, setAllStudents] = React.useState([]);
  const [labData, setLabData] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [view, setView] = React.useState('overview');
  const [selTopic, setSelTopic] = React.useState(null);
  const [selSession, setSelSession] = React.useState(null);
  const [addingSession, setAddingSession] = React.useState(false);
  const [newDate, setNewDate] = React.useState('');
  const [newSessNo, setNewSessNo] = React.useState(1);
  const [sessionType, setSessionType] = React.useState('regular');
  const [originalDate, setOriginalDate] = React.useState('');
  const [repeatModal, setRepeatModal] = React.useState(null); // {topicId, originalDate, absentStudents[]}
  const [repeatDate, setRepeatDate] = React.useState('');
  const [labRepeatSession, setLabRepeatSession] = React.useState(null);
  const [labSearch, setLabSearch] = React.useState('');
  const [addSessionSearch, setAddSessionSearch] = React.useState(''); // {repeatDate, originalDate, topicId, studentIds}
  const [saving, setSaving] = React.useState(false);
  const [editingDate, setEditingDate] = React.useState(null);
  const [sessionStudents, setSessionStudents] = React.useState([]); // empty = all students
  const [sessionSelectMode, setSessionSelectMode] = React.useState(false);
  const [completions, setCompletions] = React.useState([]); // student_ids who completed this topic
  const [showCompleted, setShowCompleted] = React.useState(false);
  const [selStudent, setSelStudent] = React.useState(null);
  const [labStudentSearch, setLabStudentSearch] = React.useState('');

  React.useEffect(() => {
    supabase.from('batches').select('id,batch_name,status,discipline').order('batch_name')
      .then(({data}) => setBatches(sortBatches(data||[])));
  }, []);
  // another staff member marked or changed a lab session: reload quietly (no spinner)
  useLiveRefresh(['lab_attendance'], () => { if (selBatch && batchInfo) loadLabData(selBatch, batchInfo, true); }, !!selBatch);

  async function loadCompletions(topicId, batchId) {
    if (!topicId || !batchId) return;
    const { data } = await supabase.from('lab_completions')
      .select('student_id').eq('topic_id', topicId).eq('batch_id', batchId);
    setCompletions((data||[]).map(c=>c.student_id));
  }

  async function markComplete(studentId, topicId, batchId, isComplete) {
    if (!ownBatchId(batchId)) { toast.error(NOT_OWN); return; }
    if (isComplete) {
      await supabase.from('lab_completions').delete()
        .eq('student_id', studentId).eq('topic_id', topicId).eq('batch_id', batchId);
    } else {
      await supabase.from('lab_completions').upsert({
        student_id: studentId, topic_id: topicId, batch_id: batchId,
        completed_at: new Date().toISOString().split('T')[0],
      }, {onConflict: 'student_id,topic_id,batch_id'});
    }
    loadCompletions(topicId, batchId);
  }

  async function loadLabData(batchName, bData_override, silent) {
    if (!silent) setLoading(true);
    let bData = bData_override;
    if (!bData) {
      const {data} = await supabase.from('batches').select('*').eq('batch_name',batchName).single();
      bData = data;
      setBatchInfo(data);
    }
    const disc = (bData?.discipline||'').toLowerCase().includes('civil') ? 'civil' : 'mechanical';
    const {data:topics} = await supabase.from('topics')
      .select('id,topic_name,short_code').eq('is_lab',true).eq('discipline',disc).order('topic_name');
    setLabTopics(topics||[]);
    const {data:stds} = await supabase.from('students')
      .select('id,full_name,register_number')
      .eq('batch_name',batchName).eq('is_active',true)
      .not('student_status','in','("Discontinued","Shifted","Passed Out")')
      .order('register_number');
    setAllStudents(stds||[]);
    if (bData?.id && topics?.length) {
      const {data:att} = await supabase.from('lab_attendance').select('*').eq('batch_id',bData.id);
      const ld = {};
      (topics||[]).forEach(t => { ld[t.id] = {sessions:{}}; });
      (att||[]).forEach(a => {
        if (!ld[a.topic_id]) ld[a.topic_id] = {sessions:{}};
        const _k = mkKey(a.session_date, a.session_no || 1);
        if (!ld[a.topic_id].sessions[_k]) ld[a.topic_id].sessions[_k] = [];
        ld[a.topic_id].sessions[_k].push(a);
      });
      setLabData(ld);
    }
    setLoading(false);
  }

  async function saveLabRepeat() {
    if (!labRepeatSession) return;
    if (!labOwn) { toast.error(NOT_OWN); return; }
    setSaving(true);
    const recs = labData[labRepeatSession.topicId]?.sessions[labRepeatSession.originalDate]||[];
    const presentStudents = recs.filter(r =>
      labRepeatSession.studentIds.has(r.student_id) && r.status === 'present'
    );
    if (!presentStudents.length) { toast.error('Mark at least one student present'); setSaving(false); return; }
    for (const r of presentStudents) {
      await supabase.from('lab_attendance')
        .update({ session_type: 'repeat', original_date: labRepeatSession.repeatDate })
        .eq('student_id', r.student_id)
        .eq('topic_id', labRepeatSession.topicId)
        .eq('session_date', skDate(labRepeatSession.originalDate))
        .eq('session_no', skNo(labRepeatSession.originalDate));
    }
    toast.success(`${presentStudents.length} student(s) marked present via repeat on ${dmy(labRepeatSession.repeatDate)}`);
    setLabRepeatSession(null);
    await loadLabData(selBatch, batchInfo);
    setSaving(false);
  }

  async function takeRepeat() {
    if (!repeatDate || !repeatModal) return;
    if (!labOwn) { toast.error(NOT_OWN); return; }
    // Store repeat info - mark absent students on original date
    setLabRepeatSession({
      repeatDate,
      originalDate: repeatModal.originalDate,
      topicId: repeatModal.topicId,
      studentIds: new Set(repeatModal.absentStudents.map(s=>s.id))
    });
    setRepeatModal(null);
    setRepeatDate('');
    setSelSession(repeatModal.originalDate); // Stay on original date session
    toast.success('Now mark who attended the repeat — click students to toggle present');
  }

  async function addSession() {
    if (!newDate || !selTopic || !batchInfo) return;
    if (!labOwn) { toast.error(NOT_OWN); return; }
    setSaving(true);
    const studentsForSession = allStudents.filter(s => sessionStudents.includes(s.id));
    if (studentsForSession.length === 0) { toast.error('Select at least one student'); setSaving(false); return; }
    const sNo = Number(newSessNo) || 1;
    if (labData[selTopic.id]?.sessions[mkKey(newDate, sNo)] && !window.confirm(`Session ${sNo} on ${dmy(newDate)} already exists for this topic. Replace it?`)) { setSaving(false); return; }
    // Delete existing records for this date / session number first to avoid stale records
    await supabase.from('lab_attendance')
      .delete().eq('topic_id', selTopic.id).eq('session_date', newDate).eq('session_no', sNo).eq('batch_id', batchInfo.id);
    const inserts = studentsForSession.map(s => ({
      student_id:s.id, topic_id:selTopic.id, batch_id:batchInfo.id,
      session_date:newDate, session_no:sNo, status:'present',
      session_type: sessionType||'regular',
      original_date: (sessionType==='repeat' && originalDate) ? originalDate : null
    }));
    const {error} = await supabase.from('lab_attendance')
      .upsert(inserts, {onConflict:'student_id,topic_id,session_date,session_no'});
    if (error) { toast.error(error.message); setSaving(false); return; }
    await loadLabData(selBatch, batchInfo);
    setNewDate(''); setNewSessNo(1); setSessionType('regular'); setOriginalDate(''); setAddingSession(false); setSessionStudents([]); setSessionSelectMode(false); setAddSessionSearch('');
    toast.success('Session added!');
    setSaving(false);
  }

  async function updateSessionDate(topicId, oldDate, newDate) {
    if (!labOwn) { toast.error(NOT_OWN); setEditingDate(null); return; }
    if (!newDate || newDate === skDate(oldDate)) { setEditingDate(null); return; }
    if (!window.confirm(`Change session date from ${dmy(oldDate)} to ${dmy(newDate)}?`)) return;
    setSaving(true);
    // keep the session number unless that number is already used on the new date
    const taken = Object.keys(labData[topicId]?.sessions || {}).filter(k => skDate(k) === newDate).map(skNo);
    const wantNo = skNo(oldDate);
    const useNo = taken.includes(wantNo) ? Math.max(...taken) + 1 : wantNo;
    const { error } = await supabase.from('lab_attendance')
      .update({ session_date: newDate, session_no: useNo })
      .eq('topic_id', topicId)
      .eq('session_date', skDate(oldDate))
      .eq('session_no', skNo(oldDate))
      .eq('batch_id', batchInfo.id);
    if (error) { toast.error(error.message); }
    else { toast.success('Session date updated!'); setEditingDate(null); loadLabData(); }
    setSaving(false);
  }

  async function deleteSession(topicId, date) {
    if (!labOwn) { toast.error(NOT_OWN); return; }
    if (!window.confirm(`Delete session ${dmy(date)}? This removes all attendance records for this date.`)) return;
    await supabase.from('lab_attendance')
      .delete().eq('topic_id',topicId).eq('session_date',skDate(date)).eq('session_no',skNo(date)).eq('batch_id',batchInfo.id);
    if (selSession===date) setSelSession(null);
    await loadLabData(selBatch, batchInfo);
    toast.success('Session deleted');
  }

  async function toggleStatus(rec, newStatus) {
    if (!labOwn) { toast.error(NOT_OWN); return; }
    await supabase.from('lab_attendance').update({status:newStatus})
      .eq('student_id',rec.student_id).eq('topic_id',rec.topic_id).eq('session_date',rec.session_date).eq('session_no',rec.session_no||1);
    setLabData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const recs = next[rec.topic_id]?.sessions[mkKey(rec.session_date, rec.session_no||1)]||[];
      const r = recs.find(x=>x.student_id===rec.student_id);
      if (r) r.status = newStatus;
      return next;
    });
  }

  async function markAll(topicId, date, status) {
    if (!labOwn) { toast.error(NOT_OWN); return; }
    const recs = labData[topicId]?.sessions[date]||[];
    await supabase.from('lab_attendance').update({status})
      .eq('topic_id',topicId).eq('session_date',skDate(date)).eq('session_no',skNo(date)).eq('batch_id',batchInfo.id);
    setLabData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      (next[topicId]?.sessions[date]||[]).forEach(r => r.status=status);
      return next;
    });
    toast.success(`All marked ${status}`);
  }

  function topicStats(topicId) {
    const td = labData[topicId]||{sessions:{}};
    const sessions = Object.keys(td.sessions).sort();
    const totalSessions = sessions.length;
    let completed=0, pending=0;
    allStudents.forEach(s => {
      const studentSessions = sessions.filter(d=>td.sessions[d].find(r=>r.student_id===s.id&&r.status!=='not_scheduled'));
      if (studentSessions.length === 0) return; // not scheduled, skip
      const attended = studentSessions.filter(d=>td.sessions[d].find(r=>r.student_id===s.id&&r.status==='present')).length;
      if (studentSessions.length>0&&(attended/studentSessions.length*100)>=75) completed++; else pending++;
    });
    const totalPresent = Object.values(td.sessions).reduce((a,recs)=>a+recs.filter(r=>r.status==='present').length,0);
    const totalScheduled = Object.values(td.sessions).reduce((a,recs)=>a+recs.length,0);
    const overall = totalScheduled>0?(totalPresent/totalScheduled*100).toFixed(0):0;
    return {totalSessions,completed,pending,overall};
  }

  // Print 1: Summary (present/total/%) - compact, fits any page size
  function printStudentSummary() {
    const activeLabs = labTopics.filter(t => { const s=labData[t.id]?.sessions||{}; return Object.keys(s).some(d=>s[d]?.length>0); });
    const rows = allStudents.map((s,i) => {
      let tp=0, pr=0;
      const cells = activeLabs.map(t => {
        const sessions = Object.keys(labData[t.id]?.sessions||{}).sort();
        const present = sessions.filter(d => labData[t.id].sessions[d].find(r => r.student_id===s.id && r.status==='present')).length;
        const total = sessions.filter(d => labData[t.id].sessions[d].find(r => r.student_id===s.id&&r.status!=='not_scheduled')).length;
        tp+=total; pr+=present;
        const pct = total>0 ? (present/total*100).toFixed(0) : 0;
        const bg = pct>=75?'#d1fae5':pct>=50?'#fef9c3':'#fee2e2';
        const col = pct>=75?'#065f46':pct>=50?'#854d0e':'#991b1b';
        return '<td style="text-align:center;background:'+bg+';color:'+col+';font-weight:700">' + present + '/' + total + '<br/>' + pct + '%</td>';
      }).join('');
      const ovPct = tp>0 ? (pr/tp*100).toFixed(0) : 0;
      const obg = ovPct>=75?'#d1fae5':ovPct>=50?'#fef9c3':'#fee2e2';
      const ocol = ovPct>=75?'#065f46':ovPct>=50?'#854d0e':'#991b1b';
      return '<tr><td style="text-align:center">'+(i+1)+'</td><td style="white-space:nowrap;font-family:monospace">'+s.register_number+'</td><td style="white-space:nowrap">'+s.full_name+'</td>'+cells+'<td style="text-align:center;background:'+obg+';color:'+ocol+';font-weight:700">'+ovPct+'%</td></tr>';
    }).join('');
    const headers = activeLabs.map(t => '<th style="text-align:center;white-space:nowrap">' + (t.short_code||t.topic_name) + '<br/><span style="font-size:8px;font-weight:400">' + Object.keys(labData[t.id]?.sessions||{}).length + ' sess</span></th>').join('');
    const html = '<!DOCTYPE html><html><head><title>Lab Summary - '+selBatch+'</title><style>body{font-family:Arial,sans-serif;font-size:10px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 6px}th{background:#1a3a6b;color:#fff}tr:nth-child(even){background:#f9f9f9}@media print{button{display:none}@page{size:A4 landscape;margin:8mm}}</style></head><body><h2>Lab Attendance Summary — '+selBatch+'</h2><table><thead><tr><th>#</th><th>Reg No</th><th style="text-align:left">Name</th>'+headers+'<th>Overall</th></tr></thead><tbody>'+rows+'</tbody></table><br/><button onclick="window.print()">Print</button></body></html>';
    const w = window.open('','_blank'); w.document.write(html); w.document.close();
  }

  // Print 2: Per-topic detail with all session dates
  function printTopicDetail(topic) {
    const sessions = Object.keys(labData[topic.id]?.sessions||{}).sort();
    if (!sessions.length) { alert('No sessions for this topic'); return; }
    const dateHeaders = sessions.map(d => '<th style="text-align:center;white-space:nowrap;font-size:9px">' + dmy(d) + '</th>').join('');
    const rows = allStudents.map((s,i) => {
      const present = sessions.filter(d => labData[topic.id].sessions[d].find(r => r.student_id===s.id && r.status==='present')).length;
      const pct = sessions.length>0 ? (present/sessions.length*100).toFixed(0) : 0;
      const dateCells = sessions.map(d => {
        const rec = labData[topic.id].sessions[d].find(r => r.student_id===s.id);
        const st = rec?.status||'absent';
        return '<td style="text-align:center;background:' + (st==='present'?'#d1fae5':'#fee2e2') + ';color:' + (st==='present'?'#065f46':'#991b1b') + ';font-weight:700">' + (st==='present'?'P':'A') + '</td>';
      }).join('');
      const bg = pct>=75?'#d1fae5':pct>=50?'#fef9c3':'#fee2e2';
      const col = pct>=75?'#065f46':pct>=50?'#854d0e':'#991b1b';
      return '<tr><td style="text-align:center">'+(i+1)+'</td><td style="white-space:nowrap;font-family:monospace">'+s.register_number+'</td><td style="white-space:nowrap">'+s.full_name+'</td>'+dateCells+'<td style="text-align:center;background:'+bg+';color:'+col+';font-weight:700">'+present+'/'+sessions.length+'<br/>'+pct+'%</td></tr>';
    }).join('');
    const html = '<!DOCTYPE html><html><head><title>'+topic.topic_name+' - '+selBatch+'</title><style>body{font-family:Arial,sans-serif;font-size:10px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 4px}th{background:#1a3a6b;color:#fff}tr:nth-child(even){background:#f9f9f9}@media print{button{display:none}@page{size:A4 landscape;margin:8mm}}</style></head><body><h2>'+topic.topic_name+' — '+selBatch+'</h2><p style="margin-bottom:8px;font-size:11px">'+sessions.length+' Sessions &nbsp;|&nbsp; '+allStudents.length+' Students</p><table><thead><tr><th>#</th><th>Reg No</th><th style="text-align:left">Name</th>'+dateHeaders+'<th>Total</th></tr></thead><tbody>'+rows+'</tbody></table><br/><button onclick="window.print()">Print</button></body></html>';
    const w = window.open('','_blank'); w.document.write(html); w.document.close();
  }




  const topicSessions = selTopic ? Object.keys(labData[selTopic.id]?.sessions||{}).sort() : [];



  return (
    <div style={{padding:'0 0 40px'}}>
      <div className="card" style={{marginBottom:16}}>
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          <span className="card-title" style={{marginBottom:0}}>🧪 Lab Attendance</span>
          <SearchableSelect
            value={selBatch}
            onChange={v=>{setSelBatch(v||'');if(v)loadLabData(v);}}
            options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
            placeholder="— Select Batch —"
            searchPlaceholder="Search batch..."
            style={{minWidth:180}}
          />
          {selBatch && labTopics.length>0 && (
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[{v:'overview',l:'📊 Overview'},{v:'topic',l:'🗓 Sessions'},{v:'student',l:'👤 Students'}].map(({v,l})=>(
                <button key={v} className={`btn btn-sm ${view===v?'btn-primary':'btn-secondary'}`}
                  onClick={()=>{setView(v);if(v==='topic'&&!selTopic&&labTopics[0])setSelTopic(labTopics[0]);}}>{l}</button>
              ))}
              {view==='student' && <button className="btn btn-secondary btn-sm" onClick={printStudentSummary}>🖨️ Print Summary</button>}
            </div>
          )}
          {loading && <span style={{fontSize:12,color:'var(--text3)'}}>⏳ Loading...</span>}
        </div>
      </div>

      {!selBatch && <div className="empty-state"><p>Select a batch to view lab attendance</p></div>}
      {selBatch && !loading && labTopics.length===0 && (
        <div className="empty-state"><p>No lab topics found. Go to Master Data → Topics and mark topics as 🧪 Lab.</p></div>
      )}

      {/* OVERVIEW */}
      {view==='overview' && labTopics.length>0 && !loading && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:16}}>
          {labTopics.map(t=>{
            const {totalSessions,completed,pending,overall}=topicStats(t.id);
            return (
              <div key={t.id} className="card" style={{cursor:'pointer',borderLeft:`4px solid ${overall>=75?'var(--green)':overall>=50?'var(--amber)':'var(--red)'}`}}
                onClick={()=>{setSelTopic(t);setView('topic');}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                  <div>
                    <span className="badge badge-blue" style={{marginRight:6,fontSize:10}}>{t.short_code}</span>
                    <span style={{fontWeight:600,fontSize:13}}>{t.topic_name}</span>
                  </div>
                  <span style={{fontSize:22,fontWeight:800,color:overall>=75?'var(--green)':overall>=50?'var(--amber)':'var(--red)'}}>{overall}%</span>
                </div>
                <div style={{display:'flex',gap:12,fontSize:11}}>
                  <span>📅 {totalSessions} session{totalSessions!==1?'s':''}</span>
                  <span style={{color:'var(--green)'}}>✓ {completed} done</span>
                  <span style={{color:'var(--red)'}}>⏳ {pending} pending</span>
                </div>
                <div style={{marginTop:8,height:4,background:'var(--border)',borderRadius:2}}>
                  <div style={{height:'100%',width:`${overall}%`,background:overall>=75?'var(--green)':overall>=50?'var(--amber)':'var(--red)',borderRadius:2}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SESSIONS VIEW */}
      {view==='topic' && !loading && (
        <div>
          <div className="card" style={{marginBottom:16,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontWeight:600,fontSize:12,color:'var(--text3)'}}>Topic:</span>
            {labTopics.map(t=>(
              <button key={t.id} className={`btn btn-sm ${selTopic?.id===t.id?'btn-primary':'btn-secondary'}`}
                onClick={()=>{setSelTopic(t);setSelSession(null);loadCompletions(t.id,batchInfo?.id);}}>
                {t.short_code||t.topic_name}
              </button>
            ))}
            {selTopic && labOwn && <button className="btn btn-secondary btn-sm" style={{marginLeft:'auto'}} onClick={()=>setAddingSession(true)}>+ Add Session</button>}
            {selTopic && <button className="btn btn-ghost btn-sm" onClick={()=>printTopicDetail(selTopic)}>🖨️ Print Topic</button>}
          </div>

          {addingSession && (
            <div className="card" style={{marginBottom:16}}>
              <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginBottom:8}}>
                <label style={{fontWeight:500,fontSize:13}}>Session Date:</label>
                <DateInput value={newDate} onChange={e=>{
                    const v=e.target.value; setNewDate(v);
                    const used=Object.keys(labData[selTopic?.id]?.sessions||{}).filter(k=>skDate(k)===v).map(skNo);
                    setNewSessNo(used.length?Math.max(...used)+1:1);
                  }}
                  style={{fontSize:13,padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)'}}/>
                <label style={{fontWeight:500,fontSize:13}}>Session of the day:</label>
                <select value={newSessNo} onChange={e=>setNewSessNo(Number(e.target.value))} style={{fontSize:13,padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)'}}>
                  {[1,2,3,4,5,6].map(n=><option key={n} value={n}>Session {n}</option>)}
                </select>
                {(()=>{
                  const sib=Object.keys(labData[selTopic?.id]?.sessions||{}).filter(k=>skDate(k)===newDate&&skNo(k)!==Number(newSessNo)).sort();
                  if(!newDate||!sib.length) return null;
                  const src=sib.find(k=>skNo(k)===1)||sib[0];
                  const ids=(labData[selTopic.id].sessions[src]||[]).map(r=>r.student_id);
                  return <button type="button" className="btn btn-secondary btn-sm" onClick={()=>{setSessionSelectMode(true);setSessionStudents(ids);}}>📋 Copy students from Session {skNo(src)} ({ids.length})</button>;
                })()}
                <button className="btn btn-primary btn-sm" onClick={addSession} disabled={saving||!newDate||sessionStudents.length===0}>{saving?'Adding...':sessionStudents.length===0?'Select students first':'Add Session ('+sessionStudents.length+')'}</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{setAddingSession(false);setNewDate('');setSessionStudents([]);setAddSessionSearch(''); }}>Cancel</button>
              </div>
              <div style={{fontSize:12,marginTop:4}}>
                <div style={{marginBottom:6,fontWeight:500,color:'var(--text2)',display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  👥 Scheduled Students: 
                  <span style={{color:'var(--text3)',fontWeight:400}}>
                    {sessionStudents.length===0?'Click students below to select':''+sessionStudents.length+' selected'}
                  </span>
                  {selTopic&&(()=>{
                    const topicSessions = labData[selTopic.id]?.sessions||{};
                    const dates = Object.keys(topicSessions).sort().reverse();
                    if(dates.length===0) return null;
                    const lastRecs = topicSessions[dates[0]]||[];
                    if(lastRecs.length===0) return null;
                    return (
                      <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'var(--blue)'}}
                        onClick={()=>{setSessionSelectMode(true);setSessionStudents(lastRecs.map(r=>r.student_id));}}>
                        📋 Last session ({lastRecs.length} students)
                      </button>
                    );
                  })()}
                  <button className="btn btn-ghost btn-sm" style={{marginLeft:4,fontSize:11}}
                    onClick={()=>setSessionStudents([])}>
                    Clear Selection
                  </button>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4,maxHeight:120,overflowY:'auto'}}>
                  <span style={{marginLeft:8,fontSize:11}}>
                    {completions.length>0&&<button className="btn btn-ghost btn-sm" style={{fontSize:10}}
                      onClick={()=>setShowCompleted(s=>!s)}>
                      {showCompleted?'Hide':'Show'} {completions.length} Completed
                    </button>}
                  </span>
                  <div style={{width:'100%',marginBottom:6}}>
                    <input placeholder="🔍 Search by name or reg number..." value={addSessionSearch} onChange={e=>setAddSessionSearch(e.target.value)} style={{width:'100%',padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',fontSize:12}}/>
                  </div>
                  {allStudents.filter(s=>(showCompleted||!completions.includes(s.id))&&(!addSessionSearch||(s.full_name?.toLowerCase().includes(addSessionSearch.toLowerCase())||s.register_number?.toLowerCase().includes(addSessionSearch.toLowerCase()))))
                  .sort((a,b)=>{
                    const aSelected=sessionStudents.includes(a.id);
                    const bSelected=sessionStudents.includes(b.id);
                    return aSelected===bSelected?0:aSelected?-1:1;
                  }).map(s=>(
                    <span key={s.id}
                      onClick={()=>{
                        if(completions.includes(s.id)) return;
                        if(!sessionSelectMode) setSessionSelectMode(true);
                        setSessionStudents(prev=>
                          prev.includes(s.id)?prev.filter(id=>id!==s.id):[...prev,s.id]
                        );
                      }}
                      style={{padding:'4px 10px',borderRadius:8,fontSize:11,cursor:completions.includes(s.id)?'not-allowed':'pointer',fontWeight:500,
                        background:completions.includes(s.id)?'var(--green-light)':sessionStudents.includes(s.id)?'var(--accent)':'#f1f5f9',
                        color:completions.includes(s.id)?'var(--green)':sessionStudents.includes(s.id)?'#fff':'#94a3b8',
                        border:'none',opacity:completions.includes(s.id)?0.7:1}}>
                      {s.full_name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selTopic && topicSessions.length===0 && (
            <div className="empty-state"><p>No sessions yet for {selTopic.topic_name}. Click "+ Add Session".</p></div>
          )}

          {selTopic && topicSessions.length>0 && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12,marginBottom:16}}>
              {topicSessions.map(date=>{
                const recs=labData[selTopic.id]?.sessions[date]||[];
                const present=recs.filter(r=>r.status==='present').length;
                const total=recs.length;
                const pct=total>0?(present/total*100):0;
                const isRepeat=recs[0]?.session_type==='repeat';
                const origDate=recs[0]?.original_date;
                return (
                  <div key={date} className="card" style={{borderLeft:`4px solid ${selSession===date?'var(--accent)':pct>=75?'var(--green)':pct>=50?'var(--amber)':'var(--red)'}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                      <div style={{fontWeight:600,fontSize:13,cursor:'pointer'}} onClick={()=>{setSelSession(selSession===date?null:date);setLabSearch('');}}>
                        {dmy(date)}
                        {isRepeat&&<span style={{fontSize:10,background:'#fef9c3',color:'#854d0e',border:'1px solid #fde68a',borderRadius:4,padding:'1px 5px',marginLeft:6}}>🔄 REPEAT{origDate?' — for '+dmy(origDate):''}</span>}
                      </div>
                      {editingDate?.topicId===selTopic.id&&editingDate?.oldDate===date ? (
                        <span style={{display:'flex',alignItems:'center',gap:4}}>
                          <DateInput value={date}
                            style={{fontSize:11,padding:'1px 4px',border:'1px solid var(--border)',borderRadius:4}}
                            onChange={e=>{ if (e.target.value && e.target.value!==date) updateSessionDate(selTopic.id,date,e.target.value); }}
                            autoFocus/>
                          <button onClick={()=>setEditingDate(null)}
                            style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:12}}>✕</button>
                        </span>
                      ) : (
                        labOwn && <button onClick={()=>setEditingDate({topicId:selTopic.id,oldDate:date})}
                          style={{background:'none',border:'none',cursor:'pointer',color:'var(--blue)',fontSize:13,padding:'2px 4px'}}
                          title="Edit date">✏️</button>
                      )}
                      {labOwn && <button onClick={()=>deleteSession(selTopic.id,date)}
                        style={{background:'none',border:'none',cursor:'pointer',color:'var(--red)',fontSize:14,padding:'2px 4px'}}
                        title="Delete session">🗑</button>}
                    </div>
                    <div style={{display:'flex',gap:10,fontSize:11,marginBottom:6,cursor:'pointer'}} onClick={()=>{setSelSession(selSession===date?null:date);setLabSearch('');}}>
                      <span style={{color:'var(--green)'}}>✓ {present} present</span>
                      <span style={{color:'var(--red)'}}>✗ {total-present} absent</span>
                    </div>
                    <div style={{height:4,background:'var(--border)',borderRadius:2,cursor:'pointer'}} onClick={()=>{setSelSession(selSession===date?null:date);setLabSearch('');}}>
                      <div style={{height:'100%',width:`${pct}%`,background:pct>=75?'var(--green)':pct>=50?'var(--amber)':'var(--red)',borderRadius:2}}/>
                    </div>
                    {labOwn && <div style={{marginTop:6,display:'flex',gap:4}}>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:10,flex:1}}
                        onClick={()=>{
                          const recs=labData[selTopic.id]?.sessions[date]||[];
                          const count=recs.filter(r=>r.status!=='not_scheduled').length;
                          if(window.confirm(`Mark all ${count} students as Present for ${dmy(date)}?`))
                            markAll(selTopic.id,date,'present');
                        }}>All Present</button>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:10,flex:1,color:'var(--red)'}}
                        onClick={()=>{
                          const recs=labData[selTopic.id]?.sessions[date]||[];
                          const count=recs.filter(r=>r.status!=='not_scheduled').length;
                          if(window.confirm(`Mark all ${count} students as Absent for ${dmy(date)}?`))
                            markAll(selTopic.id,date,'absent');
                        }}>All Absent</button>
                    </div>}
                  </div>
                );
              })}
            </div>
          )}

          {selSession && selTopic && (
            <div className="card" style={{overflowX:'auto',overflowY:'auto',maxHeight:'75vh'}}>
              <div style={{fontWeight:600,fontSize:14,marginBottom:12}}>
                📋 {selTopic.topic_name} — {dmy(selSession)}
                <span style={{fontSize:12,color:'var(--text3)',marginLeft:8}}>Click student to toggle</span>
              </div>
              {labRepeatSession?.topicId===selTopic.id&&labRepeatSession?.originalDate===selSession&&(
                <div style={{background:'#fef9c3',border:'1px solid #fde68a',borderRadius:6,padding:'6px 10px',marginBottom:8,fontSize:12,color:'#854d0e'}}>
                  🔄 Repeat session mode — marking for <strong>{dmy(labRepeatSession.repeatDate)}</strong>
                  &nbsp;— Only absent students shown. Toggle present then click <strong>Save Repeat</strong>
                  <button className="btn btn-primary btn-sm" style={{marginLeft:12}} onClick={saveLabRepeat}>Save Repeat</button>
                  <button className="btn btn-ghost btn-sm" style={{marginLeft:6}} onClick={()=>setLabRepeatSession(null)}>Cancel</button>
                </div>
              )}
              {(() => {
                const recs = labData[selTopic.id]?.sessions[selSession]||[];
                const isRepeatSession = recs[0]?.session_type === 'repeat';
                const absentStudents = recs
                  .filter(r => r.status === 'absent')
                  .map(r => allStudents.find(s => s.id === r.student_id))
                  .filter(Boolean);
                return labOwn && !isRepeatSession && absentStudents.length > 0 ? (
                  <button className="btn btn-ghost btn-sm" style={{marginBottom:8,fontSize:11,color:'var(--amber)'}}
                    onClick={()=>{setRepeatModal({topicId:selTopic.id,originalDate:selSession,absentStudents});setRepeatDate('');}}>
                    🔄 Take Repeat for {absentStudents.length} absent student{absentStudents.length>1?'s':''}
                  </button>
                ) : null;
              })()}
              <div style={{marginBottom:8}}>
                <input placeholder="🔍 Search by name or reg number..." value={labSearch} onChange={e=>setLabSearch(e.target.value)} style={{width:'100%',padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',fontSize:12}}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:8}}>
                {[...(labData[selTopic.id]?.sessions[selSession]||[])].filter(rec=>{
                  if(labRepeatSession?.topicId===selTopic.id&&labRepeatSession?.originalDate===selSession){
                    return labRepeatSession.studentIds.has(rec.student_id);
                  }
                  return true;
                }).filter(rec=>{
                  if(!labSearch) return true;
                  const stu=allStudents.find(s=>s.id===rec.student_id);
                  const q=labSearch.toLowerCase();
                  return stu?.full_name?.toLowerCase().includes(q)||stu?.register_number?.toLowerCase().includes(q);
                }).sort((a,b)=>{const sa=allStudents.find(x=>x.id===a.student_id);const sb=allStudents.find(x=>x.id===b.student_id);return (sa?.register_number||'').localeCompare(sb?.register_number||'',undefined,{numeric:true});}).map(rec=>{
                  const stu=allStudents.find(s=>s.id===rec.student_id);
                  if (!stu) return null;
                  return (
                    <div key={rec.student_id}
                      style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:8,
                        background:completions.includes(rec.student_id)?'rgba(34,197,94,0.06)':rec.status==='present'?'rgba(34,197,94,0.1)':rec.status==='not_scheduled'?'rgba(148,163,184,0.1)':'rgba(239,68,68,0.05)',
                        border:`1px solid ${completions.includes(rec.student_id)?'rgba(34,197,94,0.3)':rec.status==='present'?'rgba(34,197,94,0.35)':rec.status==='not_scheduled'?'rgba(148,163,184,0.3)':'var(--border)'}`,transition:'all 0.15s'}}>
                      <span onClick={()=>labOwn&&toggleStatus(rec,rec.status==='present'?'absent':rec.status==='absent'?'not_scheduled':'present')}
                        style={{fontSize:18,minWidth:22,textAlign:'center',cursor:'pointer',
                          color:rec.status==='present'?'var(--green)':rec.status==='not_scheduled'?'var(--text3)':'var(--red)'}}>
                        {rec.status==='present'?'✓':rec.status==='not_scheduled'?'—':'✗'}
                      </span>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:500,fontSize:12}}>{stu.full_name}</div>
                        <div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{stu.register_number}</div>
                        {rec.status==='not_scheduled'&&<div style={{fontSize:9,color:'var(--text3)'}}>Not scheduled</div>}
                      </div>
                      <button disabled={!labOwn} onClick={e=>{e.stopPropagation();markComplete(rec.student_id,selTopic.id,batchInfo.id,completions.includes(rec.student_id));}}
                        title={completions.includes(rec.student_id)?'Unmark Complete':'Mark Lab Complete'}
                        style={{background:'none',border:'none',cursor:'pointer',fontSize:14,padding:'2px',
                          color:completions.includes(rec.student_id)?'var(--green)':'var(--text3)'}}>
                        {completions.includes(rec.student_id)?'✅':'⬜'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* STUDENT SUMMARY */}
      <style>{`
        .lab-student-table thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background: #dbeafe !important;
          border-bottom: 2px solid #3b82f6 !important;
        }
      `}</style>
      {view==='student' && !loading && labTopics.length>0 && (
        <div className="card" style={{overflowX:'auto'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
            <input className="input" style={{width:260}} placeholder="🔍 Search name or register no..." value={labStudentSearch} onChange={e=>setLabStudentSearch(e.target.value)} />
            {labStudentSearch && <button className="btn btn-secondary btn-sm" onClick={()=>setLabStudentSearch('')}>✕ Clear</button>}
          </div>
          <div style={{maxHeight:'60vh',overflowY:'auto'}}><table className="lab-student-table" style={{width:'100%',borderCollapse:'separate',borderSpacing:0,fontSize:12}}>
            <thead style={{position:'sticky',top:0,zIndex:10,background:'#e8f4fd'}}>
              <tr>
                <th style={{padding:'8px',textAlign:'left',minWidth:40,background:'#e8f4fd',position:'sticky',top:0,boxShadow:'0 2px 0 #93c5fd'}}>#</th>
                <th style={{padding:'8px',textAlign:'left',minWidth:90,background:'#e8f4fd',position:'sticky',top:0,boxShadow:'0 2px 0 #93c5fd'}}>Reg No</th>
                <th style={{padding:'8px',textAlign:'left',minWidth:150,background:'#e8f4fd',position:'sticky',top:0,boxShadow:'0 2px 0 #93c5fd'}}>Name</th>
                {labTopics.map(t=>(
                  <th key={t.id} style={{padding:'6px 4px',textAlign:'center',minWidth:70,fontSize:10,background:'#e8f4fd',position:'sticky',top:0,boxShadow:'0 2px 0 #93c5fd'}}>
                    {t.short_code||t.topic_name}<br/>
                    <span style={{fontWeight:600,color:'#475569'}}>({Object.keys(labData[t.id]?.sessions||{}).length} sess)</span>
                  </th>
                ))}
                <th style={{padding:'8px',textAlign:'center',minWidth:60,background:'#e8f4fd',position:'sticky',top:0,boxShadow:'0 2px 0 #93c5fd'}}>Overall</th>
              </tr>
            </thead>
            <tbody>
              {allStudents.filter(s=>{const q=labStudentSearch.trim().toLowerCase();return !q||(s.full_name||'').toLowerCase().includes(q)||(s.register_number||'').toLowerCase().includes(q);}).map((s,si)=>{
                let tp=0,pr=0;
                const cells = labTopics.map(t=>{
                  const sessions=Object.keys(labData[t.id]?.sessions||{});
                  const present=sessions.filter(d=>labData[t.id].sessions[d].find(r=>r.student_id===s.id&&r.status==='present')).length;
                  const repeatSess=sessions.filter(d=>labData[t.id].sessions[d]?.[0]?.session_type==='repeat');
                  const total=sessions.length;
                  tp+=total; pr+=present;
                  const pct=total>0?(present/total*100).toFixed(0):null;
                  return (
                    <td key={t.id} style={{padding:'4px',textAlign:'center'}}>
                      {pct===null?<span style={{color:'var(--text3)'}}>—</span>:
                        <span className={`badge ${pctClass(pct)}`} style={{fontSize:10,display:'inline-block',lineHeight:1.4}}>
                          {present}/{total}<br/>{pct}%
                        </span>}
                    </td>
                  );
                });
                const ovPct=tp>0?(pr/tp*100).toFixed(0):0;
                return (
                  <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:si%2===0?'':'var(--surface2)',cursor:'pointer'}} onClick={()=>setSelStudent(s)}>
                    <td style={{padding:'6px 8px',color:'var(--text3)'}}>{si+1}</td>
                    <td style={{padding:'6px 8px',fontFamily:'monospace',fontSize:11,color:'var(--accent)'}}>{s.register_number}</td>
                    <td style={{padding:'6px 8px',fontWeight:500}}>{s.full_name}</td>
                    {cells}
                    <td style={{padding:'6px',textAlign:'center'}}>
                      {tp>0?<span className={`badge ${pctClass(ovPct)}`} style={{fontWeight:700}}>{ovPct}%</span>
                        :<span style={{color:'var(--text3)'}}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    {/* Student Detail Modal */}
      {selStudent && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSelStudent(null)}>
          <div className="modal" style={{maxWidth:700}}>
            <div className="modal-header">
              <span className="modal-title">👤 {selStudent.full_name} — {selStudent.register_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setSelStudent(null)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'65vh',overflowY:'auto'}}>
              {labTopics.map(t=>{
                const sessions=Object.keys(labData[t.id]?.sessions||{}).sort();
                const allStudentSessions=sessions.filter(d=>labData[t.id].sessions[d].find(r=>r.student_id===selStudent.id));
                const scheduledSessions=allStudentSessions.filter(d=>labData[t.id].sessions[d].find(r=>r.student_id===selStudent.id&&r.status!=='not_scheduled'));
                const present=scheduledSessions.filter(d=>labData[t.id].sessions[d].find(r=>r.student_id===selStudent.id&&r.status==='present')).length;
                const pct=scheduledSessions.length>0?(present/scheduledSessions.length*100).toFixed(0):null;
                return (
                  <div key={t.id} style={{marginBottom:16,padding:'12px',borderRadius:8,background:'var(--surface2)',border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                      <div>
                        <span className="badge badge-blue" style={{marginRight:6,fontSize:10}}>{t.short_code}</span>
                        <span style={{fontWeight:600,fontSize:13}}>{t.topic_name}</span>
                      </div>
                      {pct!==null?<span className={`badge ${pctClass(pct)}`} style={{fontSize:13,fontWeight:700}}>{present}/{scheduledSessions.length} — {pct}%</span>
                        :<span style={{color:'var(--text3)',fontSize:12}}>No sessions</span>}
                    </div>
                    {sessions.length>0&&(
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        {sessions.map(date=>{
                          const rec=labData[t.id].sessions[date].find(r=>r.student_id===selStudent.id);
                          if(!rec) return null; // not scheduled
                          const isPresent=rec.status==='present';
                          const isNotSched=rec.status==='not_scheduled';
                          return (
                            <span key={date} style={{padding:'3px 8px',borderRadius:6,fontSize:11,fontWeight:500,
                              background:isPresent?'rgba(34,197,94,0.15)':isNotSched?'rgba(148,163,184,0.1)':'rgba(239,68,68,0.1)',
                              color:isPresent?'var(--green)':isNotSched?'var(--text3)':'var(--red)',
                              border:`1px solid ${isPresent?'rgba(34,197,94,0.3)':isNotSched?'rgba(148,163,184,0.3)':'rgba(239,68,68,0.3)'}`}}>
                              {isPresent?'✓':isNotSched?'—':'✗'} {dmy(date)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={()=>{
                const rows = labTopics.map(t => {
                  const sessions=Object.keys(labData[t.id]?.sessions||{}).sort();
                  const present=sessions.filter(d=>labData[t.id].sessions[d].find(r=>r.student_id===selStudent.id&&r.status==='present')).length;
                  const pct=sessions.length>0?(present/sessions.length*100).toFixed(0):null;
                  const dateCells=sessions.map(d=>{
                    const rec=labData[t.id].sessions[d].find(r=>r.student_id===selStudent.id);
                    const st=rec?.status||'absent';
                    return '<td style="text-align:center;background:'+(st==='present'?'#d1fae5':'#fee2e2')+';color:'+(st==='present'?'#065f46':'#991b1b')+'">'+(st==='present'?'P':'A')+'<br/><span style="font-size:8px">'+dmy(d)+'</span></td>';
                  }).join('');
                  const pctCell=pct===null?'<td style="text-align:center;color:#999">—</td>':'<td style="text-align:center;font-weight:700;background:'+(pct>=75?'#d1fae5':pct>=50?'#fef9c3':'#fee2e2')+';color:'+(pct>=75?'#065f46':pct>=50?'#854d0e':'#991b1b')+'">'+present+'/'+sessions.length+' ('+pct+'%)</td>';
                  const dateHeaders=sessions.map(d=>'<th style="text-align:center;font-size:9px;white-space:nowrap">'+dmy(d)+'</th>').join('');
                  return {name:t.topic_name,short:t.short_code,dateHeaders,dateCells,pctCell,sessions:sessions.length};
                });
                const tables = rows.map(r => r.sessions===0?'':'<h3 style="margin:12px 0 4px;font-size:12px;color:#1a3a6b">'+r.short+' — '+r.name+'</h3><table><thead><tr><th>Date</th>'+r.dateHeaders+'<th>Total</th></tr></thead><tbody><tr><td style="font-weight:500">Status</td>'+r.dateCells+r.pctCell+'</tr></tbody></table>').join('');
                const html='<!DOCTYPE html><html><head><title>Lab Report - '+selStudent.full_name+'</title><style>body{font-family:Arial,sans-serif;font-size:11px}table{border-collapse:collapse;margin-bottom:8px}th,td{border:1px solid #ccc;padding:5px 8px}th{background:#1a3a6b;color:#fff}h2{font-size:14px;margin-bottom:4px}h3{color:#1a3a6b}p{font-size:11px;color:#666;margin-bottom:12px}@media print{button{display:none}@page{size:A4;margin:12mm}}</style></head><body><h2>Lab Attendance Report</h2><p>'+selStudent.full_name+' &nbsp;|&nbsp; '+selStudent.register_number+' &nbsp;|&nbsp; Batch: '+selBatch+'</p>'+tables+'<br/><button onclick="window.print()">Print</button></body></html>';
                const w=window.open('','_blank'); w.document.write(html); w.document.close();
              }}>🖨️ Print Student Report</button>
              <button className="btn btn-secondary" onClick={()=>setSelStudent(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    {/* Repeat Session Modal */}
      {repeatModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setRepeatModal(null)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-header">
              <span className="modal-title">🔄 Schedule Repeat Session</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setRepeatModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{marginBottom:12,fontSize:13,color:'var(--text2)'}}>
                Original date: <strong>{dmy(repeatModal.originalDate)}</strong>
                &nbsp;|&nbsp; <strong>{repeatModal.absentStudents.length} absent students</strong> will be added
              </p>
              <div style={{marginBottom:12}}>
                <label style={{fontWeight:500,fontSize:13,display:'block',marginBottom:4}}>Repeat Session Date:</label>
                <DateInput value={repeatDate} onChange={e=>setRepeatDate(e.target.value)}
                  style={{fontSize:13,padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',width:'100%'}}/>
              </div>
              <div style={{maxHeight:200,overflowY:'auto',background:'var(--surface2)',borderRadius:8,padding:8}}>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:6}}>Students who will be in repeat session:</div>
                {repeatModal.absentStudents.map(s=>(
                  <div key={s.id} style={{fontSize:12,padding:'2px 0'}}>{s.register_number} — {s.full_name}</div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setRepeatModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!repeatDate||saving} onClick={takeRepeat}>
                {saving?'Creating...':'✓ Create Repeat Session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function sortBatches(batches) {
  return [...(batches||[])].sort((a,b)=>{
    const order = {ongoing:0,upcoming:1,completed:2};
    const sa = order[a.status]??1;
    const sb = order[b.status]??1;
    if (sa!==sb) return sa-sb;
    return (a.batch_name||'').localeCompare(b.batch_name||'');
  });
}


const CALL_STATUSES = ['Not Called','Called - No Answer','Called - Reason Given','Called - Will Come','Called - Personal Issue','Called - Health Issue','Called - Other'];

function CallLogTab({ supabase, batches, callLogBatch, setCallLogBatch, callLogDate, setCallLogDate,
  callLogStudents, setCallLogStudents, callLogStaff, callLogLoading, setCallLogLoading }) {

  const [logs, setLogs] = React.useState({});  // {student_id: {called_by, reason, remarks, call_status, call_date, id}}
  const [saving, setSaving] = React.useState({});

  async function loadAbsentStudents() {



    if (!callLogBatch || !callLogDate) return;
    setCallLogLoading(true);
    // Get absent students for this batch/date
    const { data: attRecs } = await supabase.from('hr_attendance')
      .select('student_id, attendance_marked, students(id, full_name, register_number, course_name)')
      .eq('batch_name', callLogBatch)
      .eq('date', callLogDate)
      .eq('attendance_marked', 'A');

    const students = (attRecs||[]).map(r => r.students).filter(Boolean);
    setCallLogStudents(students);

    // Load existing call logs for these students on this date
    if (students.length > 0) {
      const { data: existingLogs } = await supabase.from('attendance_absence_calls')
        .select('*')
        .in('student_id', students.map(s => s.id))
        .eq('absent_date', callLogDate)
        .eq('batch_name', callLogBatch);

      const logMap = {};
      (existingLogs||[]).forEach(l => { logMap[l.student_id] = l; });
      setLogs(logMap);
    }
    setCallLogLoading(false);
  }

  function updateLog(studentId, field, value) {
    setLogs(prev => ({...prev, [studentId]: {...(prev[studentId]||{}), [field]: value}}));
  }

  async function saveLog(student) {
    const log = logs[student.id] || {};
    setSaving(prev => ({...prev, [student.id]: true}));
    const payload = {
      student_id: student.id,
      batch_name: callLogBatch,
      absent_date: callLogDate,
      called_by: log.called_by || null,
      call_date: log.call_date || callLogDate,
      reason: log.reason || '',
      remarks: log.remarks || '',
      call_status: log.call_status || 'Not Called',
    };
    if (log.id) {
      await supabase.from('attendance_absence_calls').update(payload).eq('id', log.id);
    } else {
      const { data } = await supabase.from('attendance_absence_calls').insert(payload).select().single();
      if (data) setLogs(prev => ({...prev, [student.id]: {...prev[student.id], id: data.id}}));
    }
    setSaving(prev => ({...prev, [student.id]: false}));
    toast.success('Saved');
  }

  const statusColor = s => ({
    'Not Called': 'var(--red)',
    'Called - No Answer': 'var(--amber)',
    'Called - Reason Given': 'var(--green)',
    'Called - Will Come': 'var(--green)',
    'Called - Personal Issue': '#8b5cf6',
    'Called - Health Issue': '#8b5cf6',
    'Called - Other': 'var(--text3)',
  }[s] || 'var(--text3)');

  const statusDot = s => ({
    'Not Called': '🔴',
    'Called - No Answer': '🟡',
    'Called - Reason Given': '🟢',
    'Called - Will Come': '🟢',
    'Called - Personal Issue': '🟣',
    'Called - Health Issue': '🟣',
    'Called - Other': '⚪',
  }[s] || '🔴');

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{marginBottom:12,display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <SearchableSelect value={callLogBatch} onChange={v=>setCallLogBatch(v||'')}
          options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
          placeholder="Select Batch" searchPlaceholder="Search batch..." style={{minWidth:180}}/>
        <DateInput value={callLogDate} onChange={e=>setCallLogDate(e.target.value)}
          style={{padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)'}}/>
        <button className="btn btn-primary btn-sm" onClick={loadAbsentStudents}
          disabled={!callLogBatch||!callLogDate||callLogLoading}>
          {callLogLoading?'Loading...':'🔍 Load Absent Students'}
        </button>
        {callLogStudents.length > 0 && (
          <span style={{fontSize:12,color:'var(--text3)'}}>
            {callLogStudents.length} absent student(s) on {dmy(callLogDate)}
          </span>
        )}
      </div>

      {/* Student call log table */}
      {callLogStudents.length > 0 && (
        <div className="card" style={{padding:0}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{background:'var(--accent-light)',borderBottom:'2px solid var(--border)'}}>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Student</th>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Package</th>
                <th style={{padding:'8px 12px',textAlign:'center',minWidth:120}}>Status</th>
                <th style={{padding:'8px 12px',textAlign:'left',minWidth:160}}>Called By</th>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Call Date</th>
                <th style={{padding:'8px 12px',textAlign:'left',minWidth:150}}>Reason</th>
                <th style={{padding:'8px 12px',textAlign:'left',minWidth:150}}>Remarks</th>
                <th style={{padding:'8px 12px',textAlign:'center'}}>Action</th>
              </tr>
            </thead>
            <tbody>
              {callLogStudents.map((s,i) => {
                const log = logs[s.id] || {};
                const status = log.call_status || 'Not Called';
                return (
                  <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                    <td style={{padding:'8px 12px'}}>
                      <div style={{fontWeight:600}}>{s.full_name}</div>
                      <div style={{fontSize:11,color:'var(--text3)',fontFamily:'monospace'}}>{s.register_number}</div>
                    </td>
                    <td style={{padding:'8px 12px',fontSize:12,color:'var(--text2)'}}>{s.course_name||'—'}</td>
                    <td style={{padding:'8px 12px',textAlign:'center'}}>
                      <select value={status}
                        onChange={e=>updateLog(s.id,'call_status',e.target.value)}
                        style={{fontSize:11,padding:'3px 6px',color:statusColor(status),fontWeight:600,maxWidth:160}}>
                        {CALL_STATUSES.map(st=><option key={st} value={st}>{statusDot(st)} {st}</option>)}
                      </select>
                    </td>
                    <td style={{padding:'8px 12px'}}>
                      <select value={log.called_by||''}
                        onChange={e=>updateLog(s.id,'called_by',e.target.value)}
                        style={{fontSize:12,padding:'3px 6px',maxWidth:160}}>
                        <option value="">Select caller</option>
                        {callLogStaff.map(st=><option key={st.id} value={st.id}>{st.staff_name}</option>)}
                      </select>
                    </td>
                    <td style={{padding:'8px 12px'}}>
                      <DateInput value={log.call_date||callLogDate}
                        onChange={e=>updateLog(s.id,'call_date',e.target.value)}
                        style={{fontSize:11,padding:'3px 6px',maxWidth:130}}/>
                    </td>
                    <td style={{padding:'8px 12px'}}>
                      <input value={log.reason||''} onChange={e=>updateLog(s.id,'reason',e.target.value)}
                        placeholder="Reason for absence..."
                        style={{fontSize:12,padding:'3px 6px',width:'100%',minWidth:140}}/>
                    </td>
                    <td style={{padding:'8px 12px'}}>
                      <input value={log.remarks||''} onChange={e=>updateLog(s.id,'remarks',e.target.value)}
                        placeholder="Remarks..."
                        style={{fontSize:12,padding:'3px 6px',width:'100%',minWidth:140}}/>
                    </td>
                    <td style={{padding:'8px 12px',textAlign:'center'}}>
                      <button className="btn btn-primary btn-sm"
                        disabled={saving[s.id]}
                        onClick={()=>saveLog(s)}>
                        {saving[s.id]?'...':'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {callLogStudents.length === 0 && callLogBatch && callLogDate && !callLogLoading && (
        <div className="card" style={{textAlign:'center',color:'var(--text3)',padding:40}}>
          ✅ No absent students for {callLogBatch} on {dmy(callLogDate)}
        </div>
      )}
    </div>
  );
}

function CallHistorySection({ supabase, batch, callLogStaff, defaultDate }) {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fStatus, setFStatus] = React.useState('');
  const [fDate, setFDate] = React.useState(defaultDate||'');
  React.useEffect(()=>{if(defaultDate)setFDate(defaultDate);},[defaultDate]);
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { if (batch) loadHistory(); }, [batch]);

  async function loadHistory() {
    setLoading(true);
    const { data } = await supabase.from('attendance_absence_calls')
      .select('*,students(full_name,register_number,course_name)')
      .eq('batch_name', batch)
      .order('absent_date', {ascending:false})
      .order('created_at', {ascending:false});
    setLogs(data||[]);
    setLoading(false);
  }

  async function saveEdit(id) {
    setSaving(true);
    const {error} = await supabase.from('attendance_absence_calls').update(editForm).eq('id',id);
    if(error) toast.error(error.message);
    else { toast.success('Updated'); setEditingId(null); loadHistory(); }
    setSaving(false);
  }

  async function deleteLog(id) {
    if(!window.confirm('Delete this call log?')) return;
    const {error} = await supabase.from('attendance_absence_calls').delete().eq('id',id);
    if(error) toast.error(error.message);
    else { toast.success('Deleted'); loadHistory(); }
  }

  const STATUS_COLORS = {'Not Called':'var(--red)','Called - No Answer':'var(--amber)','Called - Reason Given':'var(--green)','Called - Will Come':'var(--green)','Called - Personal Issue':'#8b5cf6','Called - Health Issue':'#8b5cf6','Called - Other':'var(--text3)'};
  const STATUS_DOTS = {'Not Called':'🔴','Called - No Answer':'🟡','Called - Reason Given':'🟢','Called - Will Come':'🟢','Called - Personal Issue':'🟣','Called - Health Issue':'🟣','Called - Other':'⚪'};
  const STATUSES = ['Not Called','Called - No Answer','Called - Reason Given','Called - Will Come','Called - Personal Issue','Called - Health Issue','Called - Other'];
  const filtered = logs.filter(l => (!fStatus||l.call_status===fStatus)&&(!fDate||l.absent_date===fDate));
  const getCallerName = (id) => callLogStaff.find(s=>s.id===id)?.staff_name || '—';

  return (
    <div className="card" style={{marginTop:16}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12,flexWrap:'wrap'}}>
        <span style={{fontWeight:600,fontSize:14}}>📋 Call History — {batch}</span>
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{fontSize:12,padding:'3px 8px',maxWidth:180}}>
          <option value="">All Statuses</option>
          {STATUSES.map(s=><option key={s} value={s}>{STATUS_DOTS[s]} {s}</option>)}
        </select>
        <DateInput value={fDate} onChange={e=>setFDate(e.target.value)}
          style={{fontSize:12,padding:'3px 8px',border:'1px solid var(--border)',borderRadius:4}}/>
        {fDate&&<button className="btn btn-ghost btn-sm" onClick={()=>setFDate('')}>✕ Clear Date</button>}
        <button className="btn btn-ghost btn-sm" onClick={loadHistory} style={{marginLeft:'auto'}}>🔄 Refresh</button>
        <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} records</span>
      </div>
      {loading?<div style={{textAlign:'center',padding:20,color:'var(--text3)'}}>Loading...</div>:filtered.length===0?(
        <div style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No call logs yet for {batch}</div>
      ):(
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'var(--accent-light)',borderBottom:'2px solid var(--border)'}}>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Absent Date</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Student</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Package</th>
              <th style={{padding:'8px 10px',textAlign:'center'}}>Status</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Called By</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Call Date</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Reason</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Remarks</th>
              <th style={{padding:'8px 10px',textAlign:'center'}}>Actions</th>
            </tr></thead>
            <tbody>
            {filtered.map((l,i)=>editingId===l.id?(
              <tr key={l.id} style={{borderBottom:'1px solid var(--border)',background:'var(--accent-light)'}}>
                <td style={{padding:'6px 10px',fontSize:11}}>{dmy(l.absent_date)}</td>
                <td style={{padding:'6px 10px'}}><div style={{fontWeight:600}}>{l.students?.full_name||'—'}</div><div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{l.students?.register_number}</div></td>
                <td style={{padding:'6px 10px',fontSize:11}}>{l.students?.course_name||'—'}</td>
                <td style={{padding:'4px 6px'}}><select value={editForm.call_status||''} onChange={e=>setEditForm(f=>({...f,call_status:e.target.value}))} style={{fontSize:11,padding:'3px 6px',maxWidth:150}}>{STATUSES.map(s=><option key={s} value={s}>{s}</option>)}</select></td>
                <td style={{padding:'4px 6px'}}><select value={editForm.called_by||''} onChange={e=>setEditForm(f=>({...f,called_by:e.target.value}))} style={{fontSize:11,padding:'3px 6px',maxWidth:140}}><option value="">Select</option>{callLogStaff.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}</select></td>
                <td style={{padding:'4px 6px'}}><DateInput value={editForm.call_date||''} onChange={e=>setEditForm(f=>({...f,call_date:e.target.value}))} style={{fontSize:11,padding:'3px 6px',maxWidth:120}}/></td>
                <td style={{padding:'4px 6px'}}><input value={editForm.reason||''} onChange={e=>setEditForm(f=>({...f,reason:e.target.value}))} style={{fontSize:11,padding:'3px 6px',width:'100%'}}/></td>
                <td style={{padding:'4px 6px'}}><input value={editForm.remarks||''} onChange={e=>setEditForm(f=>({...f,remarks:e.target.value}))} style={{fontSize:11,padding:'3px 6px',width:'100%'}}/></td>
                <td style={{padding:'6px 10px',textAlign:'center',whiteSpace:'nowrap'}}>
                  <button className="btn btn-primary btn-sm" style={{fontSize:10,marginRight:4}} disabled={saving} onClick={()=>saveEdit(l.id)}>{saving?'...':'✓'}</button>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>setEditingId(null)}>✕</button>
                </td>
              </tr>
            ):(
              <tr key={l.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{dmy(l.absent_date)}</td>
                <td style={{padding:'6px 10px'}}><div style={{fontWeight:600}}>{l.students?.full_name||'—'}</div><div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{l.students?.register_number}</div></td>
                <td style={{padding:'6px 10px',fontSize:11,color:'var(--text2)'}}>{l.students?.course_name||'—'}</td>
                <td style={{padding:'6px 10px',textAlign:'center'}}><span style={{fontSize:11,fontWeight:600,color:STATUS_COLORS[l.call_status]||'var(--text3)'}}>{STATUS_DOTS[l.call_status]||'⚪'} {l.call_status||'—'}</span></td>
                <td style={{padding:'6px 10px',fontSize:11}}>{getCallerName(l.called_by)}</td>
                <td style={{padding:'6px 10px',fontSize:11,fontFamily:'monospace'}}>{l.call_date?dmy(l.call_date):'—'}</td>
                <td style={{padding:'6px 10px',fontSize:11}}>{l.reason||'—'}</td>
                <td style={{padding:'6px 10px',fontSize:11,color:'var(--text3)'}}>{l.remarks||'—'}</td>
                <td style={{padding:'6px 10px',textAlign:'center',whiteSpace:'nowrap'}}>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:10,marginRight:4}} onClick={()=>{setEditingId(l.id);setEditForm({call_status:l.call_status,called_by:l.called_by,call_date:l.call_date,reason:l.reason,remarks:l.remarks});}}>✏️</button>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)'}} onClick={()=>deleteLog(l.id)}>🗑</button>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function StudentAbsenceDetail({ student, batch, topic, callHistory, setCallHistory, callLogStaff, supabase, trackerData, canEditLog = true }) {
  const [editId, setEditId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const STATUSES = ['Not Called','Called - No Answer','Called - Reason Given','Called - Will Come','Called - Personal Issue','Called - Health Issue','Called - Other'];
  const STATUS_COLOR = {'Not Called':'var(--red)','Called - No Answer':'var(--amber)','Called - Reason Given':'var(--green)','Called - Will Come':'var(--green)','Called - Personal Issue':'#8b5cf6','Called - Health Issue':'#8b5cf6','Called - Other':'var(--text3)'};

  // Get all absent dates for this student from trackerData
  const absentDates = (trackerData?.dates||[]).filter(d=>
    !trackerData.grid[student.id]?.[d] || trackerData.grid[student.id][d].present===0
  );

  async function saveEdit(id) {
    if (!canEditLog) { toast.error(NOT_OWN); return; }
    setSaving(true);
    const {error} = await supabase.from('attendance_absence_calls').update(editForm).eq('id',id);
    if(error) toast.error(error.message);
    else {
      toast.success('Updated');
      setEditId(null);
      const {data} = await supabase.from('attendance_absence_calls')
        .select('*').eq('student_id',student.id).eq('batch_name',batch).order('absent_date',{ascending:false});
      setCallHistory(data||[]);
    }
    setSaving(false);
  }

  async function deleteLog(id) {
    if (!canEditLog) { toast.error(NOT_OWN); return; }
    if(!window.confirm('Delete this call log?')) return;
    const {error} = await supabase.from('attendance_absence_calls').delete().eq('id',id);
    if(error) toast.error(error.message);
    else {
      toast.success('Deleted');
      setCallHistory(prev => prev.filter(l=>l.id!==id));
    }
  }

  return (
    <div style={{padding:'12px 16px'}}>
      <div style={{fontWeight:600,fontSize:13,marginBottom:4}}>
        📊 {student.full_name} <span style={{fontFamily:'monospace',fontWeight:400,fontSize:11,color:'var(--text3)'}}>{student.register_number}</span>
      </div>
      <div style={{fontSize:11,color:'var(--text3)',marginBottom:12}}>
        Topic: <strong>{topic}</strong> | Absent {absentDates.length} day(s): {absentDates.map(d=>dmy(d)).join(', ')}
      </div>
      {callHistory.length===0?(
        <div style={{color:'var(--text3)',fontSize:12,padding:'8px 0'}}>No call logs recorded yet. Log calls by clicking a date column above.</div>
      ):(
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#e8f0fe'}}>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Absent Date</th>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Topic</th>
            <th style={{padding:'5px 8px',textAlign:'center'}}>Status</th>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Called By</th>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Call Date</th>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Reason</th>
            <th style={{padding:'5px 8px',textAlign:'left'}}>Remarks</th>
            <th style={{padding:'5px 8px',textAlign:'center'}}>Actions</th>
          </tr></thead>
          <tbody>
          {callHistory.map((l,i)=>editId===l.id?(
            <tr key={l.id} style={{borderBottom:'1px solid var(--border)',background:'#fffbeb'}}>
              <td style={{padding:'4px 8px',fontSize:11}}>{dmy(l.absent_date)}</td>
              <td style={{padding:'4px 8px',fontSize:11,color:'var(--text3)'}}>{l.topic_name||'—'}</td>
              <td style={{padding:'4px 6px'}}><select value={editForm.call_status||''} onChange={e=>setEditForm(f=>({...f,call_status:e.target.value}))} style={{fontSize:11,padding:'2px 4px',maxWidth:150}}>{STATUSES.map(s=><option key={s} value={s}>{s}</option>)}</select></td>
              <td style={{padding:'4px 6px'}}><select value={editForm.called_by||''} onChange={e=>setEditForm(f=>({...f,called_by:e.target.value}))} style={{fontSize:11,padding:'2px 4px',maxWidth:130}}><option value="">Select</option>{callLogStaff.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}</select></td>
              <td style={{padding:'4px 6px'}}><DateInput value={editForm.call_date||''} onChange={e=>setEditForm(f=>({...f,call_date:e.target.value}))} style={{fontSize:11,padding:'2px 4px',maxWidth:110}}/></td>
              <td style={{padding:'4px 6px'}}><input value={editForm.reason||''} onChange={e=>setEditForm(f=>({...f,reason:e.target.value}))} style={{fontSize:11,padding:'2px 4px',width:'100%'}}/></td>
              <td style={{padding:'4px 6px'}}><input value={editForm.remarks||''} onChange={e=>setEditForm(f=>({...f,remarks:e.target.value}))} style={{fontSize:11,padding:'2px 4px',width:'100%'}}/></td>
              <td style={{padding:'4px 8px',textAlign:'center',whiteSpace:'nowrap'}}>
                <button className="btn btn-primary btn-sm" style={{fontSize:10,marginRight:4}} disabled={saving} onClick={()=>saveEdit(l.id)}>{saving?'...':'✓'}</button>
                <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>setEditId(null)}>✕</button>
              </td>
            </tr>
          ):(
            <tr key={l.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'#fff':'#f9fafb'}}>
              <td style={{padding:'4px 8px',fontFamily:'monospace',fontSize:11}}>{dmy(l.absent_date)}</td>
              <td style={{padding:'4px 8px',fontSize:11,color:'var(--text3)'}}>{l.topic_name||'—'}</td>
              <td style={{padding:'4px 8px',textAlign:'center',fontSize:11,fontWeight:600,color:STATUS_COLOR[l.call_status]||'var(--text3)'}}>{l.call_status||'—'}</td>
              <td style={{padding:'4px 8px',fontSize:11}}>{callLogStaff.find(s=>s.id===l.called_by)?.staff_name||'—'}</td>
              <td style={{padding:'4px 8px',fontSize:11,fontFamily:'monospace'}}>{l.call_date?dmy(l.call_date):'—'}</td>
              <td style={{padding:'4px 8px',fontSize:11}}>{l.reason||'—'}</td>
              <td style={{padding:'4px 8px',fontSize:11,color:'var(--text3)'}}>{l.remarks||'—'}</td>
              <td style={{padding:'4px 8px',textAlign:'center',whiteSpace:'nowrap'}}>
                {canEditLog && <button className="btn btn-ghost btn-sm" style={{fontSize:10,marginRight:4}} onClick={()=>{setEditId(l.id);setEditForm({call_status:l.call_status,called_by:l.called_by,call_date:l.call_date,reason:l.reason,remarks:l.remarks});}}>✏️</button>}
                {canEditLog && <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)'}} onClick={()=>deleteLog(l.id)}>🗑</button>}
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BatchAbsenceSummary({ supabase, batch, callLogStaff, canEditLog = true }) {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fStatus, setFStatus] = React.useState('');
  const [fTopic, setFTopic] = React.useState('');
  const [editId, setEditId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [localStaff, setLocalStaff] = React.useState([]);

  React.useEffect(() => { if(batch) load(); }, [batch]);

  async function load() {
    setLoading(true);
    const [{ data: logs }, { data: staff }] = await Promise.all([
      supabase.from('attendance_absence_calls')
        .select('*,students(full_name,register_number,course_name)')
        .eq('batch_name', batch)
        .order('absent_date', {ascending:false}),
      supabase.from('hr_staff').select('id,staff_name').eq('is_active',true)
        .in('designation',['Academic Counselor','Office Admin'])
    ]);
    setData(logs||[]);
    setLocalStaff(staff||[]);
    setLoading(false);
  }

  async function saveEdit(id) {
    if (!canEditLog) { toast.error(NOT_OWN); return; }
    setSaving(true);
    const {error} = await supabase.from('attendance_absence_calls').update(editForm).eq('id',id);
    if(error) toast.error(error.message);
    else { toast.success('Updated'); setEditId(null); load(); }
    setSaving(false);
  }

  async function deleteLog(id) {
    if (!canEditLog) { toast.error(NOT_OWN); return; }
    if(!window.confirm('Delete this call log?')) return;
    const {error} = await supabase.from('attendance_absence_calls').delete().eq('id',id);
    if(error) toast.error(error.message);
    else { toast.success('Deleted'); setData(prev=>prev.filter(l=>l.id!==id)); }
  }

  const STATUSES = ['Not Called','Called - No Answer','Called - Reason Given','Called - Will Come','Called - Personal Issue','Called - Health Issue','Called - Other'];
  const STATUS_DOTS = {'Not Called':'🔴','Called - No Answer':'🟡','Called - Reason Given':'🟢','Called - Will Come':'🟢','Called - Personal Issue':'🟣','Called - Health Issue':'🟣','Called - Other':'⚪'};
  const STATUS_COLOR = {'Not Called':'var(--red)','Called - No Answer':'var(--amber)','Called - Reason Given':'var(--green)','Called - Will Come':'var(--green)','Called - Personal Issue':'#8b5cf6','Called - Health Issue':'#8b5cf6','Called - Other':'var(--text3)'};

  const staff = localStaff.length ? localStaff : callLogStaff;
  const topics = [...new Set(data.map(l=>l.topic_name).filter(Boolean))].sort();
  const filtered = data.filter(l=>(!fStatus||l.call_status===fStatus)&&(!fTopic||l.topic_name===fTopic));
  const studentCount = new Set(filtered.map(l=>l.student_id)).size;
  const getCallerName = id => staff.find(s=>s.id===id)?.staff_name||'—';

  return (
    <div className="card" style={{marginTop:16}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12,flexWrap:'wrap'}}>
        <span style={{fontWeight:600,fontSize:14}}>📊 Batch Absence & Call Summary — {batch}</span>
        <select value={fTopic} onChange={e=>setFTopic(e.target.value)} style={{fontSize:12,padding:'3px 8px',maxWidth:200}}>
          <option value="">All Topics</option>
          {topics.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{fontSize:12,padding:'3px 8px',maxWidth:170}}>
          <option value="">All Statuses</option>
          {STATUSES.map(s=><option key={s} value={s}>{STATUS_DOTS[s]} {s}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load} style={{marginLeft:'auto'}}>🔄 Refresh</button>
        <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} records | {studentCount} students</span>
      </div>
      {loading?<div style={{textAlign:'center',padding:20,color:'var(--text3)'}}>Loading...</div>:filtered.length===0?(
        <div style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No absence call logs yet for {batch}</div>
      ):(
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'var(--accent-light)',borderBottom:'2px solid var(--border)'}}>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Student</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Package</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Topic</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Absent Date</th>
              <th style={{padding:'8px 10px',textAlign:'center'}}>Status</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Called By</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Call Date</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Reason</th>
              <th style={{padding:'8px 10px',textAlign:'left'}}>Remarks</th>
              <th style={{padding:'8px 10px',textAlign:'center'}}>Actions</th>
            </tr></thead>
            <tbody>
            {filtered.map((l,i)=>editId===l.id?(
              <tr key={l.id} style={{borderBottom:'1px solid var(--border)',background:'#fffbeb'}}>
                <td style={{padding:'6px 10px'}}><div style={{fontWeight:600}}>{l.students?.full_name||'—'}</div><div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{l.students?.register_number}</div></td>
                <td style={{padding:'6px 10px',fontSize:11}}>{l.students?.course_name||'—'}</td>
                <td style={{padding:'6px 10px',fontSize:11,color:'var(--accent)'}}>{l.topic_name||'—'}</td>
                <td style={{padding:'6px 10px',fontSize:11,fontFamily:'monospace'}}>{dmy(l.absent_date)}</td>
                <td style={{padding:'4px 6px'}}><select value={editForm.call_status||''} onChange={e=>setEditForm(f=>({...f,call_status:e.target.value}))} style={{fontSize:11,padding:'2px 4px',maxWidth:150}}>{STATUSES.map(s=><option key={s} value={s}>{s}</option>)}</select></td>
                <td style={{padding:'4px 6px'}}><select value={editForm.called_by||''} onChange={e=>setEditForm(f=>({...f,called_by:e.target.value}))} style={{fontSize:11,padding:'2px 4px',maxWidth:140}}><option value="">Select</option>{staff.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}</select></td>
                <td style={{padding:'4px 6px'}}><DateInput value={editForm.call_date||''} onChange={e=>setEditForm(f=>({...f,call_date:e.target.value}))} style={{fontSize:11,padding:'2px 4px',maxWidth:110}}/></td>
                <td style={{padding:'4px 6px'}}><input value={editForm.reason||''} onChange={e=>setEditForm(f=>({...f,reason:e.target.value}))} style={{fontSize:11,padding:'2px 4px',width:'100%'}}/></td>
                <td style={{padding:'4px 6px'}}><input value={editForm.remarks||''} onChange={e=>setEditForm(f=>({...f,remarks:e.target.value}))} style={{fontSize:11,padding:'2px 4px',width:'100%'}}/></td>
                <td style={{padding:'6px 10px',textAlign:'center',whiteSpace:'nowrap'}}>
                  <button className="btn btn-primary btn-sm" style={{fontSize:10,marginRight:4}} disabled={saving} onClick={()=>saveEdit(l.id)}>{saving?'...':'✓'}</button>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>setEditId(null)}>✕</button>
                </td>
              </tr>
            ):(
              <tr key={l.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                <td style={{padding:'6px 10px'}}><div style={{fontWeight:600}}>{l.students?.full_name||'—'}</div><div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{l.students?.register_number}</div></td>
                <td style={{padding:'6px 10px',fontSize:11}}>{l.students?.course_name||'—'}</td>
                <td style={{padding:'6px 10px',fontSize:11,color:'var(--accent)'}}>{l.topic_name||'—'}</td>
                <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{dmy(l.absent_date)}</td>
                <td style={{padding:'6px 10px',textAlign:'center'}}><span style={{fontSize:11,fontWeight:600,color:STATUS_COLOR[l.call_status]||'var(--text3)'}}>{STATUS_DOTS[l.call_status]||'⚪'} {l.call_status||'—'}</span></td>
                <td style={{padding:'6px 10px',fontSize:11}}>{getCallerName(l.called_by)}</td>
                <td style={{padding:'6px 10px',fontSize:11,fontFamily:'monospace'}}>{l.call_date?dmy(l.call_date):'—'}</td>
                <td style={{padding:'6px 10px',fontSize:11}}>{l.reason||'—'}</td>
                <td style={{padding:'6px 10px',fontSize:11,color:'var(--text3)'}}>{l.remarks||'—'}</td>
                <td style={{padding:'6px 10px',textAlign:'center',whiteSpace:'nowrap'}}>
                  {canEditLog && <button className="btn btn-ghost btn-sm" style={{fontSize:10,marginRight:4}} onClick={()=>{setEditId(l.id);setEditForm({call_status:l.call_status,called_by:l.called_by,call_date:l.call_date,reason:l.reason,remarks:l.remarks});}}>✏️</button>}
                  {canEditLog && <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)'}} onClick={()=>deleteLog(l.id)}>🗑</button>}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function DailySessionLog({ supabase, batches, dailyLog, setDailyLog,
  dailyLogBatch, setDailyLogBatch, dailyLogFrom, setDailyLogFrom,
  dailyLogTo, setDailyLogTo, dailyLogLoading, setDailyLogLoading }) {

  const [expanded, setExpanded] = React.useState(null); // date+topic key
  const [sessionStudents, setSessionStudents] = React.useState({});

  async function loadLog() {
    if (!dailyLogBatch) { toast.error('Select a batch'); return; }
    setDailyLogLoading(true);
    setExpanded(null);
    setSessionStudents({});
    let { data: stds } = await supabase.from('students')
      .select('id,full_name,register_number,joining_date,student_status,course_end_date').eq('batch_name', dailyLogBatch).eq('is_active', true)
      .not('student_status','in','("Discontinued","Shifted")')
      .order('register_number');
    if (stds) stds = stds.filter(s => s.student_status !== 'Passed Out' || s.course_end_date);
    if (!stds?.length) { setDailyLogLoading(false); return; }
    const sIds = stds.map(s => s.id);
    let q = supabase.from('attendance')
      .select('student_id,date,topic_name,faculty_name,num_sessions,session_type,session1,session2,session3,session4,session5,session6,session7,session8,day_value')
      .in('student_id', sIds)
      .not('session_type', 'in', '("holiday","no_class")')
      .not('topic_name', 'is', null)
      .order('date', {ascending: false});
    if (dailyLogFrom) q = q.gte('date', dailyLogFrom);
    if (dailyLogTo) q = q.lte('date', dailyLogTo);
    const { data: att } = await q;
    const map = {};
    (att||[]).forEach(a => {
      const key = a.date + '||' + a.topic_name;
      if (!map[key]) map[key] = { date:a.date, topic:a.topic_name, faculty:a.faculty_name, num_sessions:a.num_sessions, records:[], stds };
      map[key].records.push(a);
    });
    const rows = Object.values(map).sort((a,b)=>b.date.localeCompare(a.date)||a.topic.localeCompare(b.topic));
    const byDate = {};
    rows.forEach(r => { if(!byDate[r.date]) byDate[r.date]=[]; byDate[r.date].push(r); });
    setDailyLog(Object.entries(byDate).map(([date,sessions])=>({date,sessions})));
    setDailyLogLoading(false);
  }

  async function toggleExpand(key, session) {
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (sessionStudents[key]) return; // already loaded
    // Build student attendance for this session
    const sessionDate = session.date;
    const ALL_SESS = ['session1','session2','session3','session4','session5','session6','session7','session8'];
    const studentList = session.stds.filter(s => !s.course_end_date || s.course_end_date >= sessionDate).map(s => {
      const rec = session.records.find(r => r.student_id === s.id);
      let present = 0;
      if (rec) { const n = rec.num_sessions||4; ALL_SESS.slice(0,n).forEach(k=>{ if(rec[k]==='present') present++; }); }
      return { ...s, joining_date: s.joining_date||null, status: rec ? (present>0?'present':'absent') : (!s.joining_date||s.joining_date<=sessionDate?'not_marked':'not_joined'), present, total: rec?.num_sessions||0 };
    });
    setSessionStudents(prev => ({...prev, [key]: studentList}));
  }

  const SESS_KEYS = ['session1','session2','session3','session4','session5','session6','session7','session8'];

  return (
    <div>
      <div className="card" style={{marginBottom:12,display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <SearchableSelect value={dailyLogBatch} onChange={v=>setDailyLogBatch(v||'')}
          options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
          placeholder="Select Batch" searchPlaceholder="Search batch..." style={{minWidth:180}}/>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          <label style={{fontSize:12}}>From:</label>
          <DateInput value={dailyLogFrom} onChange={e=>setDailyLogFrom(e.target.value)}
            style={{padding:'4px 8px',borderRadius:6,border:'1px solid var(--border)',fontSize:12}}/>
        </div>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          <label style={{fontSize:12}}>To:</label>
          <DateInput value={dailyLogTo} onChange={e=>setDailyLogTo(e.target.value)}
            style={{padding:'4px 8px',borderRadius:6,border:'1px solid var(--border)',fontSize:12}}/>
        </div>
        <button className="btn btn-primary btn-sm" onClick={loadLog} disabled={!dailyLogBatch||dailyLogLoading}>
          {dailyLogLoading?'Loading...':'📅 Load Log'}
        </button>
      </div>

      {dailyLog.length > 0 && (
        <div className="card" style={{padding:0}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{background:'var(--accent-light)',borderBottom:'2px solid var(--border)'}}>
                <th style={{padding:'8px 12px',textAlign:'left',width:120}}>Date</th>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Topic</th>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Faculty</th>
                <th style={{padding:'8px 12px',textAlign:'center',width:80}}>Sessions</th>
                <th style={{padding:'8px 12px',textAlign:'center',width:60}}></th>
              </tr>
            </thead>
            <tbody>
            {dailyLog.map((day, di) => (
              <React.Fragment key={day.date}>
              {day.sessions.map((s, si) => {
                const key = day.date + '||' + s.topic;
                const isOpen = expanded === key;
                const stuList = sessionStudents[key];
                return (
                  <React.Fragment key={key}>
                  <tr style={{
                    borderBottom: isOpen?'none':(si===day.sessions.length-1?'2px solid var(--border)':'1px solid var(--border)'),
                    background: di%2===0?'':'var(--surface2)',
                    cursor:'pointer'
                  }} onClick={()=>toggleExpand(key, s)}>
                    {si===0 && (
                      <td rowSpan={day.sessions.length} style={{padding:'10px 12px',fontWeight:700,verticalAlign:'middle',borderRight:'1px solid var(--border)',borderBottom:si===day.sessions.length-1?'2px solid var(--border)':'1px solid var(--border)'}}>
                        {dmy(day.date)}
                        {day.sessions.length>1&&<div style={{fontSize:10,color:'var(--green)',marginTop:2}}>✅ {day.sessions.length} sessions</div>}
                      </td>
                    )}
                    <td style={{padding:'10px 12px',color:'var(--accent)',fontWeight:500}}>{s.topic}</td>
                    <td style={{padding:'10px 12px',color:'var(--text2)'}}>{s.faculty||'—'}</td>
                    <td style={{padding:'10px 12px',textAlign:'center'}}><span className="badge badge-blue">{s.num_sessions||'—'}</span></td>
                    <td style={{padding:'10px 12px',textAlign:'center',color:'var(--text3)',fontSize:12}}>{isOpen?'▲':'▼'}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} style={{padding:0,borderBottom:'2px solid var(--border)'}}>
                        <div style={{padding:'8px 16px',background:'var(--accent-light)'}}>
                          <div style={{fontWeight:600,fontSize:12,marginBottom:8,color:'var(--accent)'}}>
                            👥 Student Attendance — {s.topic} on {dmy(day.date)}
                          </div>
                          {!stuList ? <div style={{fontSize:12,color:'var(--text3)'}}>Loading...</div> : (
                            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                              {stuList.map(st=>(
                                <span key={st.id} style={{
                                  fontSize:11,padding:'3px 8px',borderRadius:4,
                                  background:st.status==='present'?'var(--green-light)':st.status==='absent'?'rgba(239,68,68,0.1)':st.status==='not_joined'?'rgba(245,158,11,0.1)':'var(--surface2)',
                                  color:st.status==='present'?'var(--green)':st.status==='absent'?'var(--red)':st.status==='not_joined'?'var(--amber)':'var(--text3)',
                                  border:`1px solid ${st.status==='present'?'var(--green)':st.status==='absent'?'var(--red)':st.status==='not_joined'?'var(--amber)':'var(--border)'}`
                                }}>
                                  {st.register_number} {st.full_name}
                                  {st.status==='present'&&` ✓${st.present}/${st.total}`}
                                  {st.status==='absent'&&' ✗'}
                                  {st.status==='not_marked'&&' —'}
                  {st.status==='not_joined'&&' 🆕'}
                                </span>
                              ))}
                            </div>
                          )}
                          <div style={{marginTop:6,fontSize:11,color:'var(--text3)'}}>
                            {stuList&&`✅ ${stuList.filter(s=>s.status==='present').length} present | ❌ ${stuList.filter(s=>s.status==='absent').length} absent | — ${stuList.filter(s=>s.status==='not_marked').length} not marked | 🆕 ${stuList.filter(s=>s.status==='not_joined').length} not yet joined`}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              </React.Fragment>
            ))}
            </tbody>
          </table>
          <div style={{padding:'8px 16px',fontSize:12,color:'var(--text3)',borderTop:'1px solid var(--border)'}}>
            {dailyLog.length} day(s) | {dailyLog.reduce((s,d)=>s+d.sessions.length,0)} total sessions | Click a row to see student details
          </div>
        </div>
      )}

      {dailyLog.length===0&&dailyLogBatch&&!dailyLogLoading&&(
        <div className="card" style={{textAlign:'center',color:'var(--text3)',padding:40}}>
          No attendance records found. Click Load Log after selecting batch.
        </div>
      )}
    </div>
  );
}


export default function Attendance() {
  const { isEditor, canEdit, canView, user } = useAuth();
  const { batchRestricted, ownBatchId } = useBatchLimit();

  const [batches, setBatches]             = useState([]);
  const ownBatch = name => !batchRestricted || batches.some(b => b.batch_name === name && ownBatchId(b.id));
  const batchStatusRef = React.useRef({});
  const [topics, setTopics]               = useState([]);
  const [students, setStudents]           = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [numSessions, setNumSessions]     = useState(4);
  const [date, setDate]                   = useState(today());
  const [attSessionType, setAttSessionType] = useState('regular');
  const [attOriginalDate, setAttOriginalDate] = useState('');
  const [attRepeatModal, setAttRepeatModal] = useState(null);
  const [trackerBatch, setTrackerBatch] = useState('');
  const [trackerTopic, setTrackerTopic] = useState('');
  const [trackerData, setTrackerData] = useState(null); // {dates:[], students:[], grid:{studentId:{date:status}}}
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerSelDate, setTrackerSelDate] = useState(null);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [showBatchSummary, setShowBatchSummary] = useState(false);
  const [studentCallHistory, setStudentCallHistory] = useState([]);
  const absentTrackerRef = React.useRef(null);
  const [otherSessions, setOtherSessions] = React.useState([]);
  const [callLogs, setCallLogs] = useState({});
  const [callLogStaff, setCallLogStaff] = useState([]);
  const [callLogSaving, setCallLogSaving] = useState({});
  const [allTopicNames, setAllTopicNames] = useState([]);
  const [trackerTopicList, setTrackerTopicList] = useState(null);   // [{name,label,status}] topics that have classes for the selected batch (null = loading)
  const [attRepeatDate, setAttRepeatDate] = useState('');
  const [attRepeatSession, setAttRepeatSession] = useState(null); // {date, originalDate, studentIds}
  const [topicName, setTopicName]         = useState('');

  // Leave periods
  const [leavePeriods, setLeavePeriods] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ student_id:'', start_date:'', end_date:'', reason:'', notes:'', approved_by:'' });
  const [leaveSaving, setLeaveSaving] = useState(false);
  const LEAVE_REASONS = ['Illness','Accident','Exam','Personal','Family Emergency','Other'];

  function isOnLeave(studentId, date) {
    return leavePeriods.some(lp => lp.student_id === studentId &&
      lp.status === 'active' &&
      lp.start_date <= date &&
      (lp.actual_return_date ? lp.actual_return_date > date : lp.end_date >= date)
    );
  }

  function getLeave(studentId, date) {
    return leavePeriods.find(lp => lp.student_id === studentId &&
      lp.status === 'active' &&
      lp.start_date <= date &&
      (lp.actual_return_date ? lp.actual_return_date > date : lp.end_date >= date)
    );
  }

  async function loadLeavePeriods(batchName) {
    if (!batchName) return;
    // Load staff if not already loaded
    if (!callLogStaff.length) {
      const {data: staff} = await supabase.from('hr_staff').select('id,staff_name').eq('is_active',true).in('designation',['Academic Counselor','Office Admin']);
      setCallLogStaff(staff||[]);
    }
    // Get student IDs for this batch
    const {data: stds} = await supabase.from('students').select('id').eq('batch_name', batchName);
    if (!stds?.length) return;
    const ids = stds.map(s => s.id);
    const {data} = await supabase.from('student_leave_periods').select('*').in('student_id', ids).eq('status','active');
    setLeavePeriods(data||[]);
  }

  async function saveLeave() {
    if (!canTrk) { toast.error(NOT_OWN); return; }
    if (!leaveForm.student_id || !leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason) {
      toast.error('Student, dates and reason are required'); return;
    }
    if (leaveForm.end_date < leaveForm.start_date) {
      toast.error('End date must be after start date'); return;
    }
    setLeaveSaving(true);
    const {data: lpData, error} = await supabase.from('student_leave_periods')
      .insert({...leaveForm, status:'active'}).select().single();
    if (error) { toast.error(error.message); setLeaveSaving(false); return; }

    // Auto-fill attendance as absent for past/present days in the range
    const today_ = new Date().toISOString().split('T')[0];
    const fillEnd = leaveForm.end_date < today_ ? leaveForm.end_date : today_;

    if (leaveForm.start_date <= fillEnd && trackerBatch) {
      // Get all dates in attendance for this batch in range
      const {data: attDates} = await supabase.from('attendance')
        .select('date, student_id, num_sessions')
        .eq('student_id', leaveForm.student_id)
        .gte('date', leaveForm.start_date)
        .lte('date', fillEnd);

      if (attDates?.length) {
        const sessionKeys = ['session1','session2','session3','session4','session5','session6','session7','session8'];
        for (const att of attDates) {
          const n = att.num_sessions || 4;
          const absentSessions = {};
          sessionKeys.slice(0, n).forEach(k => { absentSessions[k] = 'absent'; });
          // day_value kept as-is so leave days count as absent in attendance %
          await supabase.from('attendance').update({ ...absentSessions })
            .eq('student_id', leaveForm.student_id).eq('date', att.date);
        }
      }
    }

    toast.success('Leave period saved & attendance marked absent ✅');
    setShowLeaveModal(false);
    setLeaveForm({ student_id:'', start_date:'', end_date:'', reason:'', notes:'', approved_by:'' });
    await loadLeavePeriods(trackerBatch);
    if (trackerData) await loadTrackerData();
    setLeaveSaving(false);
  }

  async function markLeaveReturned(lpId, studentId) {
    if (!canTrk) { toast.error(NOT_OWN); return; }
    const returnDate = window.prompt('Enter actual return date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!returnDate) return;
    const {error} = await supabase.from('student_leave_periods')
      .update({ actual_return_date: returnDate, status: 'completed' }).eq('id', lpId);
    if (error) { toast.error(error.message); return; }
    toast.success('Leave marked as completed ✅');
    await loadLeavePeriods(trackerBatch);
    if (trackerData) await loadTrackerData();
  }
  // Tracker batch students (loaded on batch select, independent of topic)
  const [trackerBatchStudents, setTrackerBatchStudents] = useState([]);

  // Online periods
  const [onlinePeriods, setOnlinePeriods] = useState([]);
  const [showOnlineModal, setShowOnlineModal] = useState(false);
  const [onlineForm, setOnlineForm] = useState({ student_id:'', start_date:'', end_date:'', notes:'' });
  const [onlineSaving, setOnlineSaving] = useState(false);
  // Online sessions tracker
  const [showOnlineTracker, setShowOnlineTracker] = useState(false);
  const [onlineSessions, setOnlineSessions] = useState([]);
  const [onlineTrackerBatch, setOnlineTrackerBatch] = useState('');
  const [onlineTrackerStudents, setOnlineTrackerStudents] = useState([]);
  const [onlineTrackerTopic, setOnlineTrackerTopic] = useState('');
  const [onlineTopicSessions, setOnlineTopicSessions] = useState(9);
  const [onlineSessionsLoading, setOnlineSessionsLoading] = useState(false);
  const [savingOnlineSession, setSavingOnlineSession] = useState({});
  const [onlineStudentSummary, setOnlineStudentSummary] = useState(null);
  const [onlineStudentSummaryLoading, setOnlineStudentSummaryLoading] = useState(false);
  const [onlineStudentDashboard, setOnlineStudentDashboard] = useState(null); // {student, sessions, attPct, topicStats}
  const [onlineStudentDashboardLoading, setOnlineStudentDashboardLoading] = useState(false);
  const [allOnlineSessions, setAllOnlineSessions] = useState([]); // all sessions for all students (no topic filter)

  function isOnline(studentId, date) {
    return onlinePeriods.some(op => op.student_id === studentId &&
      op.status === 'active' &&
      op.start_date <= date &&
      (op.actual_return_date ? op.actual_return_date > date : (op.end_date ? op.end_date >= date : true))
    );
  }
  function getOnlinePeriod(studentId, date) {
    return onlinePeriods.find(op => op.student_id === studentId &&
      op.status === 'active' &&
      op.start_date <= date &&
      (op.actual_return_date ? op.actual_return_date > date : (op.end_date ? op.end_date >= date : true))
    );
  }
  async function loadOnlinePeriods(batchName) {
    if (!batchName) return;
    const {data: stds} = await supabase.from('students').select('id').eq('batch_name', batchName);
    if (!stds?.length) { setOnlinePeriods([]); return; }
    const ids = stds.map(s => s.id);
    const {data} = await supabase.from('student_online_periods').select('*').in('student_id', ids).eq('status','active');
    setOnlinePeriods(data||[]);
  }
  async function saveOnlinePeriod() {
    if (!canTrk) { toast.error(NOT_OWN); return; }
    if (!onlineForm.student_id || !onlineForm.start_date) {
      toast.error('Student and start date required'); return;
    }
    setOnlineSaving(true);
    const insertData = { student_id: onlineForm.student_id, start_date: onlineForm.start_date, status: 'active' };
    if (onlineForm.end_date) insertData.end_date = onlineForm.end_date;
    if (onlineForm.notes) insertData.notes = onlineForm.notes;
    const {error} = await supabase.from('student_online_periods').insert(insertData);
    if (error) { toast.error(error.message); setOnlineSaving(false); return; }
    toast.success('Student marked as Online ✅');
    setShowOnlineModal(false);
    setOnlineForm({ student_id:'', start_date:'', end_date:'', notes:'' });
    await loadOnlinePeriods(trackerBatch);
    setOnlineSaving(false);
  }
  async function markOnlineReturned(opId) {
    if (!canTrk) { toast.error(NOT_OWN); return; }
    const returnDate = window.prompt('Enter return date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!returnDate) return;
    const {error} = await supabase.from('student_online_periods')
      .update({ actual_return_date: returnDate, status:'completed', end_date: returnDate }).eq('id', opId);
    if (error) { toast.error(error.message); return; }
    toast.success('Student returned to offline ✅');
    await loadOnlinePeriods(trackerBatch);
  }
  async function loadOnlineTrackerData(batchOverride) {
    const batch = batchOverride || onlineTrackerBatch;
    if (!batch) return;
    setOnlineSessionsLoading(true);
    const {data: stds} = await supabase.from('students').select('id,full_name,register_number').eq('batch_name', batch).order('register_number',{ascending:true});
    if (!stds?.length) { setOnlineTrackerStudents([]); setAllOnlineSessions([]); setOnlineSessionsLoading(false); return; }
    // filter to students who have/had online period
    const {data: ops} = await supabase.from('student_online_periods').select('*').in('student_id', stds.map(s=>s.id));
    const onlineStudentIds = new Set((ops||[]).map(o=>o.student_id));
    const filteredStds = (stds||[]).filter(s=>onlineStudentIds.has(s.id));
    setOnlineTrackerStudents(filteredStds);
    if (filteredStds.length) {
      // Load ALL sessions for all online students — no topic filter
      const {data: sessions} = await supabase.from('online_sessions')
        .select('*').in('student_id', filteredStds.map(s=>s.id))
        .order('day_number', {ascending:true});
      setAllOnlineSessions(sessions||[]);
    } else {
      setAllOnlineSessions([]);
    }
    setOnlineSessionsLoading(false);
  }

  async function loadOnlineStudentDashboard(student) {
    setOnlineStudentDashboard({student, sessions:[], attPct:'0', topicStats:[], loading:true});
    // Load all online_sessions for this student
    const {data: sessions} = await supabase.from('online_sessions')
      .select('*').eq('student_id', student.id).order('topic_name').order('day_number',{ascending:true});
    // Load attendance stats
    const {data: att} = await supabase.from('attendance')
      .select('date,topic_name,num_sessions,session1,session2,session3,session4,session5,session6,session7,session8,day_value')
      .eq('student_id', student.id).not('session_type','in','("holiday","no_class")');
    let presentSess=0, totalSess=0;
    const topicAttMap = {};
    (att||[]).forEach(a => {
      if (!a.topic_name||a.topic_name==='Holiday'||a.topic_name==='No Class') return;
      const n = a.num_sessions||4;
      totalSess += n;
      if (!topicAttMap[a.topic_name]) topicAttMap[a.topic_name]={present:0,total:0};
      topicAttMap[a.topic_name].total += n;
      ALL_SESSIONS.slice(0,n).forEach(sess => {
        if (a[sess.key]==='present') { presentSess++; topicAttMap[a.topic_name].present++; }
      });
    });
    const attPct = totalSess>0?((presentSess/totalSess)*100).toFixed(1):'0';
    // Group online sessions by topic
    const sessionsByTopic = {};
    (sessions||[]).forEach(s => {
      if (!sessionsByTopic[s.topic_name]) sessionsByTopic[s.topic_name]=[];
      sessionsByTopic[s.topic_name].push(s);
    });
    // Build topic stats
    const topicStats = Object.entries(sessionsByTopic).map(([topic, sess]) => {
      const days = sess.filter(s=>s.day_number>0);
      const summary = sess.find(s=>s.day_number===0);
      const watched = days.filter(d=>d.status==='watched').length;
      const shared = days.filter(d=>d.status==='shared').length;
      const missed = days.filter(d=>d.status==='missed').length;
      const maxDay = days.length>0?Math.max(...days.map(d=>d.day_number)):0;
      const att = topicAttMap[topic];
      return { topic, days, summary, watched, shared, missed, maxDay, att };
    }).sort((a,b)=>a.topic.localeCompare(b.topic));
    // Load online periods for this student
    const {data: periods} = await supabase.from('student_online_periods').select('*').eq('student_id',student.id).order('start_date',{ascending:false});
    setOnlineStudentDashboard({student, sessions:sessions||[], attPct, presentSess, totalSess, topicStats, periods:periods||[], loading:false});
  }
  async function saveOnlineSessionStatus(studentId, dayNumber, status, topicOverride) {
    if (!ownBatch(onlineTrackerBatch)) { toast.error(NOT_OWN); return; }
    const topic = topicOverride || onlineTrackerTopic;
    const batchName = onlineTrackerBatch;
    const key = `${studentId}_${topic}_${dayNumber}`;
    setSavingOnlineSession(p=>({...p,[key]:true}));
    const existing = allOnlineSessions.find(s=>s.student_id===studentId&&s.day_number===dayNumber&&s.topic_name===topic);
    if (existing) {
      await supabase.from('online_sessions').update({status, updated_at: new Date().toISOString()}).eq('id',existing.id);
    } else {
      await supabase.from('online_sessions').insert({student_id:studentId, topic_name:topic, day_number:dayNumber, batch_name:batchName, status, shared_date: new Date().toISOString().split('T')[0]});
    }
    await loadOnlineTrackerData(batchName);
    setSavingOnlineSession(p=>({...p,[key]:false}));
  }

  async function loadOnlineStudentSummary(studentId) {
    if (!studentId) { setOnlineStudentSummary(null); return; }
    setOnlineStudentSummaryLoading(true);
    const {data: att} = await supabase.from('attendance')
      .select('date, topic_name, num_sessions, session1,session2,session3,session4,session5,session6,session7,session8, day_value')
      .eq('student_id', studentId)
      .not('session_type','in','("holiday","no_class")');
    if (!att?.length) { setOnlineStudentSummary({ pct:'0', presentSess:0, totalSess:0, topicStats:[], alreadyOnline:false }); setOnlineStudentSummaryLoading(false); return; }
    let presentSess=0, totalSess=0;
    att.forEach(a => {
      const n = a.num_sessions||4;
      totalSess += n;
      ALL_SESSIONS.slice(0,n).forEach(sess => { if (a[sess.key]==='present') presentSess++; });
    });
    const pct = totalSess>0 ? ((presentSess/totalSess)*100).toFixed(1) : '0';
    const topicMap = {};
    att.forEach(a => {
      if (!a.topic_name||a.topic_name==='Holiday'||a.topic_name==='No Class') return;
      if (!topicMap[a.topic_name]) topicMap[a.topic_name] = {present:0,total:0};
      const n = a.num_sessions||4;
      topicMap[a.topic_name].total += n;
      ALL_SESSIONS.slice(0,n).forEach(sess => { if (a[sess.key]==='present') topicMap[a.topic_name].present++; });
    });
    const topicStats = Object.entries(topicMap).map(([topic,v])=>({
      topic, present:v.present, total:v.total,
      pct: v.total>0?((v.present/v.total)*100).toFixed(1):'0'
    })).sort((a,b)=>a.topic.localeCompare(b.topic));
    // check if already online
    const alreadyOnline = onlinePeriods.some(op=>op.student_id===studentId&&op.status==='active');
    setOnlineStudentSummary({ pct, presentSess, totalSess, topicStats, alreadyOnline });
    setOnlineStudentSummaryLoading(false);
  }

  async function shareNextVideo(studentId, studentName, topicOverride, totalDaysOverride) {
    if (!ownBatch(onlineTrackerBatch)) { toast.error(NOT_OWN); return; }
    const topic = topicOverride || onlineTrackerTopic;
    const totalDays = totalDaysOverride || onlineTopicSessions;
    if (!topic) { toast.error('Select a topic first'); return; }
    // Use already-loaded allOnlineSessions if available
    const sessions = allOnlineSessions.length
      ? allOnlineSessions.filter(s=>s.student_id===studentId&&s.topic_name===topic)
      : await supabase.from('online_sessions').select('*').eq('student_id',studentId).eq('topic_name',topic).order('day_number',{ascending:true}).then(r=>r.data||[]);
    let nextDay = 1;
    for (let d=1; d<=totalDays; d++) {
      const s = sessions.find(s=>s.day_number===d);
      if (!s || s.status==='pending') { nextDay=d; break; }
      if (s.status==='shared') { toast(`Day ${d} already shared to ${studentName}`); return; }
      nextDay = d+1;
    }
    if (nextDay > totalDays) {
      const summSess = sessions.find(s=>s.day_number===0);
      if (!summSess || summSess.status==='pending') {
        await saveOnlineSessionStatus(studentId, 0, 'shared', topic);
        toast.success(`Summary video shared to ${studentName} ✅`);
      } else {
        toast(`All ${totalDays} days + summary already done for ${studentName}`);
      }
      return;
    }
    await saveOnlineSessionStatus(studentId, nextDay, 'shared', topic);
    toast.success(`Day ${nextDay} of "${topic}" shared to ${studentName} ✅`);
  }

  const [facultyName, setFacultyName]     = useState('');
  const [facultyList, setFacultyList]     = useState([]);
  const [attendance, setAttendance]       = useState({});
  const [search, setSearch]               = useState('');
  const [pkgFilter, setPkgFilter]           = useState('');
  const [saving, setSaving]               = useState(false);
  const [activeTab, setActiveTab]         = useState('mark');

  // Summary
  const [summaryBatch, setSummaryBatch]     = useState('');
  const attEdit = canEdit('attendance') || batchRestricted;   // an assigned batch grants edit on that batch even if the section is View
  const canMark = attEdit && ownBatch(selectedBatch);   // Mark tab
  const canTrk  = attEdit && ownBatch(trackerBatch);    // Absent-tracker tab
  const canSum  = attEdit && ownBatch(summaryBatch);    // Summary tab
  const [dailyLog, setDailyLog]             = useState([]);
  const [dailyLogBatch, setDailyLogBatch]   = useState('');
  const [dailyLogFrom, setDailyLogFrom]     = useState('');
  const [dailyLogTo, setDailyLogTo]         = useState('');
  const [dailyLogLoading, setDailyLogLoading] = useState(false);
  const [summaryData, setSummaryData]       = useState([]);
  const [summarySearch, setSummarySearch]   = useState('');
  const [summaryFilter, setSummaryFilter]   = useState('all');
  const batchDiscipline = selectedBatch
    ? (selectedBatch.startsWith('EM')||selectedBatch.startsWith('TM')) ? 'mechanical'
    : (selectedBatch.startsWith('EC')||selectedBatch.startsWith('TC')) ? 'civil'
    : 'all' : 'all';
  const filteredTopics = topics.filter(t => !t.is_lab && (batchDiscipline==='all' || t.discipline===batchDiscipline || !t.discipline));
  const sortedSummaryData = [...summaryData]
    .filter(s => {
      const q = (summarySearch||'').toLowerCase();
      const matchSearch = !q || s.full_name?.toLowerCase().includes(q) || s.register_number?.toLowerCase().includes(q);
      const matchFilter = summaryFilter==='all' || (summaryFilter==='good'&&Number(s.pct)>=75) || (summaryFilter==='low'&&Number(s.pct)<75);
      return matchSearch && matchFilter;
    })
    .sort((a,b)=>(a.register_number||'').localeCompare(b.register_number||''));
  const [summaryTopics, setSummaryTopics]   = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFromDate, setSummaryFromDate] = useState('');
  const [summaryToDate, setSummaryToDate]   = useState('');
  const [summaryTopic, setSummaryTopic]     = useState('');
  const [topicFacultyMap, setTopicFacultyMap] = useState({});
  const [facultyForTopic, setFacultyForTopic] = useState('');
  const [repeatSession, setRepeatSession] = useState(null); // active repeat session for current date+batch+topic
  const [facultyReport, setFacultyReport] = useState([]);
  const [facultyHolidays, setFacultyHolidays] = useState([]);
  const [holidayRowDetail, setHolidayRowDetail] = useState(null); // {batch, label, dates[]}
  const [facultyReportBatch, setFacultyReportBatch] = useState('');
  const [facultyReportLoading, setFacultyReportLoading] = useState(false);
  const [facultyRowDetail, setFacultyRowDetail] = useState(null); // selected row for detail modal
  const [facultyRowData, setFacultyRowData] = useState([]); // date-wise data for selected row

  useEffect(() => { loadBaseData(); }, []);
  useEffect(() => {
    setPkgFilter('');
    if (selectedBatch) {
      loadStudents(selectedBatch);
      const pfx = selectedBatch.charAt(0).toUpperCase();
      if (pfx === 'E') setNumSessions(2);
      else if (pfx === 'T') setNumSessions(1);
    } else setStudents([]);
  }, [selectedBatch]);

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { loadBaseData(); } catch(e){} }, 800); };
    const ch = supabase.channel('attendance-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'attendance'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);
  useEffect(() => {
    if (selectedBatch && students.length > 0) loadAttendanceForDate();
  }, [date, students]);

  async function loadBaseData() {
    const [b, t, fac] = await Promise.all([
      supabase.from('batches').select('id, batch_name, batch_code, status').order('batch_name'),
      supabase.from('topics').select('id, topic_name, short_code, discipline, duration_days, is_lab').order('topic_name'),
      supabase.from('hr_staff').select('id, staff_name, designation').eq('is_active', true).in('designation',['Head-Training','MEP Engineer','NDT Inspection Engineer','Project Engineer','Project Engineer-Mechanical','Project Manager-Civil','Sr. Inspection Engineer','Senior project Engineer-Civil','Project Engineer-Civil','Sr. Project Engineer-Civil','External Faculty']).order('staff_name'),
    ]);
    const _bsm = {}; (b.data||[]).forEach(x => { _bsm[x.batch_name] = x.status || 'completed'; }); batchStatusRef.current = _bsm;
    setBatches(sortBatches(b.data || []));
    setTopics(t.data || []);
    setFacultyList((fac.data||[]).map(f=>f.staff_name).filter(Boolean));
  }

  async function loadStudents(batchId) {
    let allStds = [], pg = 0;
    while (true) {
      const { data, error } = await supabase
        .from('students')
        .select('id, full_name, register_number, batch_name, joining_date, course_name, student_status, course_end_date')
        .eq('batch_name', batchId)
        .neq('is_active', false)
        .not('student_status', 'in', '("Discontinued","Shifted")')
        .order('register_number')
        .range(pg * 1000, (pg + 1) * 1000 - 1);
      if (error || !data || data.length === 0) break;
      allStds = [...allStds, ...data];
      if (data.length < 1000) break;
      pg++;
    }
    // Passed Out students stay in the list (up to their completion date) only when a completion date is set
    allStds = allStds.filter(s => s.student_status !== 'Passed Out' || s.course_end_date);
    const statusOrder = { ongoing: 0, upcoming: 1, completed: 2 };
    const sorted = allStds.slice().sort((a, b) => {
      const sa = statusOrder[batchStatusRef.current[a.batch_name]] ?? 2;
      const sb = statusOrder[batchStatusRef.current[b.batch_name]] ?? 2;
      if (sa !== sb) return sa - sb;
      return (a.register_number||'').localeCompare(b.register_number||'', undefined, {numeric:true});
    });
    setStudents(sorted);
    setAttendance({});
  }

  async function loadRepeatSession(batch, dateStr, topic) {
    if (!batch || !dateStr || !topic) { setRepeatSession(null); return; }
    const bObj = batches.find(b => b.batch_name === batch);
    if (!bObj) { setRepeatSession(null); return; }
    const { data } = await supabase.from('repeat_sessions')
      .select('*').eq('batch_id', bObj.id).eq('session_date', dateStr).eq('topic_name', topic).maybeSingle();
    setRepeatSession(data || null);
  }

  // Filter students by joining_date for the selected date
  const studentsForDate = React.useMemo(() =>
    students.filter(s => (!s.joining_date || s.joining_date <= date) && (!s.course_end_date || s.course_end_date >= date)),
  [students, date]);

  async function loadAttendanceForDate(topicOverride) {
    if (studentsForDate.length === 0) return;
    // Auto-fill topic and faculty from existing attendance for this batch+date
    if (!topicName) {
      const { data: lastAtt } = await supabase.from('attendance')
        .select('topic_name, faculty_name')
        .in('student_id', studentsForDate.map(s=>s.id))
        .eq('date', date)
        .not('topic_name','is',null)
        .limit(1);
      if (lastAtt?.[0]?.topic_name) {
        setTopicName(lastAtt[0].topic_name);
        if (lastAtt[0].faculty_name) setFacultyName(lastAtt[0].faculty_name);
      }
    }
    const attQ = supabase
      .from('attendance')
      .select('*')
      .eq('date', date)
      .in('student_id', studentsForDate.map(s => s.id));
    const effectiveTopic = topicOverride !== undefined ? topicOverride : topicName;
    if (effectiveTopic) attQ.eq('topic_name', effectiveTopic);
    const { data } = await attQ;
    // Load all sessions for this date to show info bar
    const { data: allSess } = await supabase.from('attendance')
      .select('topic_name,faculty_name,num_sessions')
      .eq('date', date)
      .in('student_id', students.slice(0,1).map(s=>s.id))
      .not('topic_name','is',null);
    // Holiday / No Class are day markers, not class sessions - keep them out of the "sessions on this date" bar
    setOtherSessions([...new Map((allSess||[]).filter(a=>a.topic_name!=='Holiday'&&a.topic_name!=='No Class').map(a=>[a.topic_name,a])).values()]);
    const map = {};
    (data || []).forEach(a => {
      const sessObj = {};
      ALL_SESSIONS.forEach(sess => { sessObj[sess.key] = a[sess.key] || 'absent'; });
      sessObj._session_type = a.session_type || 'regular';
      sessObj._repeat_date = a.repeat_date || null;
      map[a.student_id] = sessObj;
    });
    // Only restore numSessions/topic from DB if attendance exists for today
    if (data && data.length > 0) {
      if (data[0].num_sessions) setNumSessions(data[0].num_sessions);
      if (data[0].topic_name) setTopicName(data[0].topic_name);
      // Restore day type (holiday/no_class)
      const dayType = data[0].session_type;
      if (dayType === 'holiday' || dayType === 'no_class') {
        setAttSessionType(dayType);
      } else {
        setAttSessionType('regular');
      }
    } else {
      setAttSessionType('regular');
    }
    setAttendance(map);
  }

  function setSession(studentId, session, value) {
    setAttendance(prev => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || emptyAtt()), [session]: value }
    }));
  }

  // Day value: each session = 0.25, max 1.0
  function dayValue(studentId) {
    const a = attendance[studentId] || emptyAtt(numSessions);
    let count = 0;
    SESSIONS.forEach(s => { if (a[s.key] === 'present') count++; });
    return count > 0 ? parseFloat((count / numSessions).toFixed(4)) : 0;
  }

  function presentSessions(studentId) {
    const a = attendance[studentId] || emptyAtt(numSessions);
    return SESSIONS.filter(s => a[s.key] === 'present').length;
  }

  async function clearAttendance() {
    if (!selectedBatch || !date) return;
    if (!canMark) { toast.error(NOT_OWN); return; }
    if (!window.confirm(`Delete all attendance for ${selectedBatch} on ${formatDate(date)}?`)) return;
    setSaving(true);
    try {
      const {data:stds} = await supabase.from('students').select('id').eq('batch_name',selectedBatch).neq('is_active',false);
      if (stds?.length) {
        await supabase.from('attendance').delete()
          .in('student_id', stds.map(s=>s.id)).eq('date', date);
      }
      toast.success('Attendance cleared');
      await loadAttendanceForDate();
    } catch(e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveHoliday() {
    if (!selectedBatch || !date) { toast.error('Select batch and date'); return; }
    if (!canMark) { toast.error(NOT_OWN); return; }
    setSaving(true);
    try {
      // Store holiday as a special attendance record with session_type = holiday/no_class
      // Get all students in batch
      const {data:stds} = await supabase.from('students')
        .select('id').eq('batch_name', selectedBatch).neq('is_active', false)
        .not('student_status','in','("Discontinued","Shifted")');
      if (!stds?.length) { toast.error('No students found'); setSaving(false); return; }
      // Delete existing attendance for this date if any
      await supabase.from('attendance').delete()
        .in('student_id', stds.map(s=>s.id)).eq('date', date);
      // Insert holiday record for each student
      const label = attSessionType === 'holiday' ? 'Holiday' : 'No Class';
      const rows = stds.map(s=>({
        student_id: s.id,
        date,
        topic_name: label,
        session_type: attSessionType,
        num_sessions: 0,
        session1: 'absent', session2: 'absent',
        day_value: 0,
      }));
      const {error} = await supabase.from('attendance').insert(rows);
      if (error) throw error;
      toast.success(`${label} marked for ${selectedBatch} on ${formatDate(date)}`);
      setAttSessionType('regular');
      try { await loadAttendanceForDate(); } catch (e) { /* the page refreshes on the next date change anyway */ }
    } catch(e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveRepeatAttendance() {
    if (!attRepeatDate || !attRepeatModal) return;
    // Store repeat session info - will be used when marking attendance
    setAttRepeatModal(null);
    setDate(attRepeatModal.originalDate); // Stay on original date
    setAttRepeatSession({
      repeatDate: attRepeatDate,
      originalDate: attRepeatModal.originalDate,
      studentIds: new Set(attRepeatModal.absentStudents.map(s=>s.id)),
      topicName: attRepeatModal.topicName
    });
    toast.success('Now mark who attended the repeat on ' + attRepeatDate + ' — only absent students shown');
    setAttRepeatDate('');
  }

  async function removeRepeatTag(studentId) {
    if (!canMark) { toast.error(NOT_OWN); return; }
    if (!window.confirm('Remove repeat tag for this student? They will be marked absent on this date.')) return;
    const emptyA = emptyAtt(numSessions);
    const updatePayload = { session_type: 'regular', repeat_date: null };
    ALL_SESSIONS.slice(0, numSessions).forEach(sess => { updatePayload[sess.key] = 'absent'; });
    const {error} = await supabase.from('attendance')
      .update(updatePayload)
      .eq('date', date)
      .eq('student_id', studentId);
    if (error) toast.error(error.message);
    else {
      toast.success('Repeat tag removed');
      // Update local state
      setAttendance(prev => {
        const next = {...prev};
        next[studentId] = {...emptyA, _session_type: 'regular', _repeat_date: null};
        return next;
      });
    }
  }

  function printStudentAttendance(sv, batchName) {
    const topicRows = (sv.topicStats||[]).map(ts => {
      const pct = Number(ts.topicPct);
      const color = pct>=75?'#15803d':pct>=50?'#d97706':'#dc2626';
      const bg = pct>=75?'#dcfce7':pct>=50?'#fef9c3':'#fee2e2';
      const dateCells = (ts.dateWise||[]).map(d => {
        const dc = d.sessPresent===d.totalSess?'#dcfce7':d.sessPresent>0?'#fef9c3':'#fee2e2';
        const tc = d.sessPresent===d.totalSess?'#15803d':d.sessPresent>0?'#d97706':'#dc2626';
        const fmtD = dmy(d.date);
        return '<span style="display:inline-block;margin:2px;padding:2px 6px;border-radius:4px;font-size:10px;background:'+dc+';color:'+tc+';border:1px solid '+tc+'">'+fmtD+' '+d.sessPresent+'/'+d.totalSess+(d.session_type==='repeat'?' 🔄':'')+'</span>';
      }).join('');
      return '<tr><td style="padding:8px 10px;font-weight:500;border-bottom:1px solid #e5e7eb">'+ts.topic+'</td>'
        +'<td style="text-align:center;padding:8px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;font-weight:700">'+ts.topicPresent+'</td>'
        +'<td style="text-align:center;padding:8px 10px;border-bottom:1px solid #e5e7eb;color:#6b7280">'+ts.topicTotalSess+'</td>'
        +'<td style="text-align:center;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-weight:700;color:'+color+';background:'+bg+'">'+ts.topicPct+'%</td>'
        +'<td style="padding:8px 10px;border-bottom:1px solid #e5e7eb">'+dateCells+'</td></tr>';
    }).join('');
    const html = '<!DOCTYPE html><html><head><title>Attendance - '+sv.full_name+'</title>'
      +'<style>body{font-family:Arial,sans-serif;font-size:11px;margin:0;padding:16px}h2{font-size:15px;margin-bottom:4px;color:#1a3a6b}p{font-size:11px;color:#666;margin-bottom:12px}.stat{display:inline-block;margin-right:20px;font-size:12px}.stat span{font-weight:700;font-size:14px}table{width:100%;border-collapse:collapse}th{background:#1a3a6b;color:#fff;padding:8px 10px;text-align:left}@media print{button{display:none}@page{size:A4;margin:12mm}}</style>'
      +'</head><body>'
      +'<h2>📊 Attendance Report — '+sv.full_name+'</h2>'
      +'<p>Register No: <strong>'+sv.register_number+'</strong> &nbsp;|&nbsp; Batch: <strong>'+(sv.batch_name||batchName||'')+'</strong></p>'
      +'<div style="margin-bottom:16px">'
      +'<span class="stat">Total Days: <span>'+sv.totalDays+'</span></span>'
      +'<span class="stat">Total Sessions: <span>'+sv.totalSessions+'</span></span>'
      +'<span class="stat">Present: <span style="color:#15803d">'+sv.presentSessions+'</span></span>'
      +'<span class="stat">Overall: <span style="color:'+(Number(sv.pct)>=75?'#15803d':Number(sv.pct)>=50?'#d97706':'#dc2626')+'">'+sv.pct+'%</span></span>'
      +'</div>'
      +'<table><thead><tr><th>Topic</th><th style="text-align:center">Present</th><th style="text-align:center">Total</th><th style="text-align:center">%</th><th>Dates</th></tr></thead>'
      +'<tbody>'+topicRows+'</tbody></table>'
      +'<br/><button onclick="window.print()">🖨️ Print</button>'
      +'</body></html>';
    const w = window.open('','_blank'); w.document.write(html); w.document.close();
  }

  function printStudentAttendance(sv) {
    const topicRows = (sv.topicStats||[]).map(ts => {
      const pct = Number(ts.topicPct);
      const color = pct>=75?'#15803d':pct>=50?'#d97706':'#dc2626';
      const bg = pct>=75?'#dcfce7':pct>=50?'#fef9c3':'#fee2e2';
      const dateCells = (ts.dateWise||[]).map(d => {
        const dc = d.sessPresent===d.totalSess?'#dcfce7':d.sessPresent>0?'#fef9c3':'#fee2e2';
        const tc = d.sessPresent===d.totalSess?'#15803d':d.sessPresent>0?'#d97706':'#dc2626';
        const fmtD = dmy(d.date);
        return '<span style="display:inline-block;margin:2px;padding:2px 6px;border-radius:4px;font-size:10px;background:'+dc+';color:'+tc+';border:1px solid '+tc+'">'+fmtD+' '+d.sessPresent+'/'+d.totalSess+(d.session_type==='repeat'?' 🔄':'')+'</span>';
      }).join('');
      return '<tr><td style="padding:8px 10px;font-weight:500;border-bottom:1px solid #e5e7eb">'+ts.topic+'</td>'
        +'<td style="text-align:center;padding:8px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;font-weight:700">'+ts.topicPresent+'</td>'
        +'<td style="text-align:center;padding:8px 10px;border-bottom:1px solid #e5e7eb;color:#6b7280">'+ts.topicTotalSess+'</td>'
        +'<td style="text-align:center;padding:8px 10px;border-bottom:1px solid #e5e7eb;font-weight:700;color:'+color+';background:'+bg+'">'+ts.topicPct+'%</td>'
        +'<td style="padding:8px 10px;border-bottom:1px solid #e5e7eb">'+dateCells+'</td></tr>';
    }).join('');
    const html = '<!DOCTYPE html><html><head><title>Attendance - '+sv.full_name+'</title>'
      +'<style>body{font-family:Arial,sans-serif;font-size:11px;margin:0;padding:16px}h2{font-size:15px;margin-bottom:4px;color:#1a3a6b}p{font-size:11px;color:#666;margin-bottom:12px}.stat{display:inline-block;margin-right:20px;font-size:12px}.stat span{font-weight:700;font-size:14px}table{width:100%;border-collapse:collapse}th{background:#1a3a6b;color:#fff;padding:8px 10px;text-align:left}@media print{button{display:none}@page{size:A4;margin:12mm}}</style>'
      +'</head><body>'
      +'<h2>📊 Attendance Report — '+sv.full_name+'</h2>'
      +'<p>Register No: <strong>'+sv.register_number+'</strong> &nbsp;|&nbsp; Batch: <strong>'+(sv.batch_name||'')+'</strong></p>'
      +'<div style="margin-bottom:16px">'
      +'<span class="stat">Total Days: <span>'+sv.totalDays+'</span></span>'
      +'<span class="stat">Total Sessions: <span>'+sv.totalSessions+'</span></span>'
      +'<span class="stat">Present: <span style="color:#15803d">'+sv.presentSessions+'</span></span>'
      +'<span class="stat">Overall: <span style="color:'+(Number(sv.pct)>=75?'#15803d':Number(sv.pct)>=50?'#d97706':'#dc2626')+'">'+sv.pct+'%</span></span>'
      +'</div>'
      +'<table><thead><tr><th>Topic</th><th style="text-align:center">Present</th><th style="text-align:center">Total</th><th style="text-align:center">%</th><th>Dates</th></tr></thead>'
      +'<tbody>'+topicRows+'</tbody></table>'
      +'<br/><button onclick="window.print()">🖨️ Print</button>'
      +'</body></html>';
    const w = window.open('','_blank'); w.document.write(html); w.document.close();
  }

  async function loadCallLogs(date, batch, students) {
    if (!students.length) return;
    const {data:staff} = await supabase.from('hr_staff').select('id,staff_name').eq('is_active',true).in('designation',['Academic Counselor','Office Admin']);
    setCallLogStaff(staff||[]);
    const topic = trackerTopic||'';
    const {data:logs} = await supabase.from('attendance_absence_calls')
      .select('*').in('student_id',students.map(s=>s.id)).eq('absent_date',date).eq('batch_name',batch)
      .or(`topic_name.eq.${topic},topic_name.is.null`);
    const m = {};
    (logs||[]).forEach(l=>{ m[l.student_id]=l; });
    setCallLogs(m);
  }

  async function saveCallLog(student, date, batch) {
    if (!ownBatch(batch)) { toast.error(NOT_OWN); return; }
    const log = callLogs[student.id]||{};
    setCallLogSaving(p=>({...p,[student.id]:true}));
    const payload = { student_id:student.id, batch_name:batch, absent_date:date,
      topic_name:trackerTopic||'',
      called_by:log.called_by||null, call_date:log.call_date||date,
      reason:log.reason||'', remarks:log.remarks||'', call_status:log.call_status||'Not Called' };
    if (log.id) {
      const {error:e1} = await supabase.from('attendance_absence_calls').update(payload).eq('id',log.id);
      if(e1) { toast.error('Update error: '+e1.message); setCallLogSaving(p=>({...p,[student.id]:false})); return; }
    } else {
      const {data:d,error:e2} = await supabase.from('attendance_absence_calls').insert(payload).select().single();
      if(e2) { toast.error('Insert error: '+e2.message); setCallLogSaving(p=>({...p,[student.id]:false})); return; }
      if(d) setCallLogs(p=>({...p,[student.id]:{...p[student.id],id:d.id}}));
    }
    setCallLogSaving(p=>({...p,[student.id]:false}));
    toast.success('Call log saved');
  }

  function updateCallLog(studentId, field, value) {
    setCallLogs(p=>({...p,[studentId]:{...(p[studentId]||{}),[field]:value}}));
  }

  async function loadTrackerData() {
    if (!trackerBatch || !trackerTopic) return;
    setTrackerLoading(true);
    // Fetch students for this batch directly
    const { data: stData } = await supabase.from('students')
      .select('id, full_name, register_number, batch_name')
      .eq('batch_name', trackerBatch)
      .neq('is_active', false)
      .not('student_status', 'in', '("Discontinued","Shifted","Passed Out")')
      .order('register_number');
    const batchStudents = stData || [];
    const sIds = batchStudents.map(s => s.id);
    const { data } = await supabase.from('attendance')
      .select('student_id, date, session1, session2, session3, session4, session5, session6, session7, session8, num_sessions, session_type, repeat_date')
      .eq('topic_name', trackerTopic)
      .in('student_id', sIds)
      .not('session_type', 'in', '("holiday","no_class")')
      .order('date');
    const grid = {};
    const dateSet = new Set();
    (data||[]).forEach(a => {
      dateSet.add(a.date);
      const n = a.num_sessions || 4;
      let present = 0;
      ALL_SESSIONS.slice(0, n).forEach(sess => { if (a[sess.key]==='present') present++; });
      if (!grid[a.student_id]) grid[a.student_id] = {};
      grid[a.student_id][a.date] = { present, total: n, session_type: a.session_type, repeat_date: a.repeat_date };
    });
    const dates = [...dateSet].sort();
    setTrackerData({ dates, students: batchStudents, grid });
    setTrackerLoading(false);
  }

  React.useEffect(() => {
    if (!trackerBatch) { setAllTopicNames([]); setTrackerTopic(''); setTrackerBatchStudents([]); setTrackerTopicList([]); return; }
    setTrackerTopicList(null);
    supabase.from('students')
      .select('id,full_name,register_number').eq('batch_name', trackerBatch).neq('is_active', false)
      .order('register_number', {ascending: true})
      .then(async ({data: stds}) => {
        setTrackerBatchStudents(stds||[]);
        // Topics that actually have classes for this batch: ongoing / completed
        try {
          const ids0 = (stds||[]).map(x => x.id);
          const seen = {};   // topic -> Set(dates)
          for (let pg = 0; pg < 60 && ids0.length; pg++) {
            const { data: pageRows, error: pe } = await supabase.from('attendance')
              .select('topic_name, date, session_type').in('student_id', ids0).not('topic_name', 'is', null)
              .order('date').range(pg * 1000, (pg + 1) * 1000 - 1);
            if (pe) throw pe;
            (pageRows || []).forEach(r => {
              if (!r.topic_name || r.topic_name === 'Holiday' || r.topic_name === 'No Class' || r.session_type === 'holiday' || r.session_type === 'no_class') return;
              (seen[r.topic_name] = seen[r.topic_name] || new Set()).add(r.date);
            });
            if (!pageRows || pageRows.length < 1000) break;
          }
          const today = Date.now();
          const list = Object.keys(seen).map(name => {
            const dts = [...seen[name]].sort(); const last = dts[dts.length - 1]; const days = dts.length;
            const dur = Number((topics.find(t => t.topic_name === name) || {}).duration_days) || 0;
            const idle = (today - new Date(last).getTime()) / 86400000;
            const done = (dur > 0 && days >= dur) || idle > 14;
            return { name, last, days, dur, status: done ? 'completed' : 'ongoing',
              label: name };
          }).sort((a, b) => (a.status === b.status ? String(b.last).localeCompare(String(a.last)) : (a.status === 'ongoing' ? -1 : 1)));
          setTrackerTopicList(list);
        } catch (err) { setTrackerTopicList([]); }
        // Load topics from topics table based on batch discipline
        const {data: bData} = await supabase.from('batches').select('discipline').eq('batch_name', trackerBatch).single();
        const disc = (bData?.discipline||'').toLowerCase().includes('civil') ? 'civil' : 'mechanical';
        const {data: topicsData} = await supabase.from('topics').select('topic_name').eq('discipline', disc).order('topic_name');
        if (topicsData?.length) {
          setAllTopicNames(topicsData.map(t=>t.topic_name).filter(Boolean));
        } else if (stds?.length) {
          // fallback: scan attendance table
          const ids = stds.map(s => s.id);
          const {data} = await supabase.from('attendance')
            .select('topic_name').in('student_id', ids).not('topic_name','is',null).limit(5000);
          setAllTopicNames([...new Set((data||[]).map(d=>d.topic_name).filter(Boolean))].filter(t=>t!=='Holiday'&&t!=='No Class'));
        }
      });
    loadLeavePeriods(trackerBatch);
    loadOnlinePeriods(trackerBatch);
    setTrackerTopic(''); setTrackerData(null);
  // eslint-disable-next-line
  }, [trackerBatch]);

  function printAbsentTracker() {
    if (!trackerData) return;
    const dateHeaders = trackerData.dates.map(d =>
      '<th style="text-align:center;white-space:nowrap;font-size:9px;padding:4px 3px">'+
      dmy(d)+'<br/>'+
      '<span style="color:#dc2626;font-size:8px">'+
      trackerData.students.filter(s=>!trackerData.grid[s.id]?.[d]||trackerData.grid[s.id][d].present===0).length+'✗</span></th>'
    ).join('');

    const rows = trackerData.students.map((s,i) => {
      const totalSess = trackerData.dates.reduce((acc,d)=>acc+(trackerData.grid[s.id]?.[d]?.total||0),0);
      const presentSess = trackerData.dates.reduce((acc,d)=>acc+(trackerData.grid[s.id]?.[d]?.present||0),0);
      const pct = totalSess>0?(presentSess/totalSess*100).toFixed(0):null;
      const pctColor = pct===null?'#666':pct>=75?'#15803d':pct>=50?'#d97706':'#dc2626';
      const dateCells = trackerData.dates.map(d => {
        const rec = trackerData.grid[s.id]?.[d];
        if (!rec) return '<td style="text-align:center;background:#f3f4f6;color:#9ca3af;font-size:9px">—</td>';
        const bg = rec.present===rec.total?(rec.session_type==='repeat'?'#fef9c3':'#dcfce7'):rec.present>0?'#fef9c3':'#fee2e2';
        const col = rec.present===rec.total?(rec.session_type==='repeat'?'#854d0e':'#15803d'):rec.present>0?'#854d0e':'#dc2626';
        return '<td style="text-align:center;background:'+bg+';color:'+col+';font-weight:700;font-size:9px;padding:3px">'+rec.present+'/'+rec.total+(rec.session_type==='repeat'?' R':'')+'</td>';
      }).join('');
      return '<tr style="background:'+(i%2===0?'#fff':'#f9fafb')+'">'
        +'<td style="text-align:center;padding:4px 6px;font-size:10px;color:#6b7280">'+(i+1)+'</td>'
        +'<td style="padding:4px 6px;font-family:monospace;font-size:10px;white-space:nowrap">'+s.register_number+'</td>'
        +'<td style="padding:4px 6px;font-size:10px;white-space:nowrap">'+s.full_name+'</td>'
        +dateCells
        +'<td style="text-align:center;font-weight:700;font-size:11px;color:'+pctColor+'">'+(pct===null?'—':pct+'%')+'</td>'
        +'</tr>';
    }).join('');

    const absentSummary = trackerData.dates.map(d => {
      const absentList = trackerData.students.filter(s=>!trackerData.grid[s.id]?.[d]||trackerData.grid[s.id][d].present===0);
      if (!absentList.length) return '';
      return '<div style="margin-bottom:8px"><strong>'+dmy(d)+'</strong> — '+absentList.length+' absent: '
        +absentList.map(s=>s.register_number+' '+s.full_name).join(', ')+'</div>';
    }).join('');

    const html = '<!DOCTYPE html><html><head><title>Absent Tracker - '+trackerBatch+' - '+trackerTopic+'</title>'
      +'<style>body{font-family:Arial,sans-serif;font-size:10px;margin:0;padding:12px}h2{font-size:14px;color:#1a3a6b;margin-bottom:4px}p{font-size:11px;color:#666;margin-bottom:10px}table{width:100%;border-collapse:collapse}th{background:#1a3a6b;color:#fff;padding:5px 4px}td{border:1px solid #e5e7eb;padding:3px}h3{font-size:12px;margin-top:16px;color:#1a3a6b}@media print{button{display:none}@page{size:A4 landscape;margin:8mm}}</style>'
      +'</head><body>'
      +'<h2>📋 Absent Tracker — '+trackerBatch+' / '+trackerTopic+'</h2>'
      +'<p>'+trackerData.dates.length+' days | '+trackerData.students.length+' students</p>'
      +'<table><thead><tr>'
      +'<th style="width:30px">#</th><th style="white-space:nowrap">Reg No</th><th style="text-align:left">Name</th>'
      +dateHeaders
      +'<th>%</th></tr></thead><tbody>'+rows+'</tbody></table>'
      +'<h3>Absent Student Details by Date</h3>'+absentSummary
      +'<br/><button onclick="window.print()">🖨️ Print</button>'
      +'</body></html>';
    const w = window.open('','_blank'); w.document.write(html); w.document.close();
  }



  async function saveAttendance() {
    if (!selectedBatch) { toast.error('Select a batch first'); return; }
    if (!canMark) { toast.error(NOT_OWN); return; }
    setSaving(true);
    // In repeat mode: update original date records for present students only
    const isRepeatMode = attRepeatSession?.originalDate === date;
    if (isRepeatMode) {
      // Find students who are marked present in this repeat marking
      const presentStudents = filteredStudents.filter(s => {
        const a = attendance[s.id];
        if (!a) return false;
        return ALL_SESSIONS.slice(0, numSessions).some(sess => a[sess.key] === 'present');
      });
      if (presentStudents.length > 0) {
        // Update their original date record: mark present + tag as repeat
        for (const s of presentStudents) {
          const a = attendance[s.id] || emptyAtt(numSessions);
          const updatePayload = {
            session_type: 'repeat',
            repeat_date: attRepeatSession.repeatDate,
          };
          ALL_SESSIONS.slice(0, numSessions).forEach(sess => {
            updatePayload[sess.key] = a[sess.key] || 'absent';
          });
          await supabase.from('attendance')
            .update(updatePayload)
            .eq('date', date)
            .eq('student_id', s.id);
        }
        toast.success(presentStudents.length + ' student(s) marked present via repeat session on ' + attRepeatSession.repeatDate);
        setAttRepeatSession(null);
      } else {
        toast.error('Please mark at least one student as present');
      }
      setSaving(false);
      return;
    }
    const saveStudents = studentsForDate;
    const records = saveStudents.map(s => {
      const onLeave = isOnLeave(s.id, date);
      const online = isOnline(s.id, date);
      const forceAbsent = onLeave || online;
      const a = forceAbsent ? emptyAtt(numSessions) : (attendance[s.id] || emptyAtt(numSessions));
      // Leave counts as absent (affects attendance %), online excluded from %
      const dv = online ? 0 : dayValue(s.id);
      const payload = {
        student_id: s.id,
        date,
        topic_name: topicName || null,
        faculty_name: facultyName || null,
        day_value: dv,
        num_sessions: numSessions,
        session_type: 'regular',
        original_date: null,
      };
      ALL_SESSIONS.forEach(sess => {
        payload[sess.key] = forceAbsent ? 'absent' : (a[sess.key] || 'absent');
      });
      return payload;
    });
    await supabase.from('attendance').delete()
      .eq('date', date)
      .eq('topic_name', topicName)
      .in('student_id', saveStudents.map(s => s.id));

    const { error } = await supabase.from('attendance').insert(records);
    if (error) toast.error(error.message);
    else {
      toast.success(`Attendance saved for ${records.length} students - ${formatDate(date)}`);
      if (summaryBatch === selectedBatch) loadSummary(summaryBatch);
    }
    setSaving(false);
  }

  function markAll(session, value) {
    const newAtt = { ...attendance };
    students.filter(s => (!pkgFilter || s.course_name === pkgFilter) && (!s.course_end_date || s.course_end_date >= date)).forEach(s => {
      newAtt[s.id] = { ...(newAtt[s.id] || emptyAtt()), [session]: value };
    });
    setAttendance(newAtt);
  }

  function markAllSessions(value) {
    const newAtt = { ...attendance };
    students.filter(s => (!pkgFilter || s.course_name === pkgFilter) && (!s.course_end_date || s.course_end_date >= date)).forEach(s => {
      const a = {};
      SESSIONS.forEach(sess => { a[sess.key] = value; });
      newAtt[s.id] = a;
    });
    setAttendance(newAtt);
  }

  async function loadSummary(batchIdOverride) {
    const batchId = typeof (batchIdOverride||summaryBatch)==='object' ? (batchIdOverride||summaryBatch)?.value : (batchIdOverride||summaryBatch);
    if (!batchId) return;
    setSummaryLoading(true);

    // Fetch ALL students in batch using pagination (no is_active filter)
    let allStds = [];
    let page = 0;
    const pageSize = 1000;
    while (true) {
      const { data, error } = await supabase
        .from('students')
        .select('id, full_name, register_number, batch_name, joining_date, student_status, course_end_date')
        .eq('batch_name', batchId)
        .neq('is_active', false)
        .not('student_status', 'in', '("Discontinued","Shifted")')
        .order('register_number')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (error || !data || data.length === 0) break;
      allStds = [...allStds, ...data];
      if (data.length < pageSize) break;
      page++;
    }

    allStds = allStds.filter(s => s.student_status !== 'Passed Out' || s.course_end_date);
    if (allStds.length === 0) { setSummaryData([]); setSummaryLoading(false); return; }
    const stds = allStds;

    const ids = stds.map(s => s.id);
    let query = supabase
      .from('attendance')
      .select('student_id, date, topic_name, faculty_name, num_sessions, session1, session2, session3, session4, session5, session6, session7, session8, day_value')
      .in('student_id', ids)
      .not('session_type', 'in', '("holiday","no_class")');
    if (summaryFromDate) query = query.gte('date', summaryFromDate);
    if (summaryToDate)   query = query.lte('date', summaryToDate);
    const { data: att } = await query;

    // Lab Report sessions of this batch: every session held counts as 1 session, present = 1
    let labRows = [];
    const _labBatchId = (batches || []).find(b => b.batch_name === batchId)?.id;
    if (_labBatchId && !summaryTopic) {
      for (let lp = 0; lp < 60; lp++) {
        let lq = supabase.from('lab_attendance').select('student_id, topic_id, session_date, session_no, status')
          .eq('batch_id', _labBatchId).order('session_date').order('session_no').order('student_id').order('topic_id')
          .range(lp * 1000, lp * 1000 + 999);
        if (summaryFromDate) lq = lq.gte('session_date', summaryFromDate);
        if (summaryToDate)   lq = lq.lte('session_date', summaryToDate);
        const { data: labPage, error: labErr } = await lq;
        if (labErr) break;
        labRows = labRows.concat(labPage || []);
        if (!labPage || labPage.length < 1000) break;
      }
    }

    const allAtt = att || [];

    const allTopics = [...new Set(allAtt.map(a => a.topic_name).filter(Boolean))].filter(t=>t!=='Holiday'&&t!=='No Class');

    const filteredAtt = summaryTopic
      ? allAtt.filter(a => a.topic_name === summaryTopic)
      : allAtt;

    const uniqueDates = [...new Set(filteredAtt.map(a => a.date))];

    // Total sessions = unique date+topic combinations (not per student)
    const dateTopicMap = {};
    filteredAtt.forEach(a => {
      const key = a.date + '||' + (a.topic_name||'');
      if (!dateTopicMap[key]) dateTopicMap[key] = a.num_sessions || 4;
    });
    const totalSessions = Object.values(dateTopicMap).reduce((s,n)=>s+n, 0);

    const topicTotalMap = {};
    allTopics.forEach(topic => {
      const topicDates = [...new Set(allAtt.filter(a => a.topic_name === topic).map(a => a.date))];
      topicTotalMap[topic] = topicDates.reduce((sum, d) => {
        const rec = allAtt.find(a => a.date === d && a.topic_name === topic);
        return sum + (rec?.num_sessions || 4);
      }, 0);
    });

    const displayTopics = summaryTopic ? [summaryTopic] : allTopics;

    const result = stds.map(s => {
      // Students who completed early are measured only against sessions up to their completion date
      const endD = s.course_end_date || null;
      const sTotalSessions = endD
        ? Object.entries(dateTopicMap).reduce((sum, [k, n]) => (k.split('||')[0] <= endD ? sum + n : sum), 0)
        : totalSessions;
      const sUniqueDates = endD ? uniqueDates.filter(d => d <= endD) : uniqueDates;
      const myAtt = filteredAtt.filter(a => a.student_id === s.id &&
        (!s.joining_date || a.date >= s.joining_date) && (!endD || a.date <= endD));

      let presentSess = 0;
      myAtt.forEach(a => {
        const n = a.num_sessions || 4;
        ALL_SESSIONS.slice(0, n).forEach(sess => {
          if (a[sess.key] === 'present') presentSess++;
        });
      });

      const presentDays = myAtt.reduce((acc, a) => acc + Number(a.day_value || 0), 0);
      const _labOk = d => (!s.joining_date || d >= s.joining_date) && (!endD || d <= endD);
      // Only the lab sessions this student was part of (a lab day is often attended by one group only)
      const myLabRows  = labRows.filter(r => r.student_id === s.id && _labOk(r.session_date));
      const labTotal   = myLabRows.length;
      const labPresent = myLabRows.filter(r => r.status === 'present').length;
      const pct = (sTotalSessions + labTotal) > 0 ? (((presentSess + labPresent) / (sTotalSessions + labTotal)) * 100).toFixed(1) : '0';

      const topicStats = displayTopics.map(topic => {
        const topicAtt = allAtt.filter(a => a.student_id === s.id && a.topic_name === topic);
        const topicTotalSess = endD
          ? [...new Set(allAtt.filter(a => a.topic_name === topic && a.date <= endD).map(a => a.date))]
              .reduce((sum, d) => sum + (allAtt.find(a => a.date === d && a.topic_name === topic)?.num_sessions || 4), 0)
          : (topicTotalMap[topic] || 0);
        let topicPresent = 0;
        topicAtt.forEach(a => {
          const n = a.num_sessions || 4;
          ALL_SESSIONS.slice(0, n).forEach(sess => {
            if (a[sess.key] === 'present') topicPresent++;
          });
        });
        const topicPct = topicTotalSess > 0 ? ((topicPresent / topicTotalSess) * 100).toFixed(1) : '0';
        const dateWise = topicAtt.map(a => {
          const n = a.num_sessions || 4;
          let sessPresent = 0;
          ALL_SESSIONS.slice(0, n).forEach(sess => { if (a[sess.key]==='present') sessPresent++; });
          return { date: a.date, present: sessPresent > 0, sessPresent, totalSess: n, session_type: a.session_type, repeat_date: a.repeat_date };
        }).sort((a,b) => a.date.localeCompare(b.date));
        return { topic, topicPresent, topicTotalSess, topicPct, dateWise };
      });

      // Lab topics for the row detail: one entry per lab topic, one chip per lab session held
      const labStats = [...new Set(myLabRows.map(r => String(r.topic_id)))].map(tid => {
        const dateWise = myLabRows.filter(r => String(r.topic_id) === tid).map(r => ({
          date: r.session_date, no: r.session_no || 1, present: r.status === 'present', sessPresent: r.status === 'present' ? 1 : 0, totalSess: 1,
          session_type: r.session_type || 'regular', repeat_date: r.original_date || null
        })).sort((x, y) => x.date.localeCompare(y.date) || (x.no - y.no));
        const topicPresent = dateWise.filter(d => d.present).length;
        const nm = (topics.find(t => String(t.id) === tid) || {}).topic_name || 'Lab';
        return { topic: nm + ' (Lab)', isLab: true, topicPresent, topicTotalSess: dateWise.length, topicPct: ((topicPresent / dateWise.length) * 100).toFixed(1), dateWise };
      }).filter(Boolean);

      return { ...s, totalDays: sUniqueDates.length, presentDays: presentDays.toFixed(1), totalSessions: sTotalSessions + labTotal, presentSessions: presentSess + labPresent, labSessions: labTotal, labPresent, pct, topicStats: [...topicStats, ...labStats] };
    });

    const tObjsFinal = (summaryTopic ? [summaryTopic] : allTopics).map(t => { const f = topics.find(x => x.topic_name === t); return { topic_name: t, short_code: f?.short_code || t }; });
    setSummaryTopics(tObjsFinal);
    // Build topic -> faculty map from raw attendance data
    const facMap = {};
    (allAtt||[]).forEach(a => {
      if (a.topic_name && a.faculty_name) facMap[a.topic_name] = a.faculty_name;
    });
    setTopicFacultyMap(facMap);
    setSummaryData(result);
    setSummaryLoading(false);
    // Re-run loadBatchTopics so dropdown stays populated after Generate
    loadBatchTopics(batchId);
  }

  // Load just topic names for selected batch (for dropdown, before Generate)
  async function loadBatchTopics(batchName) {
    if (!batchName) { setSummaryTopics([]); return; }
    // Get student ids for this batch
    const { data: stds } = await supabase
      .from('students')
      .select('id')
      .eq('batch_name', batchName);
    if (!stds || stds.length === 0) { setSummaryTopics([]); return; }
    const ids = stds.map(s => s.id);
    const { data: att } = await supabase
      .from('attendance')
      .select('topic_name, faculty_name')
      .not('session_type', 'in', '("holiday","no_class")')
      .in('student_id', ids);
    const tNames = [...new Set((att||[]).map(a => a.topic_name).filter(Boolean))].filter(t=>t!=='Holiday'&&t!=='No Class').sort();
    const tObjs = tNames.map(t => {
      const f = topics.find(x => x.topic_name === t);
      const facEntry = [...(att||[])].filter(a=>a.topic_name===t&&a.faculty_name).pop();
      return { topic_name: t, short_code: f?.short_code || t, faculty_name: facEntry?.faculty_name||'' };
    });
    // Build topic -> faculty map
    const facMap2 = {};
    tObjs.forEach(t => { if (t.faculty_name) facMap2[t.topic_name] = t.faculty_name; });
    setTopicFacultyMap(facMap2);
    setSummaryTopics(tObjs);
  }

  const [summaryViewStudent, setSummaryViewStudent] = useState(null);
  // Lab topics that have sessions in the loaded summary - shown as extra columns after the theory topics
  const summaryLabCols = React.useMemo(() => {
    const map = new Map();
    (summaryData || []).forEach(st => (st.topicStats || []).forEach(ts => {
      if (!ts.isLab) return;
      const e = map.get(ts.topic) || { topic: ts.topic, dates: new Set() };
      (ts.dateWise || []).forEach(d => e.dates.add(d.date + '~' + (d.no || 1)));
      map.set(ts.topic, e);
    }));
    return [...map.values()].map(e => ({ topic: e.topic, days: e.dates.size })).sort((a, b) => a.topic.localeCompare(b.topic));
  }, [summaryData]);

  // If repeat session active, only show those students
  const displayStudents = repeatSession
    ? students.filter(s => (repeatSession.student_ids||[]).includes(s.id))
    : students;
  // When in repeat session mode, only show absent students from original date
  const repeatStudentIds = attRepeatSession?.originalDate === date ? attRepeatSession.studentIds : null;
  const filteredStudents = displayStudents.filter(s => {
    if (repeatStudentIds && !repeatStudentIds.has(s.id)) return false;
    if (s.course_end_date && s.course_end_date < date) return false; // course already completed on this date
    if (pkgFilter && s.course_name !== pkgFilter) return false;
    return !search || s.full_name?.toLowerCase().includes(search.toLowerCase()) || s.register_number?.toLowerCase().includes(search.toLowerCase());
  }
  );

  function changeDate(days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().split('T')[0]);
    setTopicName('');
    setFacultyName('');
  }

  const inCourse = s => !s.course_end_date || s.course_end_date >= date;
  const packageOptions = React.useMemo(
    () => [...new Set(students.filter(s => !s.course_end_date || s.course_end_date >= date).map(s => s.course_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [students, date]);
  const SESSIONS = ALL_SESSIONS.slice(0, numSessions);
  const totalPresent = filteredStudents.filter(s => presentSessions(s.id) > 0).length;
  const totalAbsent  = filteredStudents.filter(s => presentSessions(s.id) === 0).length;

  async function loadFacultyReport(batchOverride) {
    setFacultyReportLoading(true);
    const batch = batchOverride !== undefined ? batchOverride : facultyReportBatch;
    // Get student ids and their batch names
    let studentQuery = supabase.from('students').select('id,batch_name').neq('is_active',false);
    if (batch) studentQuery = studentQuery.eq('batch_name', batch);
    else {
      // Only active batches
      const activeBatchNames = batches.filter(b=>b.status==='ongoing').map(b=>b.batch_name);
      if (activeBatchNames.length) studentQuery = studentQuery.in('batch_name', activeBatchNames);
    }
    const {data:stds} = await studentQuery;
    const sidToBatch = {};
    (stds||[]).forEach(s=>{ sidToBatch[s.id]=s.batch_name; });
    const studentIds = (stds||[]).map(s=>s.id);
    if (!studentIds.length) { setFacultyReport([]); setFacultyReportLoading(false); return; }
    const {data:att} = await supabase.from('attendance')
      .select('student_id,topic_name,faculty_name,date,num_sessions,session_type')
      .in('student_id', studentIds);
    if (!att) { setFacultyReportLoading(false); return; }

    // Split holiday/no_class from regular attendance
    const holidayAtt = (att||[]).filter(a =>
      a.session_type === 'holiday' || a.session_type === 'no_class' ||
      a.topic_name === 'Holiday' || a.topic_name === 'No Class'
    );
    const regularAtt = (att||[]).filter(a =>
      a.session_type !== 'holiday' && a.session_type !== 'no_class' &&
      a.topic_name !== 'Holiday' && a.topic_name !== 'No Class'
    );

    // Build holiday summary (deduplicated by batch+date+label)
    const holidayDateMap = {};
    holidayAtt.forEach(a => {
      const batchName = sidToBatch[a.student_id] || batch || 'All';
      const label = a.topic_name || (a.session_type === 'holiday' ? 'Holiday' : 'No Class');
      const key = batchName + '||' + a.date + '||' + label;
      if (!holidayDateMap[key]) holidayDateMap[key] = { batch: batchName, date: a.date, label };
    });
    const hRows = Object.values(holidayDateMap).sort((a,b) =>
      a.batch.localeCompare(b.batch) || a.date.localeCompare(b.date)
    );
    const hGrouped = {};
    hRows.forEach(h => {
      const key = h.batch + '||' + h.label;
      if (!hGrouped[key]) hGrouped[key] = { batch: h.batch, label: h.label, dates: [] };
      hGrouped[key].dates.push(h.date);
    });
    const holidayRows = Object.values(hGrouped).map(g => ({
      batch: g.batch, label: g.label, days: g.dates.length,
      fromDate: g.dates[0], toDate: g.dates[g.dates.length - 1]
    })).sort((a,b) => a.batch.localeCompare(b.batch) || a.fromDate.localeCompare(b.fromDate));
    setFacultyHolidays(holidayRows);

    // Group by batch+topic+date+faculty — REGULAR attendance only
    // Step 1: Build date-level map: key=batch||topic||date -> {faculty, num_sessions}
    const dateLevelMap = {};
    regularAtt.forEach(a => {
      const batchName = sidToBatch[a.student_id] || batch || 'All';
      const dateKey = batchName + '||' + (a.topic_name||'') + '||' + (a.date||'');
      if (!dateLevelMap[dateKey]) {
        dateLevelMap[dateKey] = { batch: batchName, topic: a.topic_name, date: a.date, faculty: '', num_sessions: a.num_sessions||4 };
      }
      // Take the first non-empty faculty for this date (all students on same date have same faculty)
      if (a.faculty_name && !dateLevelMap[dateKey].faculty) {
        dateLevelMap[dateKey].faculty = a.faculty_name;
      }
    });

    // Step 2: Group date-level entries by batch+topic+faculty (consecutive same faculty = one row)
    // Each unique batch+topic+faculty combination becomes a row showing its date range
    const grouped = {};
    Object.values(dateLevelMap).forEach(d => {
      // Key by batch+topic+faculty so different faculty = different row
      const key = d.batch + '||' + d.topic + '||' + (d.faculty||'Not Assigned');
      if (!grouped[key]) {
        grouped[key] = { batch: d.batch, topic: d.topic, faculty: d.faculty||'Not Assigned', dateMap: {} };
      }
      grouped[key].dateMap[d.date] = d.num_sessions;
    });

    const rows = Object.values(grouped).map(g => {
      const dates = Object.keys(g.dateMap).sort();
      const totalSessions = Object.values(g.dateMap).reduce((s,n)=>s+Number(n||0),0);
      return { batch: g.batch, topic: g.topic, faculty: g.faculty, days: dates.length, totalSessions, fromDate: dates[0], toDate: dates[dates.length-1] };
   }).sort((a,b)=>a.batch.localeCompare(b.batch)||(a.fromDate||'').localeCompare(b.fromDate||'')||a.topic.localeCompare(b.topic));
    setFacultyReport(rows);
    setFacultyReportLoading(false);
  }


  return (
    <>
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Attendance</h1><p className="page-subtitle">Mark and view student attendance</p></div>
        {!batchRestricted && <ImportButton configKey="attendance" onImported={()=>loadBaseData()}/>}
        <ExportButton
          filename="Attendance_Report.xlsx"
          label="Export Attendance"
          getData={async () => {
            const { data: att } = await supabase
              .from('attendance')
              .select('*, students(full_name, register_number, batches(batch_name))')
              .order('date', { ascending: false });
            const data = (att || []).map((a, i) => ({
              'S.No': i + 1, 'Date': fmtDate(a.date),
              'Student Name': a.students?.full_name || '', 'Register No': a.students?.register_number || '',
              'Batch': a.students?.batches?.batch_name || '', 'Topic': a.topic_name || '',
              'Morning-1': a.morning1 || '', 'Morning-2': a.morning2 || '',
              'Afternoon-1': a.afternoon1 || '', 'Afternoon-2': a.afternoon2 || '',
              'Day Value': a.day_value,
              'Status': a.day_value >= 1 ? 'Full Day' : a.day_value >= 0.5 ? 'Half Day' : a.day_value > 0 ? 'Partial' : 'Absent',
            }));
            return [{ name: 'Attendance', data }];
          }}
        />
      </div>

      <div style={{overflowX:"auto",width:"100%",maxWidth:"100%",boxSizing:"border-box"}}><div className="tabs" style={{minWidth:"max-content"}}>
        {canView('attendance_mark') && <div className={`tab ${activeTab === 'mark' ? 'active' : ''}`} style={{whiteSpace:"nowrap"}} onClick={() => setActiveTab('mark')}>Mark Attendance</div>}
        {canView('attendance_summary') && <div className={`tab ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => {
          setActiveTab('summary');
          if (selectedBatch && !summaryBatch) { setSummaryBatch(selectedBatch); loadBatchTopics(selectedBatch); }
        }}>Attendance Summary</div>}
        {canView('attendance_reports') && <div className={`tab ${activeTab === 'daily_log' ? 'active' : ''}`} onClick={() => setActiveTab('daily_log')}>📅 Daily Log</div>}
        {canView('attendance_reports') && <div className={`tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => {
          setActiveTab('reports');
          if (selectedBatch && !summaryBatch) { setSummaryBatch(selectedBatch); loadBatchTopics(selectedBatch); }
        }}>🖨️ Reports</div>}
        {canView('attendance_absent_tracker') && <div className={`tab ${activeTab === 'absent_tracker' ? 'active' : ''}`} onClick={() => setActiveTab('absent_tracker')}>📋 Absent Tracker</div>}
        {canView('attendance_lab') && <div className={`tab ${activeTab === 'lab' ? 'active' : ''}`} onClick={() => {
          setActiveTab('lab');
        }}>🧪 Lab Report</div>}
        {canView('attendance_faculty') && <div className={`tab ${activeTab === 'faculty_report' ? 'active' : ''}`} onClick={() => {
          setActiveTab('faculty_report'); loadFacultyReport();
        }}>Faculty Report</div>}
        {canView('attendance_schedule') && <div className={`tab ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => {
          setActiveTab('schedule');
        }}>Class Schedule</div>}
      </div></div>

      {/* ── MARK ATTENDANCE ── */}
      {activeTab === 'mark' && (
        <>
          {/* Session Details */}
          <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)' }}>Session Details</div>
              {otherSessions.length > 0 && (
                <div style={{background:'var(--accent-light)',borderRadius:6,padding:'6px 12px',fontSize:12,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                  <span style={{fontWeight:600,color:'var(--accent)'}}>📋 {otherSessions.length} session(s) on this date:</span>
                  {otherSessions.map(s=>(
                    <span key={s.topic_name}
                      onClick={()=>{
                        setTopicName(s.topic_name);
                        if(s.faculty_name) setFacultyName(s.faculty_name);
                        loadAttendanceForDate(s.topic_name);
                      }}
                      style={{padding:'2px 8px',borderRadius:4,cursor:'pointer',
                        background:topicName===s.topic_name?'var(--accent)':'#fff',
                        color:topicName===s.topic_name?'#fff':'var(--accent)',
                        border:'1px solid var(--accent)',fontSize:11,fontWeight:500}}>
                      {s.topic_name} ({s.num_sessions} sess)
                    </span>
                  ))}
                </div>
              )}
              {repeatSession && (
                <span style={{fontSize:11,background:'rgba(245,158,11,0.15)',color:'var(--amber)',padding:'3px 10px',borderRadius:12,fontWeight:600,border:'1px solid var(--amber)'}}>
                  Repeat Session - {repeatSession.student_ids?.length} students
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ minWidth: 220 }}>
                <label>Select Batch *</label>
                <SearchableSelect
                  options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
                  value={selectedBatch}
                  onChange={v=>{setSelectedBatch(v);setTopicName('');setFacultyName('');}}
                  placeholder="- Select a Batch -"
                  searchPlaceholder="Search batch..."
                />
              </div>
              <div className="form-group">
                <label>Attendance Date</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => changeDate(-1)}><ChevronLeft size={14} /></button>
                  <DateInput value={date} onChange={e => { setDate(e.target.value); setTopicName(''); setFacultyName(''); }} style={{ width: 160 }} />
                  <button className="btn btn-secondary btn-sm" onClick={() => changeDate(1)}><ChevronRight size={14} /></button>
                </div>
                
              </div>
              <div className="form-group" style={{minWidth:140}}>
                <label>Day Type</label>
                <select value={attSessionType} onChange={e=>setAttSessionType(e.target.value)} style={{width:140}}>
                  <option value="regular">Regular Class</option>
                  <option value="holiday">Holiday</option>
                  <option value="no_class">No Class</option>
                </select>
              </div>
              {attSessionType!=='regular' && selectedBatch && canMark && (
                <div className="form-group" style={{alignSelf:'flex-end'}}>
                  <button className="btn btn-primary" onClick={saveHoliday} disabled={saving}
                    style={{background:'#f59e0b',border:'none',whiteSpace:'nowrap'}}>
                    {saving?'Saving...':`Mark ${attSessionType==='holiday'?'Holiday':'No Class'} — ${formatDate(date)}`}
                  </button>
                </div>
              )}
              <div className="form-group" style={{ minWidth: 220 }}>
                <label>Topic (Session Topic) *</label>
                <select value={topicName} onChange={e => { const tn=e.target.value; setTopicName(tn); loadRepeatSession(selectedBatch, date, tn); if(tn&&students.length>0){ supabase.from('attendance').select('faculty_name').in('student_id',students.map(s=>s.id)).eq('topic_name',tn).not('faculty_name','is',null).order('date',{ascending:false}).limit(1).then(({data})=>{ if(data?.[0]?.faculty_name) setFacultyName(data[0].faculty_name); }); } }} style={{ width: 220, borderColor: !topicName ? 'var(--amber)' : undefined }}>
                  <option value="">- Select Topic (Required) -</option>
                  {filteredTopics.map(t => (
                    <option key={t.id} value={t.topic_name}>
                      {t.short_code ? `${t.short_code} - ${t.topic_name}` : t.topic_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ minWidth: 200 }}>
                <label>Faculty / Trainer</label>
                <select value={facultyName} onChange={e => setFacultyName(e.target.value)} style={{ width: 200 }}>
                  <option value="">- Select Faculty -</option>
                  {facultyList.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
          </div>

          {!selectedBatch && (
            <div className="empty-state"><Users /><h3>Select a Batch</h3><p>Choose a batch to load students</p></div>
          )}
          {selectedBatch && students.length === 0 && (
            <div className="empty-state"><Users /><h3>No Students in this Batch</h3></div>
          )}
          {selectedBatch && students.length > 0 && !topicName && (
            <div style={{ background: 'rgba(245,158,11,0.1)', border: '1.5px solid var(--amber)', borderRadius: 10, padding: '20px 24px', textAlign: 'center', marginTop: 8 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--amber)', marginBottom: 4 }}>Please Select a Topic First</div>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>You must select a topic before marking attendance</div>
            </div>
          )}

          {selectedBatch && students.length > 0 && topicName && attSessionType === 'regular' && (
            <>
              {/* Mark all controls */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div className="search-bar" style={{ width: 220 }}>
                  <Search /><input placeholder="Search student..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                {packageOptions.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Course package:</label>
                    <select value={pkgFilter} onChange={e => setPkgFilter(e.target.value)} style={{ fontSize: 13, maxWidth: 200 }}>
                      <option value="">All packages ({students.filter(inCourse).length})</option>
                      {packageOptions.map(p => (
                        <option key={p} value={p}>{p} ({students.filter(s => inCourse(s) && s.course_name === p).length})</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', whiteSpace: 'nowrap' }}>No of Sessions:</label>
                  <select value={numSessions} onChange={e => { setNumSessions(Number(e.target.value)); setAttendance({}); }} style={{ width: 70, fontSize: 13 }}>
                    {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>Mark all:</span>
                {SESSIONS.map(sess => (
                  <button key={sess.key} className="btn btn-sm btn-secondary"
                    onClick={() => markAll(sess.key, 'present')}
                    style={{ fontSize: 11 }}>
                    {sess.label} ✓
                  </button>
                ))}
                <button className="btn btn-sm btn-secondary" style={{ fontSize: 11 }} onClick={() => markAllSessions('present')}>All Present</button>
                <button className="btn btn-sm btn-danger"    style={{ fontSize: 11 }} onClick={() => markAllSessions('absent')}>All Absent</button>
                <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>✓ {totalPresent} Present</span>
                <span style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600 }}>✗ {totalAbsent} Absent</span>
                {attRepeatSession?.date === date && (
                  <span style={{fontSize:11,background:'#fef9c3',color:'#854d0e',border:'1px solid #fde68a',borderRadius:4,padding:'2px 8px'}}>
                    🔄 Repeat session for {dmy(attRepeatSession.originalDate)}
                    &nbsp;— Mark who attended
                  </span>
                )}
                {canMark && totalAbsent > 0 && totalPresent > 0 && (
                  <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'var(--amber)'}}
                    onClick={()=>{
                      const absentIds = students.filter(s => (!pkgFilter || s.course_name === pkgFilter) && (!s.course_end_date || s.course_end_date >= date)).filter(s => {
                        const a = attendance[s.id];
                        if (!a) return false; // Only count if attendance was actually saved
                        let hasPresent = false;
                        ALL_SESSIONS.slice(0, numSessions).forEach(sess => {
                          if (a[sess.key] === 'present') hasPresent = true;
                        });
                        return !hasPresent;
                      });
                      if (!absentIds.length) { alert('No absent students found for this date.'); return; }
                      setAttRepeatModal({originalDate: date, topicName, absentStudents: absentIds});
                      setAttRepeatDate('');
                    }}>
                    🔄 Take Repeat for {totalAbsent} absent
                  </button>
                )}
              </div>

              <style>{`
                .att-scroll-mirror::-webkit-scrollbar { height: 18px; }
                .att-scroll-mirror::-webkit-scrollbar-track { background: var(--bg2); }
                .att-scroll-mirror::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 8px; border: 3px solid var(--bg2); }
                .att-scroll-mirror { scrollbar-color: var(--accent) var(--bg2); scrollbar-width: auto; }
              `}</style>

{/* scroll sync handled by onScroll */}
              <div className="table-wrap" id="att_main_table_wrap"
                onScroll={e=>{const m=document.getElementById('att_top_scroll_mirror');if(m)m.scrollLeft=e.target.scrollLeft;}}>
                
              <table>
                  <thead style={{position:'sticky',top:0,zIndex:5,background:'var(--bg)'}}>
                    <tr>
                      <th>#</th>
                      <th>Reg No.</th>
                      <th>Student Name</th>
                      {SESSIONS.map(s => (
                        <th key={s.key} style={{ textAlign: 'center', fontSize: 11 }}>{s.label}</th>
                      ))}
                      <th style={{ textAlign: 'center' }}>Sessions</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.length === 0 ? (
                      <tr><td colSpan={4 + numSessions}><div className="empty-state"><p>No students match</p></div></td></tr>
                    ) : filteredStudents.map((s, i) => {
                      const a   = attendance[s.id] || emptyAtt(numSessions);
                      const dv  = dayValue(s.id);
                      const ps  = presentSessions(s.id);
                      return (
                        <tr key={s.id} style={{ background: ps === numSessions ? 'rgba(34,197,94,0.04)' : ps > 0 ? 'rgba(245,158,11,0.04)' : undefined }}>
                          <td style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{s.register_number}</td>
                          <td style={{ fontWeight: 500 }}>{s.full_name}</td>
                          {SESSIONS.map(sess => (
                            <td key={sess.key} style={{ textAlign: 'center' }}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: canMark ? 'pointer' : 'default' }}>
                                <input
                                  type="checkbox"
                                  checked={a[sess.key] === 'present'}
                                  onChange={e => canMark && setSession(s.id, sess.key, e.target.checked ? 'present' : 'absent')}
                                  disabled={!canMark}
                                />
                                <span style={{ fontSize: 11, color: a[sess.key] === 'present' ? 'var(--green)' : 'var(--text3)' }}>
                                  {a[sess.key] === 'present' ? (a._session_type==='repeat'?'R':'P') : 'A'}
                                </span>
                              </label>
                            </td>
                          ))}
                          <td style={{ textAlign: 'center', fontWeight: 600, fontSize: 13 }} title={a._session_type==='repeat'?'Repeat session on '+a._repeat_date:''}>
                            <span style={{ color: ps === numSessions ? 'var(--green)' : ps > 0 ? 'var(--amber)' : 'var(--red)' }}>
                              {ps}/{numSessions}
                            </span>
                            {attendance[s.id]?._session_type==='repeat'&&(
                              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:1}}>
                                <span style={{fontSize:9,background:'#fef9c3',color:'#854d0e',borderRadius:3,padding:'0 3px'}}
                                  title={'Repeat on '+(attendance[s.id]?._repeat_date||'')}>🔄R</span>
                                {canMark&&<span style={{fontSize:8,color:'var(--red)',cursor:'pointer',textDecoration:'underline'}}
                                  onClick={()=>removeRepeatTag(s.id)}>remove</span>}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`badge ${ps === numSessions ? 'badge-green' : ps > 0 ? 'badge-amber' : 'badge-red'}`}>
                              {ps === numSessions ? 'Full' : ps === 0 ? 'Absent' : `${ps} Session${ps > 1 ? 's' : ''}`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>{/* end table-wrap */}

              {canMark && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' }}>
                  {topicName && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Topic: <strong>{topicName}</strong></span>}
                  <button className="btn btn-danger btn-sm" onClick={clearAttendance} disabled={saving}
                    style={{marginRight:8}} title="Delete all attendance for this date">
                    🗑️ Clear
                  </button>
                  {attSessionType==='regular'
                    ? <button className="btn btn-primary" onClick={saveAttendance} disabled={saving || !topicName}>
                        <Save size={14} />
                        {saving ? 'Saving...' : `Save Attendance - ${formatDate(date)}`}
                      </button>
                    : <button className="btn btn-primary" onClick={saveHoliday} disabled={saving}
                        style={{background:'#f59e0b',border:'none'}}>
                        {saving ? 'Saving...' : `Mark ${attSessionType==='holiday'?'Holiday':'No Class'} - ${formatDate(date)}`}
                      </button>
                  }
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Repeat Session Modal */}
      {attRepeatModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setAttRepeatModal(null)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-header">
              <span className="modal-title">🔄 Schedule Repeat Session</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setAttRepeatModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{marginBottom:12,fontSize:13}}>
                Original date: <strong>{dmy(attRepeatModal.originalDate)}</strong>
                {attRepeatModal.topicName && <> | Topic: <strong>{attRepeatModal.topicName}</strong></>}
                &nbsp;| <strong>{attRepeatModal.absentStudents.length} absent students</strong>
              </p>
              <div style={{marginBottom:12}}>
                <label style={{fontWeight:500,fontSize:13,display:'block',marginBottom:4}}>Repeat Session Date:</label>
                <DateInput value={attRepeatDate} onChange={e=>setAttRepeatDate(e.target.value)}
                  style={{fontSize:13,padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',width:'100%'}}/>
              </div>
              <div style={{maxHeight:200,overflowY:'auto',background:'var(--surface2)',borderRadius:8,padding:8}}>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>Students in repeat session:</div>
                {attRepeatModal.absentStudents.map(s=>(
                  <div key={s.id} style={{fontSize:12,padding:'2px 0'}}>{s.register_number} — {s.full_name}</div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setAttRepeatModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!attRepeatDate} onClick={saveRepeatAttendance}>
                ✓ Create Repeat Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ABSENT TRACKER ── */}
      {activeTab === 'absent_tracker' && (
        <div>
          <div className="card" style={{marginBottom:16,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <SearchableSelect value={trackerBatch} onChange={v=>{setTrackerBatch(v||'');setTrackerData(null);}}
              options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
              placeholder="Select Batch" searchPlaceholder="Search batch..." style={{minWidth:180}}/>
            <SearchableSelect value={trackerTopic} onChange={v=>{setTrackerTopic(v||'');setTrackerData(null);}}
              options={trackerBatch ? (trackerTopicList || []).map(t=>({value:t.name,label:t.label})) : []}
              placeholder={!trackerBatch?"Select batch first":trackerTopicList===null?"Loading topics...":trackerTopicList.length===0?"No classes recorded yet":"Select Topic"} searchPlaceholder="Search topic..." style={{minWidth:200}}/>
            <button className="btn btn-primary btn-sm" onClick={loadTrackerData} disabled={!trackerBatch||!trackerTopic||trackerLoading}>
              {trackerLoading?'Loading...':'📋 Load'}
            </button>
            {trackerBatch && (
              <button className="btn btn-ghost btn-sm" style={{fontSize:12}}
                onClick={()=>setShowBatchSummary(true)}>
                📊 Absence Summary
              </button>
            )}
            {trackerBatch && canTrk && (
              <button className="btn btn-sm" style={{fontSize:12,background:'#f59e0b',color:'#fff',border:'none'}}
                onClick={()=>setShowLeaveModal(true)}>
                🏥 Mark Leave Period
              </button>
            )}
            {trackerBatch && canTrk && (
              <button className="btn btn-sm" style={{fontSize:12,background:'#6366f1',color:'#fff',border:'none'}}
                onClick={()=>setShowOnlineModal(true)}>
                🖥️ Mark Online Period
              </button>
            )}
            {trackerBatch && (
              <button className="btn btn-sm" style={{fontSize:12,background:'#0ea5e9',color:'#fff',border:'none'}}
                onClick={()=>{ setShowOnlineTracker(true); setOnlineTrackerBatch(trackerBatch); loadOnlineTrackerData(trackerBatch); }}>
                📹 Online Sessions
              </button>
            )}
            {/* Active leave periods banner */}
            {leavePeriods.length>0 && (
              <div style={{width:'100%',marginTop:8,display:'flex',gap:6,flexWrap:'wrap'}}>
                {leavePeriods.map(lp=>{
                  const stu = trackerBatchStudents.find(s=>s.id===lp.student_id);
                  return (
                    <div key={lp.id} style={{background:'#fef3c7',border:'1px solid #d97706',borderRadius:8,padding:'4px 10px',fontSize:11,display:'flex',gap:6,alignItems:'center'}}>
                      <span>🏥</span>
                      <strong>{stu?.full_name||'Student'}</strong>
                      <span style={{color:'#92400e'}}>{dmy(lp.start_date)} → {dmy(lp.end_date)} · {lp.reason}</span>
                      <button style={{background:'#10b981',color:'#fff',border:'none',borderRadius:4,padding:'1px 7px',fontSize:10,cursor:'pointer'}}
                        onClick={()=>markLeaveReturned(lp.id, lp.student_id)}>Returned Early?</button>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Active online periods banner */}
            {onlinePeriods.length>0 && (
              <div style={{width:'100%',marginTop:4,display:'flex',gap:6,flexWrap:'wrap'}}>
                {onlinePeriods.map(op=>{
                  const stu = trackerBatchStudents.find(s=>s.id===op.student_id);
                  return (
                    <div key={op.id} style={{background:'#ede9fe',border:'1px solid #7c3aed',borderRadius:8,padding:'4px 10px',fontSize:11,display:'flex',gap:6,alignItems:'center'}}>
                      <span>🖥️</span>
                      <strong>{stu?.full_name||'Student'}</strong>
                      <span style={{color:'#5b21b6'}}>Online from {op.start_date}{op.end_date?' → '+op.end_date:' (ongoing)'}</span>
                      <button style={{background:'#10b981',color:'#fff',border:'none',borderRadius:4,padding:'1px 7px',fontSize:10,cursor:'pointer'}}
                        onClick={()=>markOnlineReturned(op.id)}>Returned Offline?</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {trackerData && (
            <div className="card">
              <div style={{fontWeight:600,fontSize:14,marginBottom:12}}>
                Absent Tracker — {trackerBatch} / {trackerTopic}
                <span style={{fontWeight:400,fontSize:12,color:'var(--text3)',marginLeft:8}}>
                  {trackerData.dates.length} days | {trackerData.students.length} students
                </span>
                <button className="btn btn-ghost btn-sm" style={{marginLeft:12,fontSize:12}} onClick={printAbsentTracker}>🖨️ Print Report</button>
              </div>
              <table style={{width:'100%',borderCollapse:'separate',borderSpacing:0,fontSize:12}}>
                <thead style={{position:'sticky',top:0,zIndex:20,background:'#e8f0fe'}}>
                  <tr>
                    <th style={{padding:'6px 8px',textAlign:'left',background:'#e8f0fe',minWidth:40,position:'sticky',top:0,borderBottom:'2px solid #93c5fd'}}>#</th>
                    <th style={{padding:'6px 8px',textAlign:'left',background:'#e8f0fe',minWidth:80,position:'sticky',top:0,borderBottom:'2px solid #93c5fd'}}>Reg No</th>
                    <th style={{padding:'6px 8px',textAlign:'left',background:'#e8f0fe',minWidth:140,position:'sticky',top:0,borderBottom:'2px solid #93c5fd'}}>Name</th>
                    {trackerData.dates.map(d=>(
                      <th key={d} style={{padding:'4px 6px',textAlign:'center',background:'#e8f0fe',minWidth:60,cursor:'pointer',whiteSpace:'nowrap',position:'sticky',top:0,borderBottom:'2px solid #93c5fd'}}
                        onClick={()=>{
                          const newDate = trackerSelDate===d?null:d;
                          setTrackerSelDate(newDate);
                          if(newDate){
                            const abs = trackerData.students.filter(s=>!trackerData.grid[s.id]?.[newDate]||trackerData.grid[s.id][newDate].present===0);
                            loadCallLogs(newDate, trackerBatch, abs);
                            setTimeout(()=>absentTrackerRef.current?.scrollIntoView({behavior:'smooth',block:'start'}),100);
                          }
                        }}
                        title={'Click to see absent students on '+d}>
                        {dmy(d)}
                        <div style={{fontSize:9,color:'var(--red)',fontWeight:400}}>
                          {trackerData.students.filter(s=>!trackerData.grid[s.id]?.[d]||trackerData.grid[s.id][d].present===0).length}✗
                        </div>
                      </th>
                    ))}
                    <th style={{padding:'6px 8px',textAlign:'center',background:'#e8f0fe',minWidth:60,position:'sticky',top:0,borderBottom:'2px solid #93c5fd'}}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {trackerData.students.map((s,i)=>{
                    const totalSess = trackerData.dates.reduce((acc,d)=>acc+(trackerData.grid[s.id]?.[d]?.total||0),0);
                    const presentSess = trackerData.dates.reduce((acc,d)=>acc+(trackerData.grid[s.id]?.[d]?.present||0),0);
                    const pct = totalSess>0?(presentSess/totalSess*100).toFixed(0):null;
                    return (
                      <React.Fragment key={s.id}>
                      <tr style={{borderBottom:'1px solid var(--border)',background:expandedStudent===s.id?'var(--accent-light)':i%2===0?'':'var(--surface2)',cursor:'pointer'}}
                        onClick={async()=>{
                          if(expandedStudent===s.id){setExpandedStudent(null);return;}
                          setExpandedStudent(s.id);
                          const tp = trackerTopic||'';
          const {data} = await supabase.from('attendance_absence_calls')
                            .select('*').eq('student_id',s.id).eq('batch_name',trackerBatch)
                            .or(`topic_name.eq.${tp},topic_name.is.null`)
                            .order('absent_date',{ascending:false});
                          setStudentCallHistory(data||[]);
                        }}>
                        <td style={{padding:'4px 8px',color:'var(--text3)'}}>{i+1}</td>
                        <td style={{padding:'4px 8px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                        <td style={{padding:'4px 8px',fontWeight:500}}>{s.full_name}</td>
                        {trackerData.dates.map(d=>{
                          const rec = trackerData.grid[s.id]?.[d];
                          const isRepeat = rec?.session_type==='repeat';
                          const onlineDay = isOnline(s.id, d);
                          const leaveDay = !onlineDay && isOnLeave(s.id, d);
                          if (!rec) return <td key={d} style={{textAlign:'center',padding:'4px 6px',background:'#f3f4f6',color:'#9ca3af'}}>—</td>;
                          if (onlineDay) return <td key={d} style={{textAlign:'center',padding:'4px 6px',background:'#ede9fe',color:'#5b21b6',fontWeight:600,fontSize:10}}>🖥️</td>;
                          if (leaveDay) return <td key={d} style={{textAlign:'center',padding:'4px 6px',background:'#fef3c7',color:'#92400e',fontWeight:600,fontSize:10}}>🏥</td>;
                          const bg = rec.present===rec.total?(isRepeat?'#fef9c3':'#dcfce7'):rec.present>0?'#fef9c3':'#fee2e2';
                          const col = rec.present===rec.total?(isRepeat?'#854d0e':'#15803d'):rec.present>0?'#854d0e':'#dc2626';
                          return <td key={d} style={{textAlign:'center',padding:'4px 6px',background:bg,color:col,fontWeight:600,fontSize:11}}>
                            {rec.present}/{rec.total}{isRepeat?' 🔄':''}
                          </td>;
                        })}
                        <td style={{textAlign:'center',padding:'4px 8px',fontWeight:700,
                          color:pct===null?'var(--text3)':pct>=75?'var(--green)':pct>=50?'var(--amber)':'var(--red)'}}>
                          {pct===null?'—':pct+'%'}
                        </td>
                      </tr>
                      {expandedStudent===s.id && (
                        <tr><td colSpan={trackerData.dates.length+4} style={{padding:0,background:'var(--accent-light)',borderBottom:'2px solid var(--border)'}}>
                          <StudentAbsenceDetail
                            canEditLog={ownBatch(trackerBatch)}
                            student={s}
                            batch={trackerBatch}
                            topic={trackerTopic}
                            callHistory={studentCallHistory}
                            setCallHistory={setStudentCallHistory}
                            callLogStaff={callLogStaff}
                            supabase={supabase}
                            trackerData={trackerData}
                          />
                        </td></tr>
                      )}

                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {/* Absent students for selected date */}
              {trackerSelDate && (()=>{
                const absentStudents = trackerData.students.filter(s=>!trackerData.grid[s.id]?.[trackerSelDate]||trackerData.grid[s.id][trackerSelDate].present===0);
                return (
                  <div ref={absentTrackerRef} style={{marginTop:16,padding:12,background:'#fee2e2',borderRadius:8,border:'1px solid #fca5a5'}}>
                    <div style={{fontWeight:600,fontSize:13,marginBottom:8,color:'#991b1b'}}>
                      ✗ Absent on {dmy(trackerSelDate)} — {absentStudents.length} students
                      {canTrk && <button className="btn btn-sm btn-primary" style={{marginLeft:12,fontSize:11}}
                        onClick={async()=>{
                          if (selectedBatch !== trackerBatch) {
                            setSelectedBatch(trackerBatch);
                            await new Promise(r => setTimeout(r, 800));
                          }
                          setTopicName(trackerTopic);
                          setAttRepeatModal({originalDate:trackerSelDate,topicName:trackerTopic,absentStudents});
                          setAttRepeatDate('');
                          setDate(trackerSelDate);
                          setActiveTab('mark');
                        }}>🔄 Schedule Repeat</button>}
                    </div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12}}>
                      {absentStudents.map(s=>(
                        <span key={s.id} style={{fontSize:11,padding:'2px 8px',background:'#fff',borderRadius:4,border:'1px solid #fca5a5'}}>
                          {s.register_number} {s.full_name}
                          {callLogs[s.id]?.call_status && callLogs[s.id].call_status!=='Not Called' && <span style={{marginLeft:4}}>✓</span>}
                        </span>
                      ))}
                    </div>
                    {/* Call Log Table */}
                    <div style={{marginTop:8}}>
                      <div style={{fontWeight:600,fontSize:12,color:'#991b1b',marginBottom:8}}>📞 Call Log</div>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                        <thead><tr style={{background:'#fecaca'}}>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Student</th>
                          <th style={{padding:'6px 8px',textAlign:'left',minWidth:140}}>Status</th>
                          <th style={{padding:'6px 8px',textAlign:'left',minWidth:140}}>Called By</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Call Date</th>
                          <th style={{padding:'6px 8px',textAlign:'left',minWidth:160}}>Reason</th>
                          <th style={{padding:'6px 8px',textAlign:'left',minWidth:140}}>Remarks</th>
                          <th style={{padding:'6px 8px'}}></th>
                        </tr></thead>
                        <tbody>
                        {absentStudents.map((s,i)=>{
                          const log = callLogs[s.id]||{};
                          const status = log.call_status||'Not Called';
                          const statusDot = {'Not Called':'🔴','Called - No Answer':'🟡','Called - Reason Given':'🟢','Called - Will Come':'🟢','Called - Personal Issue':'🟣','Called - Health Issue':'🟣','Called - Other':'⚪'}[status]||'🔴';
                          const onLeave = isOnLeave(s.id, trackerSelDate);
                          const leaveRec = onLeave ? getLeave(s.id, trackerSelDate) : null;
                          const online = !onLeave && isOnline(s.id, trackerSelDate);
                          const onlineRec = online ? getOnlinePeriod(s.id, trackerSelDate) : null;
                          if (onLeave) return (
                            <tr key={s.id} style={{borderBottom:'1px solid #fecaca',background:'#fffbeb'}}>
                              <td style={{padding:'6px 8px'}}>
                                <div style={{fontWeight:600,fontSize:12}}>{s.full_name}</div>
                                <div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{s.register_number}</div>
                              </td>
                              <td colSpan={5} style={{padding:'6px 8px'}}>
                                <span style={{background:'#fef3c7',color:'#92400e',border:'1px solid #d97706',borderRadius:8,padding:'2px 10px',fontSize:11,fontWeight:600}}>
                                  🏥 On Leave — {leaveRec?.reason} ({leaveRec?.start_date} to {leaveRec?.end_date})
                                </span>
                                {leaveRec?.notes && <span style={{fontSize:11,color:'#78350f',marginLeft:8}}>{leaveRec.notes}</span>}
                              </td>
                              <td colSpan={2} style={{padding:'6px 8px',fontSize:11,color:'var(--text3)'}}>No call needed</td>
                            </tr>
                          );
                          if (online) return (
                            <tr key={s.id} style={{borderBottom:'1px solid #c4b5fd',background:'#f5f3ff'}}>
                              <td style={{padding:'6px 8px'}}>
                                <div style={{fontWeight:600,fontSize:12}}>{s.full_name}</div>
                                <div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{s.register_number}</div>
                              </td>
                              <td colSpan={5} style={{padding:'6px 8px'}}>
                                <span style={{background:'#ede9fe',color:'#5b21b6',border:'1px solid #7c3aed',borderRadius:8,padding:'2px 10px',fontSize:11,fontWeight:600}}>
                                  🖥️ Online Batch — attending recorded sessions
                                </span>
                                {onlineRec?.notes && <span style={{fontSize:11,color:'#5b21b6',marginLeft:8}}>{onlineRec.notes}</span>}
                                <span style={{fontSize:10,color:'#7c3aed',marginLeft:8}}>Since {onlineRec?.start_date}</span>
                              </td>
                              <td colSpan={2} style={{padding:'6px 8px',fontSize:11,color:'var(--text3)'}}>No call needed</td>
                            </tr>
                          );
                          return (
                            <tr key={s.id} style={{borderBottom:'1px solid #fecaca',background:i%2===0?'#fff':'#fff7f7'}}>
                              <td style={{padding:'6px 8px'}}>
                                <div style={{fontWeight:600,fontSize:12}}>{s.full_name}</div>
                                <div style={{fontSize:10,color:'var(--text3)',fontFamily:'monospace'}}>{s.register_number}</div>
                              </td>
                              <td style={{padding:'4px 8px'}}>
                                <select value={status} onChange={e=>updateCallLog(s.id,'call_status',e.target.value)}
                                  style={{fontSize:11,padding:'3px 6px',maxWidth:150}}>
                                  {['Not Called','Called - No Answer','Called - Reason Given','Called - Will Come','Called - Personal Issue','Called - Health Issue','Called - Other'].map(st=><option key={st} value={st}>{st}</option>)}
                                </select>
                              </td>
                              <td style={{padding:'4px 8px'}}>
                                <select value={log.called_by||''} onChange={e=>updateCallLog(s.id,'called_by',e.target.value)}
                                  style={{fontSize:11,padding:'3px 6px',maxWidth:150}}>
                                  <option value=''>Select caller</option>
                                  {callLogStaff.map(st=><option key={st.id} value={st.id}>{st.staff_name}</option>)}
                                </select>
                              </td>
                              <td style={{padding:'4px 8px'}}>
                                <DateInput value={log.call_date||trackerSelDate||''}
                                  onChange={e=>updateCallLog(s.id,'call_date',e.target.value)}
                                  style={{fontSize:11,padding:'3px 6px',maxWidth:120}}/>
                              </td>
                              <td style={{padding:'4px 8px'}}>
                                <input value={log.reason||''} onChange={e=>updateCallLog(s.id,'reason',e.target.value)}
                                  placeholder='Reason...' style={{fontSize:11,padding:'3px 6px',width:'100%'}}/>
                              </td>
                              <td style={{padding:'4px 8px'}}>
                                <input value={log.remarks||''} onChange={e=>updateCallLog(s.id,'remarks',e.target.value)}
                                  placeholder='Remarks...' style={{fontSize:11,padding:'3px 6px',width:'100%'}}/>
                              </td>
                              <td style={{padding:'4px 8px',textAlign:'center'}}>
                                <button className='btn btn-primary btn-sm' style={{fontSize:10}}
                                  disabled={callLogSaving[s.id]}
                                  onClick={()=>saveCallLog(s,trackerSelDate,trackerBatch)}>
                                  {callLogSaving[s.id]?'...':'Save'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Call History ── */}
          {/* Leave Period Modal */}
          {showLeaveModal && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <div style={{background:'var(--bg)',borderRadius:12,padding:24,width:480,boxShadow:'0 8px 32px rgba(0,0,0,0.2)',maxHeight:'90vh',overflowY:'auto'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                  <span style={{fontWeight:700,fontSize:15}}>🏥 Mark Leave Period</span>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setShowLeaveModal(false)}>✕</button>
                </div>
                <div className="form-grid">
                  <div className="form-group full">
                    <label>Student *</label>
                    <SearchableSelect
                      options={trackerBatchStudents.map(s=>({value:s.id, label:`${s.register_number} — ${s.full_name}`}))}
                      value={leaveForm.student_id}
                      onChange={v=>setLeaveForm(f=>({...f,student_id:v||''}))}
                      placeholder="Select student..."
                      searchPlaceholder="Search by name or reg no..."
                    />
                  </div>
                  <div className="form-group">
                    <label>From Date *</label>
                    <DateInput value={leaveForm.start_date} onChange={e=>setLeaveForm(f=>({...f,start_date:e.target.value}))}/>
                  </div>
                  <div className="form-group">
                    <label>To Date (Expected Return) *</label>
                    <DateInput value={leaveForm.end_date} onChange={e=>setLeaveForm(f=>({...f,end_date:e.target.value}))}/>
                  </div>
                  <div className="form-group">
                    <label>Reason *</label>
                    <select value={leaveForm.reason} onChange={e=>setLeaveForm(f=>({...f,reason:e.target.value}))}>
                      <option value="">Select reason...</option>
                      {LEAVE_REASONS.map(r=><option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Approved By</label>
                    <select value={leaveForm.approved_by} onChange={e=>setLeaveForm(f=>({...f,approved_by:e.target.value}))}>
                      <option value="">Select staff...</option>
                      {callLogStaff.map(st=><option key={st.id} value={st.staff_name}>{st.staff_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group full">
                    <label>Notes</label>
                    <textarea value={leaveForm.notes} onChange={e=>setLeaveForm(f=>({...f,notes:e.target.value}))} rows={2} placeholder="Any details about the leave..."/>
                  </div>
                </div>
                <div style={{background:'#fef3c7',border:'1px solid #d97706',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#92400e',marginBottom:12}}>
                  ℹ️ Past attendance within this date range will be auto-marked as <strong>absent</strong>. Future days will be marked absent as attendance is entered. If the student returns early, use the "Returned Early?" button in the tracker.
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-primary btn-sm" disabled={leaveSaving} onClick={saveLeave}>{leaveSaving?'Saving...':'Save Leave Period'}</button>
                  <button className="btn btn-secondary btn-sm" onClick={()=>setShowLeaveModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Online Period Modal */}
          {showOnlineModal && (
            <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowOnlineModal(false)}>
              <div className="modal" style={{maxWidth:500}}>
                <div className="modal-header">
                  <span style={{fontWeight:700,fontSize:15}}>🖥️ Mark Online Period</span>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setShowOnlineModal(false)}>✕</button>
                </div>
                <div className="modal-body">
                  <div className="form-grid" style={{gridTemplateColumns:'1fr 1fr',gap:12}}>
                    <div className="form-group full">
                      <label>Student *</label>
                      <SearchableSelect
                        options={trackerBatchStudents.map(s=>({value:s.id, label:`${s.register_number} — ${s.full_name}`}))}
                        value={onlineForm.student_id}
                        onChange={v=>{ setOnlineForm(f=>({...f,student_id:v||''})); loadOnlineStudentSummary(v||''); }}
                        placeholder="Select student..."
                        searchPlaceholder="Search by name or reg no..."
                      />
                    </div>
                    <div className="form-group">
                      <label>From Date *</label>
                      <DateInput value={onlineForm.start_date} onChange={e=>setOnlineForm(f=>({...f,start_date:e.target.value}))}/>
                    </div>
                    <div className="form-group">
                      <label>Expected Return (optional)</label>
                      <DateInput value={onlineForm.end_date} onChange={e=>setOnlineForm(f=>({...f,end_date:e.target.value}))}/>
                    </div>
                    <div className="form-group full">
                      <label>Notes</label>
                      <textarea value={onlineForm.notes} onChange={e=>setOnlineForm(f=>({...f,notes:e.target.value}))} rows={2} placeholder="Reason for shifting to online..."/>
                    </div>
                  </div>
                  {/* Student summary panel */}
                  {onlineStudentSummaryLoading && (
                    <div style={{textAlign:'center',padding:16,color:'var(--text3)',fontSize:12}}>Loading student summary...</div>
                  )}
                  {onlineStudentSummary && !onlineStudentSummaryLoading && (
                    <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:8,padding:12,marginBottom:12}}>
                      {/* Duplicate warning */}
                      {onlineStudentSummary.alreadyOnline && (
                        <div style={{background:'#fef3c7',border:'1px solid #d97706',borderRadius:6,padding:'6px 10px',fontSize:12,color:'#92400e',marginBottom:10,fontWeight:600}}>
                          ⚠️ This student is already marked as Online. Saving will create a second online period.
                        </div>
                      )}
                      {/* Overall % */}
                      <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:10}}>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:22,fontWeight:800,color:Number(onlineStudentSummary.pct)>=75?'#15803d':Number(onlineStudentSummary.pct)>=50?'#d97706':'#dc2626'}}>
                            {onlineStudentSummary.pct}%
                          </div>
                          <div style={{fontSize:10,color:'var(--text3)'}}>Overall Attendance</div>
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>{onlineStudentSummary.presentSess} sessions present / {onlineStudentSummary.totalSess} total</div>
                          <div style={{background:'#e2e8f0',borderRadius:4,height:8,overflow:'hidden'}}>
                            <div style={{width:`${onlineStudentSummary.pct}%`,height:'100%',background:Number(onlineStudentSummary.pct)>=75?'#22c55e':Number(onlineStudentSummary.pct)>=50?'#f59e0b':'#ef4444',borderRadius:4,transition:'width 0.3s'}}/>
                          </div>
                        </div>
                      </div>
                      {/* Topic-wise */}
                      {onlineStudentSummary.topicStats.length>0 && (
                        <div style={{maxHeight:150,overflowY:'auto'}}>
                          <div style={{fontSize:11,fontWeight:600,color:'var(--text2)',marginBottom:4}}>Topic-wise Attendance ({onlineStudentSummary.topicStats.length} topics)</div>
                          <table style={{width:'100%',fontSize:11,borderCollapse:'collapse'}}>
                            <thead>
                              <tr style={{background:'#e2e8f0'}}>
                                <th style={{padding:'3px 6px',textAlign:'left'}}>Topic</th>
                                <th style={{padding:'3px 6px',textAlign:'center'}}>Present</th>
                                <th style={{padding:'3px 6px',textAlign:'center'}}>Total</th>
                                <th style={{padding:'3px 6px',textAlign:'center'}}>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {onlineStudentSummary.topicStats.map(t=>(
                                <tr key={t.topic} style={{borderBottom:'1px solid #f1f5f9'}}>
                                  <td style={{padding:'3px 6px'}}>{t.topic}</td>
                                  <td style={{padding:'3px 6px',textAlign:'center'}}>{t.present}</td>
                                  <td style={{padding:'3px 6px',textAlign:'center'}}>{t.total}</td>
                                  <td style={{padding:'3px 6px',textAlign:'center',fontWeight:700,
                                    color:Number(t.pct)>=75?'#15803d':Number(t.pct)>=50?'#d97706':'#dc2626'}}>
                                    {t.pct}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {onlineStudentSummary.topicStats.length===0 && (
                        <div style={{fontSize:11,color:'var(--text3)'}}>No attendance records found for this student.</div>
                      )}
                    </div>
                  )}
                  <div style={{background:'#ede9fe',border:'1px solid #7c3aed',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#5b21b6',marginBottom:12}}>
                    ℹ️ This student will show as <strong>🖥️ Online</strong> in the absent tracker. Use <strong>"📹 Online Sessions"</strong> to track their recorded video progress.
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-primary btn-sm" disabled={onlineSaving} onClick={saveOnlinePeriod} style={{background:'#6366f1',border:'none'}}>{onlineSaving?'Saving...':'Save Online Period'}</button>
                    <button className="btn btn-secondary btn-sm" onClick={()=>setShowOnlineModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Online Sessions Tracker Modal */}
          {showOnlineTracker && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',flexDirection:'column'}}>
              <div style={{background:'var(--bg)',flex:1,overflow:'auto',padding:24,margin:24,borderRadius:12,boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                  <span style={{fontWeight:700,fontSize:16}}>📹 Online Sessions Tracker — {onlineTrackerBatch}</span>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:16}} onClick={()=>setShowOnlineTracker(false)}>✕ Close</button>
                </div>
                {/* Controls — no topic filter needed anymore */}
                <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap',alignItems:'flex-end'}}>
                  <button className="btn btn-primary btn-sm" onClick={loadOnlineTrackerData} disabled={onlineSessionsLoading} style={{background:'#0ea5e9',border:'none'}}>
                    {onlineSessionsLoading?'Loading...':'🔄 Refresh'}
                  </button>
                  <span style={{fontSize:12,color:'var(--text3)',alignSelf:'center'}}>Shows all online students and their individual topic progress</span>
                </div>
                {onlineTrackerStudents.length>0 && (
                  <div style={{display:'flex',flexDirection:'column',gap:12}}>
                    {onlineTrackerStudents.map((s,i)=>{
                      // All sessions for this student
                      const stuSessions = allOnlineSessions.filter(os=>os.student_id===s.id);
                      // Group by topic
                      const topicMap = {};
                      stuSessions.forEach(os=>{
                        if (!topicMap[os.topic_name]) topicMap[os.topic_name]={days:[],summary:null};
                        if (os.day_number===0) topicMap[os.topic_name].summary=os;
                        else topicMap[os.topic_name].days.push(os);
                      });
                      const topics = Object.entries(topicMap).sort((a,b)=>a[0].localeCompare(b[0]));
                      // Find current topic: most recent shared/pending
                      const activeTopic = topics.find(([,v])=>v.days.some(d=>d.status==='shared'))?.[0]
                        || topics.find(([,v])=>v.days.some(d=>d.status==='pending'))?.[0]
                        || topics[topics.length-1]?.[0];
                      const totalTopics = topics.length;
                      const completedTopics = topics.filter(([,v])=>v.summary?.status==='watched').length;
                      return (
                        <div key={s.id} style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:10,padding:14}}>
                          {/* Student header */}
                          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,flexWrap:'wrap',gap:8}}>
                            <div style={{display:'flex',alignItems:'center',gap:10}}>
                              <div>
                                <span style={{fontWeight:700,fontSize:14}}>{s.full_name}</span>
                                <span style={{fontSize:11,color:'var(--text3)',fontFamily:'monospace',marginLeft:8}}>{s.register_number}</span>
                              </div>
                              {activeTopic && (
                                <span style={{fontSize:11,background:'#0ea5e9',color:'#fff',borderRadius:4,padding:'2px 8px'}}>
                                  📍 {activeTopic}
                                </span>
                              )}
                              <span style={{fontSize:11,color:'var(--text3)'}}>
                                {completedTopics}/{totalTopics} topics done
                              </span>
                            </div>
                            <button
                              onClick={()=>loadOnlineStudentDashboard(s)}
                              style={{fontSize:11,padding:'3px 10px',background:'#6366f1',color:'#fff',border:'none',borderRadius:5,cursor:'pointer'}}
                            >🗂 Full Journey</button>
                          </div>
                          {/* Per-topic rows */}
                          {topics.length===0 && (
                            <div style={{fontSize:12,color:'var(--text3)',fontStyle:'italic'}}>No sessions recorded yet. Use Share Next to start.</div>
                          )}
                          {topics.map(([topic, tData])=>{
                            const days = tData.days.sort((a,b)=>a.day_number-b.day_number);
                            const maxDay = days.length>0?Math.max(...days.map(d=>d.day_number)):0;
                            // infer total days from existing records or default
                            const totalDays = maxDay || onlineTopicSessions;
                            const watched = days.filter(d=>d.status==='watched').length;
                            const pct = totalDays>0?(watched/totalDays*100).toFixed(0):0;
                            const summSess = tData.summary;
                            const isDone = summSess?.status==='watched';
                            return (
                              <div key={topic} style={{background:'#fff',borderRadius:7,border:'1px solid #e0f2fe',padding:'8px 12px',marginBottom:6}}>
                                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                                  <span style={{fontWeight:600,fontSize:12,minWidth:160}}>{topic}</span>
                                  {/* Progress bar */}
                                  <div style={{flex:1,minWidth:100}}>
                                    <div style={{background:'#e2e8f0',borderRadius:3,height:6,overflow:'hidden'}}>
                                      <div style={{width:`${pct}%`,height:'100%',background:isDone?'#22c55e':Number(pct)>0?'#f59e0b':'#e2e8f0',borderRadius:3,transition:'width 0.3s'}}/>
                                    </div>
                                  </div>
                                  <span style={{fontSize:11,fontWeight:700,color:isDone?'#15803d':Number(pct)>0?'#d97706':'#6b7280',minWidth:60}}>
                                    {watched}/{totalDays} {isDone?'✅':''}
                                  </span>
                                  {/* Day pills */}
                                  <div style={{display:'flex',gap:3,flexWrap:'wrap'}}>
                                    {Array.from({length:totalDays},(_,di)=>{
                                      const day=di+1;
                                      const sess=days.find(d=>d.day_number===day);
                                      const st=sess?.status||'pending';
                                      const emoji=st==='watched'?'✅':st==='shared'?'📤':st==='missed'?'❌':'⏳';
                                      return (
                                        <select key={day} value={st}
                                          onChange={e=>saveOnlineSessionStatus(s.id,day,e.target.value,topic)}
                                          disabled={savingOnlineSession[`${s.id}_${topic}_${day}`]}
                                          title={`Day ${day}`}
                                          style={{fontSize:10,width:36,border:'1px solid #e2e8f0',borderRadius:3,padding:'1px 2px',background:st==='watched'?'#dcfce7':st==='shared'?'#dbeafe':st==='missed'?'#fee2e2':'#f3f4f6',cursor:'pointer'}}>
                                          <option value='pending'>⏳</option>
                                          <option value='shared'>📤</option>
                                          <option value='watched'>✅</option>
                                          <option value='missed'>❌</option>
                                        </select>
                                      );
                                    })}
                                    {/* Summary */}
                                    <select value={summSess?.status||'pending'}
                                      onChange={e=>saveOnlineSessionStatus(s.id,0,e.target.value,topic)}
                                      disabled={savingOnlineSession[`${s.id}_${topic}_0`]}
                                      title="Summary video"
                                      style={{fontSize:10,width:44,border:'1px solid #fde68a',borderRadius:3,padding:'1px 2px',background:summSess?.status==='watched'?'#dcfce7':summSess?.status==='missed'?'#fee2e2':'#fef9c3',cursor:'pointer'}}>
                                      <option value='pending'>📝⏳</option>
                                      <option value='shared'>📝📤</option>
                                      <option value='watched'>📝✅</option>
                                      <option value='missed'>📝❌</option>
                                    </select>
                                  </div>
                                  <button
                                    onClick={()=>shareNextVideo(s.id,s.full_name,topic,totalDays)}
                                    style={{fontSize:10,padding:'2px 7px',background:'#0ea5e9',color:'#fff',border:'none',borderRadius:4,cursor:'pointer',whiteSpace:'nowrap'}}
                                  >📤 Next</button>
                                </div>
                              </div>
                            );
                          })}
                          {/* Quick add topic */}
                          <div style={{marginTop:6,display:'flex',gap:6,alignItems:'center'}}>
                            <span style={{fontSize:11,color:'var(--text3)'}}>Add topic:</span>
                            <select style={{fontSize:11,padding:'2px 6px',border:'1px solid #bae6fd',borderRadius:4,background:'#fff'}}
                              defaultValue=""
                              onChange={e=>{
                                const t=e.target.value; if(!t) return;
                                saveOnlineSessionStatus(s.id,1,'pending',t);
                                e.target.value='';
                              }}>
                              <option value="">— select —</option>
                              {(allTopicNames||[]).filter(t=>!topics.find(([tp])=>tp===t)).map(t=>(
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {onlineTrackerStudents.length===0 && !onlineSessionsLoading && (
                  <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>
                    No students found in online mode for this batch. Mark students as Online first using the "🖥️ Mark Online Period" button.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Per-Student Online Dashboard Modal */}
          {onlineStudentDashboard && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <div style={{background:'var(--bg)',width:'min(700px,95vw)',maxHeight:'90vh',overflowY:'auto',borderRadius:12,boxShadow:'0 8px 40px rgba(0,0,0,0.3)',padding:24}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                  <span style={{fontWeight:700,fontSize:15}}>🗂 Online Journey — {onlineStudentDashboard.student?.full_name}</span>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setOnlineStudentDashboard(null)}>✕</button>
                </div>
                {onlineStudentDashboard.loading && (
                  <div style={{textAlign:'center',padding:32,color:'var(--text3)'}}>Loading...</div>
                )}
                {!onlineStudentDashboard.loading && (
                  <>
                    {/* Attendance summary */}
                    <div style={{display:'flex',gap:16,marginBottom:16,background:'#f0f9ff',borderRadius:8,padding:12,alignItems:'center'}}>
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:24,fontWeight:800,color:Number(onlineStudentDashboard.attPct)>=75?'#15803d':Number(onlineStudentDashboard.attPct)>=50?'#d97706':'#dc2626'}}>
                          {onlineStudentDashboard.attPct}%
                        </div>
                        <div style={{fontSize:10,color:'var(--text3)'}}>Overall Att.</div>
                      </div>
                      <div style={{flex:1}}>
                        <div style={{background:'#e2e8f0',borderRadius:4,height:8,overflow:'hidden',marginBottom:4}}>
                          <div style={{width:`${onlineStudentDashboard.attPct}%`,height:'100%',background:Number(onlineStudentDashboard.attPct)>=75?'#22c55e':Number(onlineStudentDashboard.attPct)>=50?'#f59e0b':'#ef4444',borderRadius:4}}/>
                        </div>
                        <div style={{fontSize:11,color:'var(--text3)'}}>{onlineStudentDashboard.presentSess} present / {onlineStudentDashboard.totalSess} total sessions</div>
                      </div>
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:18,fontWeight:700,color:'#0ea5e9'}}>{onlineStudentDashboard.topicStats?.filter(t=>t.summary?.status==='watched').length || 0}</div>
                        <div style={{fontSize:10,color:'var(--text3)'}}>Topics<br/>Completed</div>
                      </div>
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:18,fontWeight:700,color:'#6366f1'}}>{onlineStudentDashboard.topicStats?.length || 0}</div>
                        <div style={{fontSize:10,color:'var(--text3)'}}>Topics<br/>Started</div>
                      </div>
                    </div>
                    {/* Online periods */}
                    {onlineStudentDashboard.periods?.length>0 && (
                      <div style={{marginBottom:16}}>
                        <div style={{fontSize:12,fontWeight:600,marginBottom:6}}>🖥️ Online Periods</div>
                        {onlineStudentDashboard.periods.map(p=>(
                          <div key={p.id} style={{fontSize:11,background:'#ede9fe',borderRadius:5,padding:'4px 10px',marginBottom:4,display:'flex',gap:12}}>
                            <span>From: <b>{dmy(p.start_date)}</b></span>
                            {p.end_date&&<span>Expected return: <b>{dmy(p.end_date)}</b></span>}
                            {p.actual_return_date&&<span>Returned: <b>{dmy(p.actual_return_date)}</b></span>}
                            <span style={{color:p.status==='active'?'#15803d':'#6b7280',fontWeight:600}}>{p.status==='active'?'🟢 Active':'✓ Completed'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Topic journey */}
                    <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>📹 Topic Journey</div>
                    {onlineStudentDashboard.topicStats?.length===0 && (
                      <div style={{fontSize:12,color:'var(--text3)',fontStyle:'italic',padding:12}}>No video sessions recorded yet.</div>
                    )}
                    {onlineStudentDashboard.topicStats?.map(t=>{
                      const isDone = t.summary?.status==='watched';
                      const pct = t.maxDay>0?(t.watched/t.maxDay*100).toFixed(0):0;
                      return (
                        <div key={t.topic} style={{border:'1px solid #e2e8f0',borderRadius:8,padding:'10px 12px',marginBottom:8,background:isDone?'#f0fdf4':'#fff'}}>
                          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                            <span style={{fontWeight:700,fontSize:13,flex:1}}>{t.topic}</span>
                            {isDone && <span style={{fontSize:11,background:'#22c55e',color:'#fff',borderRadius:4,padding:'1px 7px'}}>✅ Completed</span>}
                            {!isDone && t.shared>0 && <span style={{fontSize:11,background:'#0ea5e9',color:'#fff',borderRadius:4,padding:'1px 7px'}}>📤 In Progress</span>}
                            {t.att && (
                              <span style={{fontSize:11,color:Number((t.att.present/t.att.total*100).toFixed(0))>=75?'#15803d':'#d97706',fontWeight:600}}>
                                Att: {(t.att.present/t.att.total*100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          {/* Day pills */}
                          <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:6}}>
                            {t.days.sort((a,b)=>a.day_number-b.day_number).map(d=>(
                              <div key={d.day_number} title={`Day ${d.day_number} — ${d.status}${d.shared_date?' ('+d.shared_date+')':''}`}
                                style={{fontSize:10,padding:'2px 6px',borderRadius:4,fontWeight:600,
                                  background:d.status==='watched'?'#dcfce7':d.status==='shared'?'#dbeafe':d.status==='missed'?'#fee2e2':'#f3f4f6',
                                  color:d.status==='watched'?'#15803d':d.status==='shared'?'#1d4ed8':d.status==='missed'?'#dc2626':'#6b7280',
                                  border:'1px solid',borderColor:d.status==='watched'?'#86efac':d.status==='shared'?'#93c5fd':d.status==='missed'?'#fca5a5':'#e2e8f0'
                                }}>
                                D{d.day_number} {d.status==='watched'?'✅':d.status==='shared'?'📤':d.status==='missed'?'❌':'⏳'}
                              </div>
                            ))}
                            {t.summary && (
                              <div title={`Summary — ${t.summary.status}`}
                                style={{fontSize:10,padding:'2px 6px',borderRadius:4,fontWeight:600,
                                  background:t.summary.status==='watched'?'#dcfce7':t.summary.status==='shared'?'#fef9c3':'#f3f4f6',
                                  color:t.summary.status==='watched'?'#15803d':t.summary.status==='shared'?'#92400e':'#6b7280',
                                  border:'1px solid #fde68a'}}>
                                📝 {t.summary.status==='watched'?'✅':t.summary.status==='shared'?'📤':'⏳'}
                              </div>
                            )}
                          </div>
                          {/* Stats row */}
                          <div style={{display:'flex',gap:16,fontSize:11,color:'var(--text3)'}}>
                            <span>✅ Watched: <b>{t.watched}</b></span>
                            <span>📤 Shared: <b>{t.shared}</b></span>
                            <span>❌ Missed: <b>{t.missed}</b></span>
                            <span>Total days: <b>{t.maxDay}</b></span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}

          {showBatchSummary && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',flexDirection:'column'}}>
              <div style={{background:'var(--bg)',flex:1,overflow:'auto',padding:24,margin:24,borderRadius:12,boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                  <span style={{fontWeight:700,fontSize:16}}>📊 Batch Absence & Call Summary — {trackerBatch}</span>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:16}} onClick={()=>setShowBatchSummary(false)}>✕ Close</button>
                </div>
                <BatchAbsenceSummary
                  canEditLog={ownBatch(trackerBatch)}
                  supabase={supabase}
                  batch={trackerBatch}
                  callLogStaff={callLogStaff}
                  hideTitle={true}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ATTENDANCE SUMMARY ── */}
      {activeTab === 'summary' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <SearchableSelect
              options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
              value={summaryBatch}
              onChange={v => { const val = typeof v === 'object' ? v?.value : v; setSummaryBatch(val); setSummaryTopic(''); loadBatchTopics(val); }}
              placeholder="- Select Batch -"
              searchPlaceholder="Search batch..."
              style={{ width: 240 }}
            />
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label style={{ fontSize: 11 }}>Filter by Topic</label>
              <SearchableSelect
                options={summaryTopics.map(t => ({ value: t.topic_name||t, label: t.short_code ? `${t.short_code} - ${t.topic_name||t}` : (t.topic_name||t) }))}
                value={summaryTopic}
                onChange={async v => {
                  setSummaryTopic(v);
                  setFacultyForTopic('');
                  if (v && summaryBatch) {
                    const {data:stds2} = await supabase.from('students').select('id').eq('batch_name', summaryBatch).neq('is_active',false);
                    if (stds2?.length) {
                      const {data:facData} = await supabase.from('attendance')
                        .select('faculty_name').eq('topic_name',v)
                        .in('student_id', stds2.map(s=>s.id))
                        .not('faculty_name','is',null).limit(1);
                      if (facData?.[0]?.faculty_name) setFacultyForTopic(facData[0].faculty_name);
                    }
                  }
                }}
                placeholder="All Topics"  
                searchPlaceholder="Search topic..."
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 11 }}>From Date</label>
              <DateInput value={summaryFromDate} onChange={e => setSummaryFromDate(e.target.value)} style={{ width: 150 }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: 11 }}>To Date</label>
              <DateInput value={summaryToDate} onChange={e => setSummaryToDate(e.target.value)} style={{ width: 150 }} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={()=>loadSummary(summaryBatch)} disabled={!summaryBatch || summaryLoading}>
              {summaryLoading ? '⏳ Loading...' : '🔍 Generate'}
            </button>
            {summaryData.length > 0 && (
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: 11 }}>Search Student</label>
                <input className="input" style={{ width: 230 }} placeholder="🔍 Name or register no..." value={summarySearch} onChange={e => setSummarySearch(e.target.value)} />
              </div>
            )}
            {(summaryFromDate || summaryToDate || summaryTopic) && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setSummaryFromDate(''); setSummaryToDate(''); setSummaryTopic(''); }}>
                ✕ Clear Filters
              </button>
            )}
            {summaryData.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{summaryTopics.length} topic{summaryTopics.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {!summaryBatch && (
            <div className="empty-state"><Users /><h3>Select a Batch</h3></div>
          )}
          {summaryBatch && !summaryLoading && summaryData.length === 0 && (
            <div className="empty-state"><h3>No attendance data yet</h3></div>
          )}

          {summaryData.length > 0 && (
            <>
              <div className="stats-grid" style={{ marginBottom: 20 }}>
                {[
                  { label: 'Total Students', value: summaryData.length, color: 'var(--accent)', filter: 'all' },
                  { label: 'Total Days', value: summaryData[0]?.totalDays || 0, color: 'var(--text)' },
                  { label: 'Total Sessions', value: summaryData[0]?.totalSessions || 0, color: 'var(--cyan)' },
                  { label: 'Good Attendance (≥75%)', value: summaryData.filter(s => Number(s.pct) >= 75).length, color: 'var(--green)', filter: 'good' },
                  { label: 'Low Attendance (<75%)', value: summaryData.filter(s => Number(s.pct) < 75).length, color: 'var(--red)', filter: 'low' },
                ].map(s => (
                  <div className="stat-card" key={s.label}
                    onClick={()=>s.filter&&setSummaryFilter(s.filter==='all'?'all':prev=>prev===s.filter?'all':s.filter)}
                    style={{
                      cursor: s.filter ? 'pointer' : 'default',
                      outline: summaryFilter===s.filter ? `2px solid ${s.color}` : 'none',
                      background: summaryFilter===s.filter ? `${s.color}18` : '',
                      transform: s.filter ? 'translateY(-1px)' : 'none',
                      transition: 'all 0.15s ease',
                      userSelect: 'none',
                    }}>
                    <div className="stat-label" style={{ display:'flex', alignItems:'center', gap:4 }}>
                      {s.filter && <span style={{fontSize:10}}>{summaryFilter===s.filter ? '✅' : '🔗'}</span>}
                      {s.label}
                    </div>
                    <div className="stat-value" style={{ color: s.color, fontSize: 22 }}>{s.value}</div>
                    {s.filter && (
                      <div style={{fontSize:9,color:s.color,marginTop:2,fontWeight:600}}>
                        {summaryFilter===s.filter ? 'Click to show all' : 'Click to filter'}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <style>{`
                .att-sum-scroll::-webkit-scrollbar { height: 18px; }
                .att-sum-scroll::-webkit-scrollbar-track { background: var(--bg2); }
                .att-sum-scroll::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 8px; border: 3px solid var(--bg2); }
                .att-sum-scroll { scrollbar-color: var(--accent) var(--bg2); scrollbar-width: auto; }
              `}</style>

{/* scroll sync handled by onScroll */}
              <div className="table-wrap" id="att_sum_t1" onScroll={e=>{const m=document.getElementById('att_sum_t1_mirror');if(m)m.scrollLeft=e.target.scrollLeft;}}>
              
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Reg No.</th>
                      <th>Name</th>
                      <th style={{ textAlign: 'center' }}>Total Sessions</th>
                      <th style={{ textAlign: 'center' }}>Present Sessions</th>
                      <th>Overall %</th>
                      {summaryTopics.map(topicObj => {
                        const topic = topicObj.topic_name || topicObj;
                        const sc = topicObj.short_code || topic;
                        const topicTotal = summaryData[0]?.topicStats?.find(t => t.topic === topic)?.topicTotalSess || 0;
                        return (
                          <th key={topic} title={topic} style={{ textAlign: 'center', fontSize: 11 }}>
                            <div style={{ color: 'var(--cyan)', fontWeight: 700 }}>{sc}</div>
                            {topicObj.faculty_name && <div style={{ color: 'var(--accent)', fontSize: 10, fontWeight: 600 }}>{topicObj.faculty_name}</div>}
                            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>{topicTotal} sess / {Math.ceil(topicTotal/2)} days</div>
                          </th>
                        );
                      })}
                      {summaryLabCols.map(lc => (
                        <th key={lc.topic} title={lc.topic} style={{ textAlign: 'center', fontSize: 11 }}>
                          <div style={{ color: 'var(--purple)', fontWeight: 700 }}>{lc.topic}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>{lc.days} lab session{lc.days !== 1 ? 's' : ''}</div>
                        </th>
                      ))}
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSummaryData.map((s, i) => (
                      <tr key={s.id} style={{cursor:'pointer'}} onClick={()=>{
                          const fullStudent = summaryData.find(sd=>sd.id===s.id)||s;
                          setSummaryViewStudent(fullStudent);
                        }}>
                        <td style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{s.register_number}</td>
                        <td style={{ fontWeight: 500 }}>{s.full_name}</td>
                        <td style={{ textAlign: 'center', color: 'var(--text3)' }}>{s.totalSessions}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--green)' }}>{s.presentSessions}</td>
                        <td>
                          <div>
                            <div style={{ fontWeight: 700, color: Number(s.pct) >= 75 ? 'var(--green)' : 'var(--red)', fontSize: 14, marginBottom: 4 }}>{s.pct}%</div>
                            <div className="progress-bar" style={{ width: 100 }}>
                              <div className="progress-fill" style={{ width: `${Math.min(100, s.pct)}%`, background: Number(s.pct) >= 75 ? 'var(--green)' : Number(s.pct) >= 50 ? 'var(--amber)' : 'var(--red)' }} />
                            </div>
                          </div>
                        </td>
                        {summaryTopics.map(topicObj => {
                          const topic = topicObj.topic_name || topicObj;
                          const ts = s.topicStats?.find(t => t.topic === topic);
                          const pct = ts?.topicPct || '0';
                          const present = ts?.topicPresent || 0;
                          const total = ts?.topicTotalSess || 0;
                          const color = Number(pct) >= 75 ? 'var(--green)' : Number(pct) >= 50 ? 'var(--amber)' : total > 0 ? 'var(--red)' : 'var(--text3)';
                          return (
                            <td key={topic} style={{ textAlign: 'center', minWidth: 100 }}>
                              {total > 0 ? (
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color }}>{present}/{total}</div>
                                  <div style={{ fontSize: 11, color, marginBottom: 4 }}>{pct}%</div>
                                  <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, width: 70, margin: '0 auto' }}>
                                    <div style={{ height: '100%', width: `${Math.min(100, Number(pct))}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
                                  </div>
                                </div>
                              ) : (
                                <span style={{ color: 'var(--text3)', fontSize: 12 }}>-</span>
                              )}
                            </td>
                          );
                        })}
                        {summaryLabCols.map(lc => {
                          const ts = s.topicStats?.find(t => t.topic === lc.topic);
                          const pct = ts?.topicPct || '0';
                          const present = ts?.topicPresent || 0;
                          const total = ts?.topicTotalSess || 0;
                          const color = Number(pct) >= 75 ? 'var(--green)' : Number(pct) >= 50 ? 'var(--amber)' : total > 0 ? 'var(--red)' : 'var(--text3)';
                          return (
                            <td key={lc.topic} style={{ textAlign: 'center', minWidth: 100 }}>
                              {total > 0 ? (
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color }}>{present}/{total}</div>
                                  <div style={{ fontSize: 11, color, marginBottom: 4 }}>{pct}%</div>
                                  <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, width: 70, margin: '0 auto' }}>
                                    <div style={{ height: '100%', width: `${Math.min(100, Number(pct))}%`, background: color, borderRadius: 2 }} />
                                  </div>
                                </div>
                              ) : (
                                <span style={{ color: 'var(--text3)', fontSize: 12 }}>-</span>
                              )}
                            </td>
                          );
                        })}
                        <td>
                          <span className={`badge ${Number(s.pct) >= 75 ? 'badge-green' : Number(s.pct) >= 50 ? 'badge-amber' : 'badge-red'}`}>
                            {Number(s.pct) >= 75 ? '✓ Good' : Number(s.pct) >= 50 ? '⚠ Average' : '✗ Low'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-ghost btn-sm" title="View attendance details"
                              onClick={() => {
                                // Re-fetch from summaryData to ensure topicStats is included
                                const fullStudent = summaryData.find(sd => sd.id === s.id) || s;
                                setSummaryViewStudent(fullStudent);
                              }}
                            ><Eye size={13} /></button>
                            {canSum && <button className="btn btn-ghost btn-sm" title="Edit attendance for this student"
                              onClick={() => {
                                setSelectedBatch(summaryBatch);
                                setSummaryTopic('');
                                setActiveTab('mark');
                                // Scroll to top after tab switch
                                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                                toast.success(`Switched to Mark tab - batch ${summaryBatch} selected`);
                              }}
                            ><Edit2 size={13} /></button>}
                            {canSum && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete all attendance records for this student"
                              onClick={async () => {
                                if (!window.confirm(`Delete ALL attendance records for ${s.full_name}? This cannot be undone.`)) return;
                                await supabase.from('attendance').delete().eq('student_id', s.id);
                                toast.success('Attendance records deleted');
                                loadSummary(summaryBatch);
                              }}
                            ><Trash2 size={13} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ── REPORTS TAB ── */}
      {/* -- FACULTY REPORT TAB -- */}
      {activeTab === 'faculty_report' && (
        <div className="card" style={{padding:20}}>
          <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
            <div style={{fontWeight:700,fontSize:15}}>Faculty Report</div>
            <select style={{padding:'4px 8px',fontSize:13,borderRadius:6,border:'1px solid var(--border)'}}
              value={facultyReportBatch} onChange={e=>{setFacultyReportBatch(e.target.value);loadFacultyReport(e.target.value);}}>
              <option value="">All Active Batches</option>
              {batches.filter(b=>b.status==='ongoing').map(b=><option key={b.id} value={b.batch_name}>{b.batch_name}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={()=>loadFacultyReport()}>Refresh</button>
          </div>
          {facultyReportLoading ? <div style={{textAlign:'center',padding:40}}>Loading...</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Batch</th>
                    <th>Topic</th>
                    <th>Faculty</th>
                    <th style={{textAlign:'center'}}>Days</th>
                    <th style={{textAlign:'center'}}>Sessions</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {facultyReport.length === 0
                    ? <tr><td colSpan={9} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No faculty data found. Mark attendance with faculty selected to see report.</td></tr>
                    : facultyReport.map((r,i)=>(
                      <tr key={i} style={{cursor:'pointer'}} onClick={async()=>{
                        setFacultyRowDetail(r);
                        const {data:stds} = await supabase.from('students').select('id').eq('batch_name',r.batch).neq('is_active',false);
                        const sIds = (stds||[]).map(s=>s.id);
                        if (!sIds.length) { setFacultyRowData([]); return; }
                        let attQ = supabase.from('attendance')
                          .select('date,num_sessions,faculty_name')
                          .in('student_id',sIds).eq('topic_name',r.topic).order('date');
                        if (r.faculty && r.faculty!=='Not Assigned') attQ = attQ.eq('faculty_name',r.faculty);
                        const {data:att} = await attQ;
                        const dateMap = {};
                        (att||[]).forEach(a=>{ if(!dateMap[a.date]) dateMap[a.date]={date:a.date,sessions:a.num_sessions||4}; });
                        setFacultyRowData(Object.values(dateMap).sort((a,b)=>a.date.localeCompare(b.date)));
                      }}>
                        <td>{i+1}</td>
                        <td><span className="badge badge-blue">{r.batch}</span></td>
                        <td style={{fontWeight:600}}>{r.topic}</td>
                        <td style={{color:r.faculty==='Not Assigned'?'var(--red)':'var(--accent)',fontStyle:r.faculty==='Not Assigned'?'italic':undefined,fontWeight:r.faculty==='Not Assigned'?700:undefined}}>{r.faculty==='Not Assigned'?'⚠️ Not Assigned':r.faculty}</td>
                        <td style={{textAlign:'center'}}>{r.days}</td>
                        <td style={{textAlign:'center',fontWeight:600}}>{r.totalSessions}</td>
                        <td style={{fontSize:12,whiteSpace:'nowrap'}}>{dmy(r.fromDate)}</td>
                        <td style={{fontSize:12,whiteSpace:'nowrap'}}>{dmy(r.toDate)}</td>
                        <td style={{fontSize:12,color:'var(--accent)',cursor:'pointer',whiteSpace:'nowrap'}}>View →</td>
                      </tr>
                    ))
                  }
                  <tr style={{background:'var(--accent-light)',fontWeight:700,borderTop:'2px solid var(--accent)'}}>
                    <td colSpan={4} style={{padding:'8px 12px',textAlign:'right',fontSize:13}}>Total:</td>
                    <td style={{textAlign:'center',fontSize:14,padding:'8px 12px'}}>{facultyReport.reduce((s,r)=>s+r.days,0)}</td>
                    <td style={{textAlign:'center',fontSize:14,padding:'8px 12px',color:'var(--accent)'}}>{facultyReport.reduce((s,r)=>s+r.totalSessions,0)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {/* Holidays & No-Class Days — separate section */}
          {!facultyReportLoading && facultyHolidays.length > 0 && (
            <div style={{marginTop:20,borderTop:'2px dashed var(--border)',paddingTop:16}}>
              <div style={{fontWeight:600,fontSize:13,color:'var(--amber)',marginBottom:10}}>
                🗓️ Holidays &amp; No-Class Days
                <span style={{fontWeight:400,fontSize:11,color:'var(--text3)',marginLeft:8}}>{facultyHolidays.reduce((s,r)=>s+r.days,0)} day(s)</span>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#fef9c3',borderBottom:'2px solid #fde68a'}}>
                  <th style={{padding:'6px 10px',textAlign:'left'}}>#</th>
                  <th style={{padding:'6px 10px',textAlign:'left'}}>Batch</th>
                  <th style={{padding:'6px 10px',textAlign:'left'}}>Type</th>
                  <th style={{padding:'6px 10px',textAlign:'center'}}>Days</th>
                  <th style={{padding:'6px 10px',textAlign:'left'}}>From</th>
                  <th style={{padding:'6px 10px',textAlign:'left'}}>To</th>
                  <th style={{padding:'6px 10px'}}></th>
                </tr></thead>
                <tbody>
                  {facultyHolidays.map((h,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #fde68a',background:i%2===0?'#fffbeb':'#fff',cursor:'pointer'}}
                      onClick={async()=>{
                        const {data:stds} = await supabase.from('students').select('id').eq('batch_name',h.batch).neq('is_active',false);
                        const sIds = (stds||[]).map(s=>s.id);
                        if (!sIds.length) { setHolidayRowDetail({...h,dates:[]}); return; }
                        let q = supabase.from('attendance').select('date,session_type,topic_name').in('student_id',sIds);
                        if (h.label==='Holiday') q=q.or('session_type.eq.holiday,topic_name.eq.Holiday');
                        else q=q.or('session_type.eq.no_class,topic_name.eq.No Class');
                        const {data:att} = await q;
                        const uniqueDates = [...new Set((att||[]).map(a=>a.date))].sort();
                        setHolidayRowDetail({...h, dates: uniqueDates});
                      }}>
                      <td style={{padding:'5px 10px',color:'var(--text3)'}}>{i+1}</td>
                      <td style={{padding:'5px 10px'}}><span className="badge badge-blue">{h.batch}</span></td>
                      <td style={{padding:'5px 10px',fontWeight:600,color:'#854d0e'}}>{h.label==='Holiday'?'🏖️ Holiday':'📵 No Class'}</td>
                      <td style={{padding:'5px 10px',textAlign:'center',fontWeight:700,color:'#854d0e'}}>{h.days}</td>
                      <td style={{padding:'5px 10px',fontSize:11,fontFamily:'monospace',whiteSpace:'nowrap'}}>{dmy(h.fromDate)}</td>
                      <td style={{padding:'5px 10px',fontSize:11,fontFamily:'monospace',whiteSpace:'nowrap'}}>{dmy(h.toDate)}</td>
                      <td style={{padding:'5px 10px',fontSize:11,fontFamily:'monospace',color:'var(--accent)'}}>View →</td>
                    </tr>
                  ))}
                  <tr style={{background:'#fef9c3',fontWeight:700,borderTop:'2px solid #fde68a'}}>
                    <td colSpan={3} style={{padding:'6px 10px',textAlign:'right',fontSize:12}}>Total:</td>
                    <td style={{textAlign:'center',padding:'6px 10px'}}>{facultyHolidays.reduce((s,r)=>s+r.days,0)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Faculty Row Detail Modal */}
      {facultyRowDetail && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setFacultyRowDetail(null)}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-header">
              <span className="modal-title">{facultyRowDetail.topic} — {facultyRowDetail.faculty}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setFacultyRowDetail(null)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'60vh',overflowY:'auto'}}>
              <div style={{display:'flex',gap:12,marginBottom:12,fontSize:12,flexWrap:'wrap'}}>
                <span>Batch: <strong>{facultyRowDetail.batch}</strong></span>
                <span>Days: <strong>{facultyRowData.length}</strong></span>
                <span>Sessions: <strong>{facultyRowData.reduce((s,d)=>s+d.sessions,0)}</strong></span>
                <span>Period: <strong>{facultyRowData.length>0?dmy(facultyRowData[0].date):dmy(facultyRowDetail.fromDate)} → {facultyRowData.length>0?dmy(facultyRowData[facultyRowData.length-1].date):dmy(facultyRowDetail.toDate)}</strong></span>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'var(--accent-light)'}}>
                    <th style={{padding:'6px 10px',textAlign:'left'}}>#</th>
                    <th style={{padding:'6px 10px',textAlign:'left'}}>Date</th>
                    <th style={{padding:'6px 10px',textAlign:'center'}}>Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {facultyRowData.map((d,i)=>(
                    <tr key={d.date} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                      <td style={{padding:'6px 10px',color:'var(--text3)'}}>{i+1}</td>
                      <td style={{padding:'6px 10px',fontWeight:500}}>{dmy(d.date)}</td>
                      <td style={{padding:'6px 10px',textAlign:'center',fontWeight:600,color:'var(--accent)'}}>{d.sessions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setFacultyRowDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* -- CLASS SCHEDULE TAB -- */}
            {activeTab === 'schedule' && (
        <ClassSchedule batches={batches} topics={topics} facultyList={facultyList} />
      )}

      {activeTab === 'daily_log' && (
        <DailySessionLog supabase={supabase} batches={batches}
          dailyLog={dailyLog} setDailyLog={setDailyLog}
          dailyLogBatch={dailyLogBatch} setDailyLogBatch={setDailyLogBatch}
          dailyLogFrom={dailyLogFrom} setDailyLogFrom={setDailyLogFrom}
          dailyLogTo={dailyLogTo} setDailyLogTo={setDailyLogTo}
          dailyLogLoading={dailyLogLoading} setDailyLogLoading={setDailyLogLoading}
        />
      )}
      {activeTab === 'reports' && (
        <div className="card" style={{ padding: 24 }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>📋 Attendance Reports</h2>
            <p style={{ fontSize: 12, color: 'var(--text3)' }}>Select a batch to generate printable A4 attendance reports</p>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 200 }}>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Select Batch</label>
              <SearchableSelect
                options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
                value={summaryBatch}
                onChange={v => { setSummaryBatch(v); setSummaryTopic(''); if (v) loadSummary(v); }}
                placeholder="Choose batch..."
                style={{ width: 200 }}
              />
            </div>
            {summaryBatch && summaryTopics.length > 0 && (<>
              <div style={{ minWidth: 200 }}>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Filter by Topic (Report 1)</label>
                <select className="input" value={summaryTopic} onChange={e => setSummaryTopic(e.target.value)} style={{ width: 200 }}>
                  <option value="">All Topics Combined</option>
                  {summaryTopics.map(t => { const fac = t.faculty_name ? ` [${t.faculty_name}]` : ''; return <option key={t.topic_name||t} value={t.topic_name||t}>{t.short_code ? `${t.short_code} - ${t.topic_name||t}${fac}` : `${t.topic_name||t}${fac}`}</option>; })}
                </select>
              </div>
                          </>)}
          </div>
          {summaryBatch && summaryData.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Report 1 */}
              {summaryData.length===0&&summaryBatch&&<div style={{padding:'8px 14px',background:'#fffbeb',border:'1px solid #d97706',borderRadius:8,fontSize:12,color:'#92400e',marginBottom:12}}>⚠️ Click <strong>Generate</strong> in the summary section above first, then print reports.</div>}
              <div style={{ border: '2px solid #1a3a6b33', borderRadius: 12, padding: 20, background: '#f8faff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ background: '#1a3a6b', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 20 }}>📄</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Report 1 - Topic Attendance</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Two-column layout, attendance % per student per topic</div>
                  </div>
                </div>
                <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 11 }}>
                  {[
                    ['Batch', summaryBatch],
                    ['Topic', summaryTopic || '(All topics combined)'],
                    ['Total Sessions', summaryData[0]?.totalSessions || 0],
                    ['Students', summaryData.length],
                  ].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: 'var(--text3)' }}>{l}:</span><strong>{v}</strong>
                    </div>
                  ))}
                </div>
                <button style={{ width: '100%', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  onClick={() => {
                    const batchName = summaryBatch || '';
                    const topic = summaryTopic || 'All Topics';
                    const totalSess = summaryTopic
                      ? (summaryData[0]?.topicStats?.find(t => t.topic === summaryTopic)?.topicTotalSess || 0)
                      : (summaryData[0]?.totalSessions || 0);
                    const stds = [...summaryData].sort((a,b)=>(a.register_number||'').localeCompare(b.register_number||'')).map(s => ({
                      full_name: s.full_name,
                      register_number: s.register_number,
                      presentSessions: summaryTopic
                        ? (s.topicStats?.find(t => t.topic === summaryTopic)?.topicPresent || 0)
                        : s.presentSessions,
                      pct: summaryTopic
                        ? (s.topicStats?.find(t => t.topic === summaryTopic)?.topicPct || 0)
                        : s.pct,
                    }));
                    if(summaryData.length===0) return; printTopicReport(batchName, topic, totalSess, stds);
                  }} disabled={summaryData.length===0} title={summaryData.length===0?'Click Generate first':'Print Report 1'} style={{opacity:summaryData.length===0?0.4:1,cursor:summaryData.length===0?'not-allowed':'pointer'}}>
                  <Printer size={15}/> Print Report 1
                </button>
              </div>
              {/* Report 2 */}
              <div style={{ border: '2px solid #b4530933', borderRadius: 12, padding: 20, background: '#fff8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ background: '#b45309', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 20 }}>📊</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Report 2 - Full Batch Report</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>All topics as columns, total attendance & % per student</div>
                  </div>
                </div>
                <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 11 }}>
                  {[
                    ['Batch', summaryBatch],
                    ['Topics', summaryTopics.length > 0 ? summaryTopics.join(', ') : '-'],
                    ['Total Sessions', summaryData[0]?.totalSessions || 0],
                    ['Students', summaryData.length],
                  ].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: 'var(--text3)' }}>{l}:</span><strong style={{ maxWidth: 160, textAlign: 'right', wordBreak: 'break-word' }}>{v}</strong>
                    </div>
                  ))}
                </div>
                <button style={{ width: '100%', background: '#b45309', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  onClick={() => {
                    const batchName = summaryBatch;
                    const tMap = Object.fromEntries(topics.map(t=>[t.topic_name, t.short_code||t.topic_name])); const allTopicsForReport = [...new Set(summaryData.flatMap(s => (s.topicStats||[]).filter(t=>!t.isLab).map(t=>t.topic)))]; const totalSessForReport = allTopicsForReport.reduce((sum,t) => sum + (summaryData[0]?.topicStats?.find(x=>x.topic===t)?.topicTotalSess||0), 0); printBatchReport(batchName, allTopicsForReport, totalSessForReport, [...summaryData].sort((a,b)=>(a.register_number||'').localeCompare(b.register_number||'')), tMap);
                  }} disabled={summaryData.length===0} title={summaryData.length===0?'Click Generate first':'Print Report 2'} style={{opacity:summaryData.length===0?0.4:1,cursor:summaryData.length===0?'not-allowed':'pointer'}}>
                  <Printer size={15}/> Print Report 2
                </button>
              </div>
            </div>
          ) : summaryBatch ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <div>No attendance data found for this batch.</div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👆</div>
              <div>Select a batch above to generate reports.</div>
            </div>
          )}
        </div>
      )}

    </div>

    {summaryViewStudent && ReactDOM.createPortal(
      <div onClick={() => setSummaryViewStudent(null)} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#00b09b', borderRadius: '12px 12px 0 0' }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>📊 {summaryViewStudent.full_name} - Attendance Detail</span>
            <button onClick={() => setSummaryViewStudent(null)} style={{ background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 18 }}>×</button>
          </div>
          <div style={{ padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total Days', value: summaryViewStudent.totalDays || 0, color: '#374151' },
                { label: 'Total Sessions', value: summaryViewStudent.totalSessions || 0, color: '#0891b2' },
                { label: 'Present Sessions', value: summaryViewStudent.presentSessions || 0, color: '#16a34a' },
                { label: 'Overall %', value: `${summaryViewStudent.pct || 0}%`, color: Number(summaryViewStudent.pct) >= 75 ? '#16a34a' : Number(summaryViewStudent.pct) >= 50 ? '#d97706' : '#dc2626' },
              ].map(c => (
                <div key={c.label} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>Register No: <strong style={{ color: '#00b09b', fontFamily: 'monospace' }}>{summaryViewStudent.register_number}</strong></div>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Topic-wise Breakdown</div>
            {summaryViewStudent.topicStats && summaryViewStudent.topicStats.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#ecfdf5' }}>
                    {['Topic','Present','Total','%','Progress','Dates'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h==='Topic'?'left':'center', color: '#00b09b', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summaryViewStudent.topicStats.map((ts, i) => {
                    const pct = Number(ts.topicPct);
                    const color = pct >= 75 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';
                    return (
                      <tr key={ts.topic} style={{ borderTop: '1px solid #e5e7eb', background: i%2===0?'#fff':'#f9fafb' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 500 }}>{ts.topic}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', color: '#16a34a', fontWeight: 700 }}>{ts.topicPresent}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>{ts.topicTotalSess}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color }}>{ts.topicPct}%</td>
                        <td style={{ padding: '8px 12px' }}>
                          {ts.dateWise && ts.dateWise.length > 0 ? (
                            <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                              {ts.dateWise.map((d,di) => (
                                <span key={di} style={{
                                  fontSize:10, padding:'2px 6px', borderRadius:4, fontWeight:500,
                                  background: d.sessPresent===d.totalSess ? (d.session_type==='repeat'?'#fef9c3':'#dcfce7') : d.sessPresent>0 ? '#fef9c3' : '#fee2e2',
                                  color: d.sessPresent===d.totalSess ? (d.session_type==='repeat'?'#854d0e':'#15803d') : d.sessPresent>0 ? '#854d0e' : '#991b1b',
                                  border: `1px solid ${d.sessPresent===d.totalSess?(d.session_type==='repeat'?'#fde68a':'#86efac'):d.sessPresent>0?'#fde68a':'#fca5a5'}`
                                }} title={d.session_type==='repeat'?'Repeat session (attended on '+d.repeat_date+')':'Sessions: '+d.sessPresent+'/'+d.totalSess}>
                                  {dmy(d.date)}{d.no>1?' · S'+d.no:''}
                                  {' '}{d.sessPresent}/{d.totalSess}
                                  {d.session_type==='repeat'?' 🔄':''}
                                </span>
                              ))}
                            </div>
                          ) : <span style={{color:'#9ca3af',fontSize:11}}>—</span>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3 }}>
                              <div style={{ height: '100%', width: `${Math.min(100,pct)}%`, background: color, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 32 }}>{ts.topicPct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                <div>No topic breakdown available.</div>
              </div>
            )}
          </div>
          <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => printStudentAttendance(summaryViewStudent, summaryBatch)} style={{ background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>🖨️ Print PDF</button>
            <button onClick={() => setSummaryViewStudent(null)} style={{ background: '#00b09b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 24px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Close</button>
          </div>
        </div>
      </div>,
      document.body
    )}

      {/* ── LAB REPORT TAB ── */}
      {activeTab === 'lab' && (
        <LabReportTab supabase={supabase} />
      )}
      {/* Holiday Row Detail Modal */}
      {holidayRowDetail && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setHolidayRowDetail(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <span className="modal-title">
                {holidayRowDetail.label==='Holiday'?'🏖️ Holiday':'📵 No Class'} — {holidayRowDetail.batch}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setHolidayRowDetail(null)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'60vh',overflowY:'auto'}}>
              <div style={{display:'flex',gap:16,marginBottom:12,fontSize:12}}>
                <span>Batch: <strong>{holidayRowDetail.batch}</strong></span>
                <span>Total: <strong>{holidayRowDetail.dates.length} day(s)</strong></span>
              </div>
              {holidayRowDetail.dates.length === 0
                ? <div style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No dates found</div>
                : <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:'#fef9c3'}}>
                      <th style={{padding:'6px 10px',textAlign:'left'}}>#</th>
                      <th style={{padding:'6px 10px',textAlign:'left'}}>Date</th>
                      <th style={{padding:'6px 10px',textAlign:'left'}}>Day</th>
                    </tr></thead>
                    <tbody>
                      {holidayRowDetail.dates.map((d,i)=>(
                        <tr key={d} style={{borderBottom:'1px solid #fde68a',background:i%2===0?'#fffbeb':'#fff'}}>
                          <td style={{padding:'5px 10px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'5px 10px',fontFamily:'monospace',fontWeight:600}}>
                            {dmy(d)}
                          </td>
                          <td style={{padding:'5px 10px',color:'var(--text3)'}}>
                            {new Date(d).toLocaleDateString('en-IN',{weekday:'long'})}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setHolidayRowDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
